/**
 * growth.ts (part 1) — anchors + A* PRIMARY roads (W2d-1 Task 2).
 *
 * Primaries are the region's trunk roads — they REPLACE the old contour-streamline
 * arterials (deleted this task). A VILLAGE+ settlement selects a set of ANCHORS
 * (spec §"Core growth parameters"): its seed, up to two shared boundary PORTS
 * (hashed cross-cell so both neighbours derive the SAME port-anchor), and
 * hash-rolled high-suitability points inside its tier radius. A bounded number of
 * PRIMARIES (spec counts) are then routed seed↔anchor and anchor↔anchor
 * nearest-chain by the deterministic terrain-cost A* router (router.ts), ALL
 * through ONE shared {@link RoadNetwork} per region build so the reuse discount
 * pulls later primaries onto earlier ones — trunk-and-branch, not a spiderweb.
 * Each routed polyline is emitted as EDGE_ARTERIAL-kind edges (the enum value is
 * kept for CityGraph format stability; the CONCEPT is now "primary" — a segment
 * with a wet endpoint is emitted EDGE_BRIDGE, as arterials were). Era-0 epochs:
 * primaries predate street-growth epochs, so they carry epoch 0 and are unlogged.
 *
 * Tiers 1–2 skip the anchor/primary network entirely (spec §"Tiers 1–2"):
 *   - a HAMLET gets ONE A* through-road (two seed-nearest ports routed THROUGH
 *     the seed; fallback: one port→seed) plus 8–15 buildings placed 10–25 m off
 *     it with driveway stubs — see {@link growHamlet}. The old hamlet SC growth
 *     is gone.
 *   - a WAYSTATION gets its huddle cluster + direct building-to-building paths and
 *     NO network — see {@link emitWaystationCluster}.
 * For VILLAGE+ cells the primaries are the trunk the ERA'D TENSOR SECONDARY GROWER
 * (streetgrow.ts) grafts onto: it snapshots these primary nodes into a planar net
 * and grows streets off them under the global planarity invariant (the retired SC
 * skeleton + densify that once grew here are gone).
 *
 * DRIVEWAY/ALLEY KIND CAVEAT (documented): the spec calls hamlet driveway stubs
 * TERTIARY and waystation paths ALLEY, but the CityGraph edge enum has no such
 * kinds — width classes arrive W2d-3. Both are emitted EDGE_STREET (the generic
 * surface street) meanwhile; the class channel lands with the width work.
 *
 * DETERMINISM. The router draws NO randomness; anchor/primary COUNTS, the hashed
 * high-suitability anchor points, and hamlet building placement draw from the
 * registered salts below (hashInts / rngFromCell), each a pure function of the
 * region cell + seed. Every scan walks a fixed order with strict tie-breaks; no
 * Set/Map float-order dependence; pmath only (the pmath-ban gate covers this file).
 */
import * as pmath from "../pmath.js";
import { PlanetSampler, type TerrainSample } from "../universe.js";
import { hashSeed } from "../rng.js";
import { type CellId, LEVEL_REGION } from "./cells.js";
import {
  type RegionFrame, type CityGraph, type Parcel, CityGraphBuilder, dirToLocal, localToDir,
  NODE_SEED, NODE_PORT, NODE_JUNCTION,
  EDGE_ARTERIAL, EDGE_STREET, EDGE_STAIR, EDGE_TUNNEL, EDGE_TERRACE, EDGE_BRIDGE,
  LOG_EXTEND, LOG_PARCEL,
  CLASS_PRIMARY, CLASS_SECONDARY, CLASS_TERTIARY, CLASS_ALLEY, CLASS_UNSET,
  USE_DWELLING, USE_DEPOT, USE_BUNKHOUSE, USE_LANDING_PAD, USE_WELL, USE_HALL,
  USE_PRODUCTION, USE_INN, USE_MARKET_SQ, USE_CHAPEL, USE_WORKSHOP,
  USE_WAREHOUSE, USE_MARKET_HALL, USE_TEMPLE, USE_GUILDHALL, USE_SMITHY,
  USE_GARRISON, USE_ADMIN, USE_ACADEMY, USE_HARBOR,
  USE_COURTHOUSE, USE_MONUMENT, USE_STARPORT, USE_EMBASSY, USE_PRECURSOR,
  USE_BASILICA, USE_LOOM_NEXUS, USE_SHRINE, USE_MILL, USE_STABLE, USE_APOTHECARY,
  USE_BATHHOUSE, USE_THEATER, USE_ARENA, USE_DOCKYARD, USE_PRISON, USE_WATCHTOWER,
  USE_FUEL_SHED,
} from "./citygraph.js";
import { edgePorts, edgeEndpoints } from "./boundary.js";
import { suitabilityAt, settlementSeeds } from "./fields.js";
import {
  TIER_WAYSTATION, TIER_HAMLET, TIER_VILLAGE, TIER_TOWN, TIER_CITY, TIER_METROPOLIS,
  tierRegistryFor,
} from "./tierRegistry.js";
import { packCell, cellArcM, dirToCell, cellsPerFace } from "./cells.js";
import { hashInts, h01, rngFromCell } from "./hash.js";
import { ROAD_W, CARVE_MAX_M, PARCEL_BLEND_M } from "./carveindex.js";
import { traceFaces, faceCentroid } from "./blocks.js";
import { edgeBetweenness, trafficEndpoints, samplePairs, pathCrossings } from "./traffic.js";
import {
  polygonArea, pointInPolygon, polygonCentroid, subdividePolygon,
  clipHalfPlane, sharedBoundaryLength, chordCrossesBoundary,
} from "./lots.js";
import { cultureFor } from "./tensorfield.js";
import { makeSamplerTerrain, RoadNetwork, routePrimary, segmentHasWater, REUSE_RADIUS_M, type RouteTerrain } from "./router.js";
import { growSecondaryNetwork, curveHamletPrimaries, SECONDARY_PARAMS } from "./streetgrow.js";
import {assignCivicSites} from './civics.js';

// ── constants (spec §"Core growth parameters" + §"Tiers 1–2", binding) ────────

/**
 * Anchor count [min,max] indexed by tier 0..5 (spec: village 2–3, town 4–6,
 * city 7–10, metropolis 12–18). Tiers 0/1 (waystation/hamlet) route no
 * anchor-primary network, so they are [0,0]. The count INCLUDES the seed anchor.
 */
export const ANCHOR_COUNTS: readonly (readonly [number, number])[] = [
  [0, 0], [0, 0], [2, 3], [4, 6], [7, 10], [12, 18],
];
/**
 * Primary-road count [min,max] indexed by tier 0..5 (spec: village 1–2, town
 * 2–4, city 4–6, metropolis 6–10). Bounds the number of A* primaries routed per
 * VILLAGE+ settlement; tiers 0/1 route none.
 */
export const PRIMARY_COUNTS: readonly (readonly [number, number])[] = [
  [0, 0], [0, 0], [1, 2], [2, 4], [4, 6], [6, 10],
];
/** Max shared boundary ports folded into a settlement's anchor set (spec: "up to 2"). */
export const ANCHOR_MAX_PORTS = 2;
/** Over-sample factor for the hashed high-suitability anchor candidate pool. */
const ANCHOR_CAND_MULT = 8;

/** Hamlet building count [min,max] (spec §"Tiers 1–2": 8–15). */
export const HAMLET_BUILDINGS: readonly [number, number] = [8, 15];
/** Hamlet building perpendicular-offset band from the through-road (spec: 10–25 m). */
export const HAMLET_OFFSET_MIN_M = 10;
export const HAMLET_OFFSET_MAX_M = 25;
/** Hamlet driveway-stub heading-noise half-range (spec: ±25°), radians. */
export const HAMLET_NOISE_RAD = 25 * Math.PI / 180;
/** Hamlet building footprint half-size (m) — matches the waystation huddle. */
const HAMLET_LOT_HALF_M = 5.5;

/**
 * Overgrow slack past the nominal cell bounds — kept as the node-bound reference
 * for oracles that inherited it from the arterial era. Primaries stay inside the
 * cell chart by construction (anchors/ports/seed are all cell-local), so this is
 * a generous ceiling on how far a routed polyline may reach past the seed.
 */
export const OVERGROW_M = 300;

// Salts (registered in hash.ts): anchor count + hashed high-suitability anchor
// candidate jitter (SALT_ANCHOR); the per-settlement primary-count roll
// (SALT_PRIMARY); hamlet building count / along-road position / offset / noise
// (SALT_HAMLET). All key on (planetHash, salt, packCell, seed.k, …).
const SALT_ANCHOR = 0x5a2c;
const SALT_PRIMARY = 0x9d1a;
const SALT_HAMLET = 0x4a3e;
// R1 D3 de-quantization salts (registered in hash.ts).
const SALT_JITTER = 0x1a7e; // per-lattice-node primary jitter (magnitude + angle)
const SALT_BEND = 0xbe2d;   // straight-run-cap gentle bend (amplitude + side)

// ── R1 D2 cross-cell planarity param (spec §D2) ───────────────────────────────
/**
 * Primaries route no deeper than the cell half-extent + this collar into a neighbour
 * cell (the router's hard chart bound). Confines each cell's primary geometry to its
 * own world-space + a thin border collar, so adjacent cells only meet at shared ports
 * — killing the DEEP cross-cell weaves (28–90 m) the union-planarity oracle flags,
 * while leaving legitimate port approaches (ports sit ON the border) reachable. Tuned
 * against the promoted p2 oracle; ~½ lattice cell.
 *
 * NOTE (D2 deviation, documented + measured): the spec sketched a hashed approach-
 * CORRIDOR cost wall (penalise off-ray steps near a port). That was implemented and
 * MEASURED COUNTERPRODUCTIVE — it distorts A* routing and ADDS border crossings
 * (baseline 9 → 16 in the union sweep) with no setting improving on the no-corridor
 * case. This chart BOUND (confine primaries to their own cell) achieves D2's actual
 * goal — geometry meets only at shared ports — and is what the promoted p2 oracle
 * verifies (real cross-cell weaves == 0, chart-flattening seam artifacts excluded).
 */
export const PRIMARY_BOUND_COLLAR_M = 12;

// ── R1 D3 de-quantization params (spec §D3) ───────────────────────────────────
/** Per-node lattice jitter cap (m). Spec: ≤6 m. */
export const JITTER_MAX_M = 6;
/**
 * The jitter magnitude is drawn in [LO,HI]·JITTER_MAX per node (2.1–3.6 m). The
 * FLOOR is non-zero so every through-node gets a real, MODERATE lateral bend — with
 * the parity-alternating sign this places per-node deviations in the (7°,38°) gap,
 * away from BOTH the ~0° (straight) and ~45° (lattice) bands the geometry oracle
 * penalises. Tuned against the promoted p3 oracle (measured; do not drift without
 * re-measuring the collinear + 45-quantized shares).
 */
const JITTER_FRAC_LO = 0.35, JITTER_FRAC_HI = 0.6;
/** Straight-run cap (m): a straight primary run longer than this gets a gentle
 *  bend inserted at its midpoint. Spec: 120 m. */
export const STRAIGHT_CAP_M = 120;
/** Inserted-bend amplitude band (m). Spec: 2–5 m. */
export const BEND_AMP_MIN_M = 2, BEND_AMP_MAX_M = 5;
/**
 * Chaikin subdivision rounds. The spec calls for ×2. The W2d report chose ×1 because
 * the OLD (density-sensitive) 45-quant metric mis-scored ×2 at ~56% — but the R1
 * review REFINED that metric (resample @20 m, drop the ~0 bin), under which ×1 scores
 * ~9% and ×2 ~8% (both well under the <25% target). So the metric no longer justifies
 * ×1. ×1 is nonetheless KEPT for an INDEPENDENT, MEASURED reason: ×2's denser sub-node
 * resampling reintroduces INTRA-cell edge crossings — worldgen-growth-era's binding
 * `crossings == 0` planarity invariant goes RED (3 crossings) at ×2, GREEN at ×1. So
 * ×2 is NOT uniformly better-or-equal (it violates a binding invariant); ×1 stands.
 */
export const CHAIKIN_ROUNDS = 1;
const RAD2DEG_G = 180 / Math.PI;

/** Grade (rise/run, i.e. tan of the slope) of a chart step. 0 for a degenerate step. */
function segGrade(hu: (u: number, v: number) => number, au: number, av: number, bu: number, bv: number): number {
  const run = pmath.hypot(bu - au, bv - av);
  if (run < 1e-6) return 0;
  return Math.abs(hu(bu, bv) - hu(au, av)) / run;
}

/**
 * R1 D3 — DE-QUANTIZE an emitted primary lattice polyline into a gently curved
 * chain, in three ordered passes (spec §D3: jitter → straight-run cap → Chaikin).
 * pts[0] and pts[last] are EXACT anchors (seed / shared port / component node) and
 * are NEVER moved — so cross-cell port agreement (≤1 m) and connectivity endpoints
 * are preserved. Returns a fresh point list (a chain of degree-2 nodes); the edge
 * FORMAT is unchanged (more, shorter, straight 2-node segments).
 *
 * Deterministic: jitter is keyed on each node's 20 m lattice cell (so a node shared
 * between two primaries jitters IDENTICALLY — trunk coincidence survives), and the
 * bend cap is keyed on the rounded segment midpoint (so both neighbour cells insert
 * the SAME bend on a shared border segment). Slope-aware: a jitter or a smoothed
 * corner that would raise a step's grade class is rejected in favour of the
 * pre-transform geometry (the ≤20° road-grade discipline is never worsened).
 */
export function dequantizePrimary(
  pts: { u: number; v: number }[],
  planetHash: number, rk: number, latticeM: number,
  heightM: (u: number, v: number) => number,
  chaikinRounds: number = CHAIKIN_ROUNDS,
  doJitter: boolean = true,
  inCell?: (u: number, v: number) => boolean,
): { u: number; v: number }[] {
  // R1 review: a de-quant move (jitter / bend / Chaikin cut) is REJECTED when it would
  // push a node OUT of the cell's own world-space territory. With the router confining
  // clean primaries to their cell (RouteOpts.inCell), keeping de-quant in-cell too means
  // adjacent cells' primaries stay strictly on their own side of the shared border, so
  // NO cross-cell weave (incl. the ≤3 m border weaves) can be manufactured by de-quant.
  const keeps = (u: number, v: number): boolean => inCell === undefined || inCell(u, v);
  if (pts.length < 3 || !doJitter) return jitterlessCapChaikin(pts, planetHash, rk, heightM, chaikinRounds, keeps);
  // ── Pass 1: per-interior-node lattice jitter (≤ JITTER_MAX_M), slope-aware. ──
  const jit = pts.map((p) => ({ u: p.u, v: p.v }));
  for (let i = 1; i + 1 < jit.length; i++) {
    const gi = Math.round(pts[i].u / latticeM), gj = Math.round(pts[i].v / latticeM);
    // PERPENDICULAR jitter (to the local prev→next tangent): a lateral offset breaks
    // collinearity + 45°-lattice heading while staying NEAR the path (so the curve
    // rarely crosses a neighbour → fewer planarity fallbacks than a free 2-D kick).
    // Magnitude in [½,1]·JITTER_MAX so every node gets a real (non-tiny) bend, sign
    // hashed — keyed on the lattice cell so it is deterministic + cross-cell stable.
    let dx = pts[i + 1].u - jit[i - 1].u, dy = pts[i + 1].v - jit[i - 1].v;
    const dl = pmath.hypot(dx, dy);
    if (dl < 1e-9) continue;
    dx /= dl; dy /= dl;
    const mag = JITTER_MAX_M * (JITTER_FRAC_LO + (JITTER_FRAC_HI - JITTER_FRAC_LO) * h01(hashInts(planetHash, SALT_JITTER, rk, gi, gj, 0)));
    // ALTERNATE the lateral sign by node parity → a gentle wave (every node bends a
    // consistent MODERATE amount, so deviations avoid both ~0 (straight) and ~45°
    // lattice bands), hash-perturbed magnitude keeps it from reading as a sawtooth.
    const sign = ((i & 1) === 0 ? 1 : -1);
    const px2 = -dy * sign, py2 = dx * sign; // unit perpendicular (chosen side)
    // Slope-aware SCALING (grade-based, spec §D3): perpendicular jitter across a slope
    // raises the step grade, so scale the magnitude DOWN to the largest factor that keeps
    // both incident steps within the ≤20° cap (unless the pre-jitter step already
    // exceeded it — an anchor join on steep ground). A slope thus still gets a SMALL
    // de-quantizing wiggle (breaks collinearity) instead of reverting to dead-straight.
    const pu = jit[i - 1].u, pv = jit[i - 1].v, nu = pts[i + 1].u, nv = pts[i + 1].v;
    const before = Math.max(segGrade(heightM, pu, pv, jit[i].u, jit[i].v), segGrade(heightM, jit[i].u, jit[i].v, nu, nv));
    const cap = Math.max(DEQUANT_GRADE_MAX, before);
    for (const scale of [1, 0.6, 0.35, 0.18]) {
      const cu = jit[i].u + px2 * mag * scale, cv = jit[i].v + py2 * mag * scale;
      const after = Math.max(segGrade(heightM, pu, pv, cu, cv), segGrade(heightM, cu, cv, nu, nv));
      // Reject a jitter that leaves the cell (border reconciliation) or steepens past cap.
      if (after <= cap && keeps(cu, cv)) { jit[i].u = cu; jit[i].v = cv; break; }
    }
  }
  // Pass 1b: grade REPAIR. Each node's guard above tested it against the ORIGINAL
  // neighbour, but the NEXT node then jitters too — two jittered nodes can leave a
  // segment between them over the cap. Pull any node bounding a still-steep segment
  // back toward its ORIGINAL (router-vetted ≤cap) position until both incident
  // segments clear the cap (guaranteed to converge — the original position clears it).
  for (let pass = 0; pass < 6; pass++) {
    let fixed = true;
    for (let i = 1; i + 1 < jit.length; i++) {
      let guard = 0;
      while (guard++ < 8
        && (segGrade(heightM, jit[i - 1].u, jit[i - 1].v, jit[i].u, jit[i].v) > DEQUANT_GRADE_MAX
          || segGrade(heightM, jit[i].u, jit[i].v, jit[i + 1].u, jit[i + 1].v) > DEQUANT_GRADE_MAX)) {
        jit[i].u = (jit[i].u + pts[i].u) / 2; jit[i].v = (jit[i].v + pts[i].v) / 2;
        fixed = false;
      }
    }
    if (fixed) break;
  }
  // ── Pass 2: straight-run cap — split any run longer than STRAIGHT_CAP_M. ──
  const capped = capStraightRuns(jit, planetHash, rk, heightM, keeps);
  // ── Pass 3: Chaikin (endpoint-preserving, lateral-clamped, grade-guarded). The
  // caller may request 0 rounds (the planarity fallback in dequantizePrimaryNet). ──
  let cur = capped;
  for (let r = 0; r < chaikinRounds; r++) cur = chaikin(cur, heightM, keeps);
  return cur;
}

/**
 * The primary road-grade cap for de-quant guards (spec §D3, "≤20° grade rule"). Set
 * to tan(20°)+0.02 ≈ 0.384 to MATCH the router's own ≤20°+ε anchor-join tolerance
 * (worldgen-growth's PRIMARY_MAX_GRADE): the de-quant must not steepen a road beyond
 * what the router already emits, but need not be STRICTER (that over-reverts curves
 * to straight on near-20° terrain, regressing the geometry oracle).
 */
const DEQUANT_GRADE_MAX = 0.36397 + 0.02;

/** The <3-point path (a bare anchor→anchor edge, e.g. a link fallback): no interior
 *  to jitter, so just cap long straight runs then Chaikin. */
function jitterlessCapChaikin(
  pts: { u: number; v: number }[], planetHash: number, rk: number,
  heightM: (u: number, v: number) => number, chaikinRounds: number,
  keeps: (u: number, v: number) => boolean = () => true,
): { u: number; v: number }[] {
  if (pts.length < 2) return pts.map((p) => ({ u: p.u, v: p.v }));
  const capped = capStraightRuns(pts.map((p) => ({ u: p.u, v: p.v })), planetHash, rk, heightM, keeps);
  let cur = capped;
  for (let r = 0; r < chaikinRounds; r++) cur = chaikin(cur, heightM, keeps);
  return cur;
}

/**
 * Split every maximal near-straight run longer than STRAIGHT_CAP_M by inserting a
 * gentle hash-jittered perpendicular bend (amplitude 2–5 m) at its midpoint, so no
 * straight run survives past the cap. A run is a maximal span of consecutive
 * segments whose interior turn stays < 1° (the geometry-oracle's collinearity
 * threshold). Endpoints are preserved.
 */
function capStraightRuns(
  pts: { u: number; v: number }[], planetHash: number, rk: number,
  heightM: (u: number, v: number) => number,
  keeps: (u: number, v: number) => boolean = () => true,
): { u: number; v: number }[] {
  const out: { u: number; v: number }[] = [];
  const n = pts.length;
  if (n < 2) return pts.map((p) => ({ u: p.u, v: p.v }));
  let runStart = 0;
  // Distance ALONG the current run since the last emitted bend / run start. A run is
  // (near-)collinear, so this ACCUMULATES across the many short lattice segments — a
  // 1131 m run of 20 m collinear steps must break at every ~120 m, not slip through
  // because each individual segment is < the cap (R1 Task 1.5: the hamlet through-road
  // is one long collinear run; per-SEGMENT capping never bent it — the raw defect).
  let sinceLastBend = 0;
  const flushRun = (endIdx: number): void => {
    for (let i = runStart + 1; i <= endIdx; i++) {
      let anchorU = pts[i - 1].u, anchorV = pts[i - 1].v;
      const segLen = pmath.hypot(pts[i].u - anchorU, pts[i].v - anchorV);
      const du = segLen > 1e-9 ? (pts[i].u - anchorU) / segLen : 0;
      const dv = segLen > 1e-9 ? (pts[i].v - anchorV) / segLen : 0;
      // Place bends at each point where the accumulated straight length crosses the cap.
      let travelled = 0; // distance covered along THIS segment so far
      while (sinceLastBend + (segLen - travelled) > STRAIGHT_CAP_M) {
        const advance = STRAIGHT_CAP_M - sinceLastBend; // reach the cap within this segment
        travelled += advance;
        const mu = anchorU + du * travelled, mv = anchorV + dv * travelled;
        const ru = Math.round(mu), rv = Math.round(mv);
        const amp0 = BEND_AMP_MIN_M + h01(hashInts(planetHash, SALT_BEND, rk, ru, rv, 0)) * (BEND_AMP_MAX_M - BEND_AMP_MIN_M);
        const sign = h01(hashInts(planetHash, SALT_BEND, rk, ru, rv, 1)) < 0.5 ? -1 : 1;
        // GRADE-ADAPTIVE amplitude (spec §D3 ≤20°): shrink the bend to the largest
        // amplitude that keeps the segment from the last emitted vertex ≤ the grade
        // cap. On a road already AT the cap no bend fits — the run stays straight
        // (grade-limited; the oracle exempts steep/bridge runs it cannot curve).
        const px = out[out.length - 1].u, py = out[out.length - 1].v;
        let placed = false;
        for (const a of [amp0, amp0 * 0.5, amp0 * 0.25, 0.8]) {
          const bu = mu - dv * a * sign, bv = mv + du * a * sign;
          // Reject a bend that leaves the cell (border reconciliation) or steepens past cap.
          if (segGrade(heightM, px, py, bu, bv) <= DEQUANT_GRADE_MAX && keeps(bu, bv)) { out.push({ u: bu, v: bv }); placed = true; break; }
        }
        if (!placed) out.push({ u: mu, v: mv }); // grade-limited / border: keep it on the line
        sinceLastBend = 0; // a bend (or a kept-on-line waypoint) resets the straight run
      }
      sinceLastBend += segLen - travelled; // carry the remainder of this segment
      out.push({ u: pts[i].u, v: pts[i].v });
    }
  };
  out.push({ u: pts[0].u, v: pts[0].v });
  for (let i = 1; i + 1 < n; i++) {
    const turn = 180 - angleAt(pts[i - 1], pts[i], pts[i + 1]);
    if (turn >= 1) { flushRun(i); runStart = i; sinceLastBend = 0; } // a real ≥1° turn ends the straight run
  }
  flushRun(n - 1);
  return out;
}

/** Interior angle (deg) at b for the corner a-b-c (180 = straight). */
function angleAt(a: { u: number; v: number }, b: { u: number; v: number }, c: { u: number; v: number }): number {
  let ux = a.u - b.u, uy = a.v - b.v, vx = c.u - b.u, vy = c.v - b.v;
  const ul = pmath.hypot(ux, uy), vl = pmath.hypot(vx, vy);
  if (ul < 1e-9 || vl < 1e-9) return 180;
  ux /= ul; uy /= ul; vx /= vl; vy /= vl;
  let dot = ux * vx + uy * vy; if (dot > 1) dot = 1; else if (dot < -1) dot = -1;
  return pmath.acos(dot) * RAD2DEG_G;
}

/**
 * One endpoint-preserving Chaikin round with a per-corner GRADE GUARD. Each interior
 * corner P_i is cut into L_i (¾ toward P_{i-1}) and R_i (¾ toward P_{i+1}); the cut
 * segment L_i→R_i spans the corner over a short horizontal run, so on steep ground it
 * can exceed the ≤20° road-grade cap. When it WOULD (and the pre-cut corner did not),
 * the corner is left UN-CUT (P_i kept) — smoothing never turns a road into a cliff
 * (spec §D3, "no smoothed point exceeds the grade unless pre-smooth did"). Endpoints
 * are preserved. Cut points lie ON the incident segments (lateral 0 → inside the
 * carved bed by construction).
 */
function chaikin(
  pts: { u: number; v: number }[], heightM: (u: number, v: number) => number,
  keeps: (u: number, v: number) => boolean = () => true,
): { u: number; v: number }[] {
  const n = pts.length;
  if (n < 3) return pts.map((p) => ({ u: p.u, v: p.v }));
  const out: { u: number; v: number }[] = [{ u: pts[0].u, v: pts[0].v }];
  for (let i = 1; i + 1 < n; i++) {
    const p = pts[i], a = pts[i - 1], b = pts[i + 1];
    const lu = 0.75 * p.u + 0.25 * a.u, lv = 0.75 * p.v + 0.25 * a.v; // toward prev
    const ru = 0.75 * p.u + 0.25 * b.u, rv = 0.75 * p.v + 0.25 * b.v; // toward next
    // Strict grade guard: if the cut segment L_i→R_i (which spans the corner over a
    // short run) OR either half-cut a→L_i / R_i→b would exceed the ≤20° cap, KEEP the
    // sharp vertex uncut — so Chaikin can never push a road over the grade rule.
    const steep = segGrade(heightM, lu, lv, ru, rv) > DEQUANT_GRADE_MAX
      || segGrade(heightM, a.u, a.v, lu, lv) > DEQUANT_GRADE_MAX
      || segGrade(heightM, ru, rv, b.u, b.v) > DEQUANT_GRADE_MAX;
    // Reject a cut whose points leave the cell (border reconciliation): keep the vertex.
    if (steep || !keeps(lu, lv) || !keeps(ru, rv)) out.push({ u: p.u, v: p.v }); // keep the sharp vertex
    else out.push({ u: lu, v: lv }, { u: ru, v: rv });
  }
  out.push({ u: pts[n - 1].u, v: pts[n - 1].v });
  return out;
}

// ── public types ────────────────────────────────────────────────────────────

/**
 * Opaque, reusable per-call scratch (no allocation in the sampling loops). Held
 * as a public type because the binding interface names it; callers treat it as
 * opaque and generally never construct one. Used by the isWater/wet guards in
 * primary routing + hamlet growth.
 */
export interface GrowthScratch {
  readonly dir: Float64Array;  // localToDir output (unit sphere dir)
  readonly tan: Float64Array;  // spare unit-tangent buffer
  readonly samp: TerrainSample;
}

/** Fresh scratch buffers. */
export function newGrowthScratch(): GrowthScratch {
  return {
    dir: new Float64Array(3),
    tan: new Float64Array(3),
    samp: { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 },
  };
}

// ── port projection ─────────────────────────────────────────────────────────

/**
 * All region-edge ports of `cell`, projected into region-local (u,v) meters.
 * Fixed order: side ascending (SIDE_NEG_U..SIDE_POS_V = 0..3), then port index
 * ascending (edgePorts returns them sorted by along-edge position t).
 *
 * SIGNATURE ADAPTATION (documented). The brief sketched
 * `portLocals(radius, cell, frame)`, but ports are a pure function of the EDGE
 * KEY, which folds in the planet hash — omitted from that sketch. `planetHash`
 * is therefore threaded as the first argument (it is `hashSeed(planet.seed)`);
 * the return shape `{u,v}[]` is unchanged from the spec.
 */
export function portLocals(planetHash: number, radius: number, cell: CellId, frame: RegionFrame): { u: number; v: number }[] {
  const out: { u: number; v: number }[] = [];
  const A: number[] = [0, 0, 0];
  const B: number[] = [0, 0, 0];
  const loc: [number, number] = [0, 0];
  for (let side = 0; side < 4; side++) {
    const ports = edgePorts(planetHash, radius, cell, side);
    edgeEndpoints(radius, cell, side, A, B);
    for (let i = 0; i < ports.length; i++) {
      const t = ports[i].t;
      // Port unit dir = normalize(A + (B - A) * t).
      let x = A[0] + (B[0] - A[0]) * t;
      let y = A[1] + (B[1] - A[1]) * t;
      let z = A[2] + (B[2] - A[2]) * t;
      const il = 1 / pmath.hypot(x, y, z);
      x *= il; y *= il; z *= il;
      dirToLocal(frame, x, y, z, loc);
      out.push({ u: loc[0], v: loc[1] });
    }
  }
  return out;
}

// ── trace ────────────────────────────────────────────────────────────────────

/** Height (amplitude units) at region-local (u,v), climate off. */
function heightAt(sampler: PlanetSampler, frame: RegionFrame, u: number, v: number, sc: GrowthScratch): number {
  localToDir(frame, u, v, sc.dir);
  sampler.sample(sc.dir[0], sc.dir[1], sc.dir[2], sc.samp, false);
  return sc.samp.h;
}

/**
 * True when (u,v) is at or below sea level (climate off). A no-sea planet
 * (seaRel === 0; universe.ts sets this when def.seaRadius is null) has NO water —
 * its sub-datum lowlands are dry land, not sea — so guard that first, matching
 * fields.ts's coastal convention (`sampler.seaRel !== 0`).
 */
function isWaterAt(sampler: PlanetSampler, frame: RegionFrame, u: number, v: number, sc: GrowthScratch): boolean {
  if (sampler.seaRel === 0) return false;
  return heightAt(sampler, frame, u, v, sc) <= sampler.seaRel;
}

// ── anchors + A* primaries (part 1b, W2d-1 Task 2) ────────────────────────────

/**
 * Emit a NODE_PORT for every region-edge port (cross-region boundary continuity —
 * a neighbour cell derives the SAME port dir, so its own NODE_PORT lands within
 * ≤1 m). Returns the builder node indices in port order. EVERY branch of the
 * region pipeline calls this first, so ports are present in seedless / hamlet /
 * waystation / village+ cells exactly as the old arterial tracer left them.
 */
function emitPorts(builder: CityGraphBuilder, ports: { u: number; v: number }[]): number[] {
  const idx: number[] = [];
  for (let i = 0; i < ports.length; i++) idx.push(builder.addNode(ports[i].u, ports[i].v, NODE_PORT));
  return idx;
}

/** Quantized chart-position key for exact node dedup (0.001 m grid). */
function posKey(u: number, v: number): string {
  return Math.round(u * 1000) + "," + Math.round(v * 1000);
}

/**
 * Memoize a {@link RouteTerrain} over the A* lattice. The router queries height /
 * water along lattice segments and isoDir at step midpoints,
 * re-sampling the SAME points thousands of times across a cell's several primaries
 * — each sample is an expensive PlanetSampler call. Exact coordinate keys
 * keep the fractional diagonal probes distinct, collapsing repeats to one sample per
 * point per region build. Shared across every routePrimary call in the cell (the
 * cache also spans routes, since primaries overlap). Pure — same key ⇒ same value.
 */
function memoTerrain(base: RouteTerrain): RouteTerrain {
  const hM = new Map<string, number>();
  const wM = new Map<string, number>();
  const iM = new Map<string, [number, number] | null>();
  const key = (u: number, v: number): string => u + "," + v;
  return {
    heightM(u, v) {
      const k = key(u, v);
      let h = hM.get(k);
      if (h === undefined) { h = base.heightM(u, v); hM.set(k, h); }
      return h;
    },
    isWater(u, v) {
      const k = key(u, v);
      let w = wM.get(k);
      if (w === undefined) { w = base.isWater(u, v) ? 1 : 0; wM.set(k, w); }
      return w === 1;
    },
    isoDir(u, v, out) {
      const k = key(u, v);
      let r = iM.get(k);
      if (r === undefined) {
        const o: [number, number] = [0, 0];
        r = base.isoDir(u, v, o) ? [o[0], o[1]] : null;
        iM.set(k, r);
      }
      if (r === null) return false;
      out[0] = r[0]; out[1] = r[1];
      return true;
    },
  };
}

/**
 * R1 review — build a memoized EXACT cell-confinement predicate for {@link routePrimary}
 * ({@link RouteOpts.inCell}). `(u,v)` is IN-cell iff its world direction's nearest
 * region cell is `cell` — the TRUE per-cell border test (dirToCell), not the axis-
 * aligned cellHalf+collar chart clip (which sits well inside the real border curve).
 * Confining every primary to its own cell means adjacent cells' roads meet ONLY at
 * shared border ports, so cross-cell weaves (incl. corner weaves) vanish. Cached by
 * 1 m-rounded coord (lattice nodes are 20 m apart, so no distinct node aliases).
 */
export function makeInCell(sampler: PlanetSampler, cell: CellId, frame: RegionFrame): (u: number, v: number) => boolean {
  const radius = sampler.def.radius;
  const level = cell.level;
  const self = packCell(cell);
  const dir = new Float64Array(3);
  const cache = new Map<string, boolean>();
  return (u: number, v: number): boolean => {
    const k = Math.round(u) + "," + Math.round(v);
    let hit = cache.get(k);
    if (hit === undefined) {
      localToDir(frame, u, v, dir);
      hit = packCell(dirToCell(radius, level, dir[0], dir[1], dir[2])) === self;
      cache.set(k, hit);
    }
    return hit;
  };
}

/**
 * R1 review — drop OUT-OF-CELL lattice nodes from the ends of a routed polyline.
 * The router's exact cell-confinement exempts the GOAL lattice node (the 20 m grid
 * point nearest a border-port anchor), which can sit up to ~½·lattice·√2 ≈ 14 m
 * INSIDE the neighbour cell; the emitted polyline then protrudes past the true
 * border and can weave with the neighbour's own border-hugging roads (the residual
 * 1 m-deep union-planarity crossing). Since the EXACT anchor endpoint is bridged
 * onto the polyline anyway, an out-of-cell end lattice node is redundant — trim it
 * (both ends), so every emitted chord runs from an IN-CELL node to the exact anchor.
 * Interior nodes are in-cell by router construction; only the ends need checking.
 */
function trimOutOfCellEnds(
  points: { u: number; v: number }[], inCell: (u: number, v: number) => boolean,
): { u: number; v: number }[] {
  let s = 0, e = points.length;
  while (s < e && !inCell(points[s].u, points[s].v)) s++;
  while (e > s && !inCell(points[e - 1].u, points[e - 1].v)) e--;
  return s === 0 && e === points.length ? points : points.slice(s, e);
}

export interface Anchor { u: number; v: number; port: boolean; }

// ── R3: LANDMARK-FIRST anchors (spec §Architecture reconciliation) ─────────────
//
// A tier-4+-minded settlement is organised AROUND its civic flagship: the
// landmark's SITE is chosen first (hashed high-suitability point near the seed),
// becomes a routing ANCHOR (the trunk network converges on it), then placement
// puts the tier's landmark USE on the parcel nearest that site and the plaza
// prefers the junction nearest it (cathedral-on-the-square). TOWN gets the same
// treatment (its temple) — the spec's "tier 4+" names anchors specifically, but
// the site/placement machinery is tier-uniform and reads well from TOWN up.

/** Additional landmark uses. City administration is authored by the civic kit. */
export const LANDMARK_USE: readonly number[] = [0, 0, 0, USE_TEMPLE, 0, USE_LOOM_NEXUS];
/** Landmark site roll (hash.ts registry). */
const SALT_LANDMARK = 0x1a4d;
/** Landmark site search band (fraction of tier radius) + candidate count. */
const LANDMARK_BAND_FRAC = 0.28;
const LANDMARK_CANDS = 6;

/**
 * The settlement's hashed LANDMARK SITE: best-suitability of LANDMARK_CANDS
 * hashed candidates within LANDMARK_BAND_FRAC × tier radius of the seed. Pure —
 * selectAnchors (routing) and runRegionPipeline (placement/plaza) both derive
 * the SAME point. Null below TOWN.
 */
export function landmarkSite(
  sampler: PlanetSampler, frame: RegionFrame, cell: CellId,
  seed: { u: number; v: number; k: number; tier: number },
): { u: number; v: number } | null {
  if (seed.tier < TIER_TOWN) return null;
  const planetHash = hashSeed(sampler.def.seed);
  const rk = packCell(cell);
  const band = TIER_PARAMS[seed.tier].radiusM * LANDMARK_BAND_FRAC;
  const dir = new Float64Array(3);
  let bu = seed.u, bv = seed.v, bs = -Infinity;
  for (let i = 0; i < LANDMARK_CANDS; i++) {
    const rr = band * Math.sqrt(h01(hashInts(planetHash, SALT_LANDMARK, rk, seed.k, i * 2)));
    const th = pmath.TWO_PI * h01(hashInts(planetHash, SALT_LANDMARK, rk, seed.k, i * 2 + 1));
    const u = seed.u + rr * pmath.cos(th);
    const v = seed.v + rr * pmath.sin(th);
    localToDir(frame, u, v, dir);
    const s = suitabilityAt(sampler, dir[0], dir[1], dir[2]);
    if (s > bs) { bs = s; bu = u; bv = v; }
  }
  return { u: bu, v: bv };
}

/**
 * Select a VILLAGE+ settlement's anchor set (spec §"Core growth parameters"):
 * the seed (anchor 0), up to {@link ANCHOR_MAX_PORTS} seed-nearest shared PORTS
 * (hashed boundary conditions — the neighbour cell derives the SAME port dir, so
 * a primary routed to it agrees cross-cell), then hash-rolled high-suitability
 * points inside the tier radius, to a hash-rolled total in ANCHOR_COUNTS[tier].
 * Deterministic: ports ranked by (chart dist², index); candidate points by
 * (suit desc, index); no float-order Set/Map dependence.
 */
export function selectAnchors(
  sampler: PlanetSampler, cell: CellId, frame: RegionFrame,
  seed: { u: number; v: number; k: number; tier: number },
  ports: { u: number; v: number }[],
): Anchor[] {
  const planetHash = hashSeed(sampler.def.seed);
  const rk = packCell(cell);
  const [lo, hi] = ANCHOR_COUNTS[seed.tier];
  const count = Math.max(1, lo + Math.floor(h01(hashInts(planetHash, SALT_ANCHOR, rk, seed.k, 0)) * (hi - lo + 1)));
  const anchors: Anchor[] = [{ u: seed.u, v: seed.v, port: false }];

  // up to ANCHOR_MAX_PORTS seed-nearest ports (never more than count-1 slots).
  const order: number[] = [];
  for (let i = 0; i < ports.length; i++) order.push(i);
  order.sort((a, b) => {
    const da = (ports[a].u - seed.u) * (ports[a].u - seed.u) + (ports[a].v - seed.v) * (ports[a].v - seed.v);
    const db = (ports[b].u - seed.u) * (ports[b].u - seed.u) + (ports[b].v - seed.v) * (ports[b].v - seed.v);
    return (da - db) || (a - b);
  });
  const nPorts = Math.min(ANCHOR_MAX_PORTS, count - 1, ports.length);
  for (let i = 0; i < nPorts; i++) anchors.push({ u: ports[order[i]].u, v: ports[order[i]].v, port: true });

  // fill the remaining slots with hashed high-suitability points in the radius.
  const remaining = count - anchors.length;
  if (remaining > 0) {
    const radiusM = TIER_PARAMS[seed.tier].radiusM;
    const dir = new Float64Array(3);
    const cand = remaining * ANCHOR_CAND_MULT;
    const cu = new Float64Array(cand), cv = new Float64Array(cand), cs = new Float64Array(cand);
    for (let i = 0; i < cand; i++) {
      const rr = radiusM * Math.sqrt(h01(hashInts(planetHash, SALT_ANCHOR, rk, seed.k, i * 2 + 1)));
      const th = pmath.TWO_PI * h01(hashInts(planetHash, SALT_ANCHOR, rk, seed.k, i * 2 + 2));
      const u = seed.u + rr * pmath.cos(th);
      const v = seed.v + rr * pmath.sin(th);
      cu[i] = u; cv[i] = v;
      localToDir(frame, u, v, dir);
      cs[i] = suitabilityAt(sampler, dir[0], dir[1], dir[2]);
    }
    const ci: number[] = [];
    for (let i = 0; i < cand; i++) ci.push(i);
    ci.sort((a, b) => (cs[b] - cs[a]) || (a - b));
    for (let i = 0; i < remaining && i < ci.length; i++) anchors.push({ u: cu[ci[i]], v: cv[ci[i]], port: false });
  }
  return anchors;
}

/** Trunk-share accounting for the primary network (reported by the oracle). */
export interface PrimaryStats { primaries: number; totalLen: number; reuseLen: number; }

/**
 * Route a VILLAGE+ cell's PRIMARY roads (spec §"Core growth parameters"). For
 * each seed (ascending k) an anchor set is chosen (see {@link selectAnchors}) and
 * up to PRIMARY_COUNTS[tier] roads are routed through ONE shared {@link RoadNetwork}
 * — the seed→anchor star first (so the settlement is connected), then the
 * anchor→anchor nearest chain — so the router's reuse discount pulls later roads
 * onto earlier ones (trunk-and-branch). Each routed polyline is BRIDGED onto its
 * EXACT anchor/port/seed endpoints (the router snaps its ends to the ~20 m
 * lattice, so the first/last lattice point is replaced by the exact endpoint —
 * the lattice snap thus never moves a seed or a shared port, keeping cross-cell
 * port agreement ≤1 m), and emitted as EDGE_ARTERIAL / EDGE_BRIDGE edges (epoch 0,
 * unlogged — primaries predate street epochs). Returns trunk-share stats.
 */
export function routePrimaries(
  sampler: PlanetSampler, cell: CellId, frame: RegionFrame, builder: CityGraphBuilder,
  ports: { u: number; v: number }[], portNodes: number[],
  seeds: { u: number; v: number; k: number; tier: number }[],
  sharedTerrain?: RouteTerrain,
): PrimaryStats {
  const planetHash = hashSeed(sampler.def.seed);
  const rk = packCell(cell);
  // Carry #5 / orchestrator note: reuse the ONE memoized RouteTerrain the region
  // pipeline shares with the tensor grower, when supplied; else build our own.
  const terrain = sharedTerrain ?? memoTerrain(makeSamplerTerrain(sampler, frame));
  const net = new RoadNetwork();       // shared across ALL seeds → inter-settlement reuse
  const measure = new RoadNetwork();   // parallel net, trunk-share accounting only
  const sc = newGrowthScratch();
  const stats: PrimaryStats = { primaries: 0, totalLen: 0, reuseLen: 0 };
  // R1 D2: the hard chart bound that keeps a cell's primaries from routing deep into a
  // neighbour cell — so adjacent cells only ever meet at shared ports (cross-cell union
  // planarity). See PRIMARY_BOUND_COLLAR_M + the D2 note in the R1 report for why the
  // spec's approach-corridor COST WALL was dropped (measured counterproductive: it
  // distorts routing and ADDS border crossings; confinement delivers the goal cleanly).
  const boundM = cellArcM(sampler.def.radius, cell.level) / 2 + PRIMARY_BOUND_COLLAR_M;
  // R1 review: exact cell-confinement predicate (adjacent cells meet only at shared
  // ports → no cross-cell weaves). Shared across every route in the cell (memoized).
  const inCell = makeInCell(sampler, cell, frame);
  // R1 D1: primary edges (node-index pairs) collected for the union-find link pass.
  const primEdges: [number, number][] = [];

  const nodeOf = new Map<string, number>();
  for (let i = 0; i < ports.length; i++) nodeOf.set(posKey(ports[i].u, ports[i].v), portNodes[i]);
  const special = new Map<string, number>();
  const getNode = (u: number, v: number): number => {
    const key = posKey(u, v);
    let idx = nodeOf.get(key);
    if (idx === undefined) { idx = builder.addNode(u, v, special.get(key) ?? NODE_JUNCTION); nodeOf.set(key, idx); }
    return idx;
  };

  for (let si = 0; si < seeds.length; si++) {
    const seed = seeds[si];
    if (seed.tier < TIER_VILLAGE) continue; // a hamlet/waystation seed in a mixed cell routes no primary
    special.set(posKey(seed.u, seed.v), NODE_SEED);
    const anchors = selectAnchors(sampler, cell, frame, seed, ports);
    const [plo, phi] = PRIMARY_COUNTS[seed.tier];
    const primaryCount = Math.max(1, plo + Math.floor(h01(hashInts(planetHash, SALT_PRIMARY, rk, seed.k)) * (phi - plo + 1)));

    // R3 NOTE (measured deviation): the spec's "landmark as routing anchor" was
    // tried THREE ways (slot-consuming / count+1 / separately-routed spoke) and
    // every variant swung the downstream building calibration wildly (town
    // 76 → 27 / 126, metro 84 → 33 — secondary fronts seed from primary nodes,
    // so ANY route-structure change cascades). The landmark therefore does NOT
    // perturb routing: the seed star (0.28 R away) already organises the core.
    // Landmark-FIRST survives where it is visible: placement puts the flagship
    // ON the hashed site, the plaza prefers the junction nearest it, and the
    // building renders oversized. Trunk-convergence-on-landmark is recorded as
    // a future dedicated recalibration if wanted.

    // seed→anchor star (nearest first), then the anchor→anchor nearest chain.
    const nonSeed: number[] = [];
    for (let a = 1; a < anchors.length; a++) nonSeed.push(a);
    nonSeed.sort((a, b) => {
      const da = (anchors[a].u - seed.u) * (anchors[a].u - seed.u) + (anchors[a].v - seed.v) * (anchors[a].v - seed.v);
      const db = (anchors[b].u - seed.u) * (anchors[b].u - seed.u) + (anchors[b].v - seed.v) * (anchors[b].v - seed.v);
      return (da - db) || (a - b);
    });
    const pairs: [number, number][] = [];
    for (const a of nonSeed) pairs.push([0, a]);
    for (let i = 0; i + 1 < nonSeed.length; i++) pairs.push([nonSeed[i], nonSeed[i + 1]]);

    let routed = 0;
    for (let pi = 0; pi < pairs.length && routed < primaryCount; pi++) {
      const A = anchors[pairs[pi][0]], B = anchors[pairs[pi][1]];
      const res = routePrimary(terrain, net, { u: A.u, v: A.v }, { u: B.u, v: B.v }, { boundM, inCell });
      if (res === null || res.points.length < 2) continue;
      // Keep the router's vetted lattice points and BRIDGE the exact endpoints onto
      // them (a ≤ half-lattice ~14 m snap join at each end — TASK-2 note #2). This
      // never moves a seed/port, so cross-cell port agreement stays ≤1 m; the join
      // segment's grade is the terrain at the anchor (a port may sit on a cliff),
      // reported separately from the router's ≤20° interior steps. Out-of-cell END
      // lattice nodes (the exempt goal node near a border port) are trimmed first —
      // see trimOutOfCellEnds (R1 review, border weave fix).
      const pts = [{ u: A.u, v: A.v }, ...trimOutOfCellEnds(res.points, inCell), { u: B.u, v: B.v }];
      // Emit CLEAN lattice primaries (shared junctions stay single deduped nodes).
      // R1 D3 de-quantization runs LATER, on the shared planar net's primary CHAINS
      // (growSecondaryNetwork), so junction coincidence is preserved by construction.
      emitPolyline(builder, getNode, pts, sampler, frame, sc, stats, measure, primEdges);
      routed++;
    }
    stats.primaries += routed;
  }

  // R1 D1: CONNECTIVITY BY CONSTRUCTION — link disconnected primary components into
  // ONE network per cell. While >1 component, route a connecting primary between the
  // closest node pair of two distinct components through the SHARED net (reuse
  // discount on, chart bound on, deterministic index tie-breaks).
  linkPrimaryComponents(
    builder, getNode, primEdges, terrain, net, boundM, inCell,
    sampler, frame, sc, stats, measure,
  );
  return stats;
}

/**
 * R1 D1 — link a cell's disconnected PRIMARY components into one network. Union-find
 * over `primEdges`; while >1 edge-bearing component remains, find the globally
 * closest node pair across two distinct components (deterministic: min chart
 * distance, ties by (nodeA,nodeB) index), route a connecting primary between them
 * through the shared {@link RoadNetwork} (reuse discount + chart bound active),
 * de-quantize + emit it, and union the two components. On an A* miss the two nodes
 * are joined by a direct de-quantized link so connectivity is GUARANTEED (documented
 * fallback — a missing link would read as broken geometry). Draws NO randomness (the
 * de-quant salts aside), so it registers no new salt.
 */
function linkPrimaryComponents(
  builder: CityGraphBuilder, getNode: (u: number, v: number) => number,
  primEdges: [number, number][], terrain: RouteTerrain, net: RoadNetwork,
  boundM: number, inCell: (u: number, v: number) => boolean,
  sampler: PlanetSampler, frame: RegionFrame, sc: GrowthScratch,
  stats: PrimaryStats, measure: RoadNetwork,
): void {
  if (primEdges.length === 0) return;
  // Nodes that bear a primary edge (ascending, unique).
  const nodeSet = new Set<number>();
  for (const [a, b] of primEdges) { nodeSet.add(a); nodeSet.add(b); }
  const nodes = [...nodeSet].sort((x, y) => x - y);
  // Union-find over ALL builder nodes (indices are dense; size = current count).
  const parent = new Int32Array(builder.nodeCount);
  for (let i = 0; i < parent.length; i++) parent[i] = i;
  const find = (x: number): number => { while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; } return x; };
  const union = (a: number, b: number): void => { const ra = find(a), rb = find(b); if (ra !== rb) parent[ra < rb ? rb : ra] = ra < rb ? ra : rb; };
  for (const [a, b] of primEdges) union(a, b);

  const componentCount = (): number => {
    const roots = new Set<number>();
    for (const n of nodes) roots.add(find(n));
    return roots.size;
  };
  let guard = 0;
  while (componentCount() > 1 && guard < nodes.length) {
    guard++;
    // Globally closest node pair across two distinct components.
    let bestD = Infinity, bestA = -1, bestB = -1;
    for (let i = 0; i < nodes.length; i++) {
      const a = nodes[i], ra = find(a);
      const au = builder.nodeUAt(a), av = builder.nodeVAt(a);
      for (let j = i + 1; j < nodes.length; j++) {
        const b = nodes[j];
        if (find(b) === ra) continue;
        const d = pmath.hypot(builder.nodeUAt(b) - au, builder.nodeVAt(b) - av);
        if (d < bestD - 1e-9) { bestD = d; bestA = a; bestB = b; }
      }
    }
    if (bestA < 0) break;
    const A = { u: builder.nodeUAt(bestA), v: builder.nodeVAt(bestA) };
    const B = { u: builder.nodeUAt(bestB), v: builder.nodeVAt(bestB) };
    const res = routePrimary(terrain, net, A, B, { boundM, inCell });
    const pts = res !== null && res.points.length >= 2
      ? [A, ...trimOutOfCellEnds(res.points, inCell), B] : [A, B];
    emitPolyline(builder, getNode, pts, sampler, frame, sc, stats, measure);
    stats.primaries += 1;
    union(bestA, bestB);
  }
}

/**
 * R1 D1 — link the disconnected PRIMARY components of an ALREADY-BUILT builder graph
 * into one network (the HAMLET-branch analogue of {@link linkPrimaryComponents}: two
 * hamlet through-roads in one cell are otherwise two islands). Snapshots the builder,
 * union-finds its edge-bearing components, and while >1 routes a de-quantized (per-
 * polyline) connecting primary between the closest node pair of two distinct
 * components (shared RoadNetwork + chart bound; deterministic index
 * tie-breaks; direct de-quantized fallback on an A* miss so connectivity is
 * GUARANTEED). Runs BEFORE pruneUtility, whose bridge exemption then preserves it.
 */
function linkBuilderPrimaries(
  sampler: PlanetSampler, cell: CellId, frame: RegionFrame, builder: CityGraphBuilder,
): void {
  const g = builder.build();
  if (g.edgeA.length === 0) return;
  const terrain = memoTerrain(makeSamplerTerrain(sampler, frame));
  const cellHalfM = cellArcM(sampler.def.radius, cell.level) / 2;
  const boundM = cellHalfM + PRIMARY_BOUND_COLLAR_M;
  const inCell = makeInCell(sampler, cell, frame);
  const net = new RoadNetwork();
  const measure = new RoadNetwork();
  const sc = newGrowthScratch();
  const stats: PrimaryStats = { primaries: 0, totalLen: 0, reuseLen: 0 };
  for (let e = 0; e < g.edgeA.length; e++) {
    const a = g.edgeA[e], b = g.edgeB[e];
    net.register([{ u: g.nodeU[a], v: g.nodeV[a] }, { u: g.nodeU[b], v: g.nodeV[b] }]);
  }
  const nodeOf = new Map<string, number>();
  for (let i = 0; i < g.nodeU.length; i++) nodeOf.set(posKey(g.nodeU[i], g.nodeV[i]), i);
  const getNode = (u: number, v: number): number => {
    const k = posKey(u, v);
    let idx = nodeOf.get(k);
    if (idx === undefined) { idx = builder.addNode(u, v, NODE_JUNCTION); nodeOf.set(k, idx); }
    return idx;
  };
  const nc = g.nodeU.length;
  const parent = new Int32Array(nc);
  for (let i = 0; i < nc; i++) parent[i] = i;
  const find = (x: number): number => { while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; } return x; };
  const union = (a: number, b: number): void => { const ra = find(a), rb = find(b); if (ra !== rb) parent[ra < rb ? rb : ra] = ra < rb ? ra : rb; };
  const nodeSet = new Set<number>();
  for (let e = 0; e < g.edgeA.length; e++) { union(g.edgeA[e], g.edgeB[e]); nodeSet.add(g.edgeA[e]); nodeSet.add(g.edgeB[e]); }
  const nodes = [...nodeSet].sort((x, y) => x - y);
  const compCount = (): number => { const r = new Set<number>(); for (const n of nodes) r.add(find(n)); return r.size; };
  let guard = 0;
  while (compCount() > 1 && guard < nodes.length) {
    guard++;
    let bestD = Infinity, bestA = -1, bestB = -1;
    for (let i = 0; i < nodes.length; i++) {
      const a = nodes[i], ra = find(a);
      const au = g.nodeU[a], av = g.nodeV[a];
      for (let j = i + 1; j < nodes.length; j++) {
        const b = nodes[j];
        if (find(b) === ra) continue;
        const d = pmath.hypot(g.nodeU[b] - au, g.nodeV[b] - av);
        if (d < bestD - 1e-9) { bestD = d; bestA = a; bestB = b; }
      }
    }
    if (bestA < 0) break;
    const A = { u: g.nodeU[bestA], v: g.nodeV[bestA] };
    const B = { u: g.nodeU[bestB], v: g.nodeV[bestB] };
    const res = routePrimary(terrain, net, A, B, { boundM, inCell });
    const raw = res !== null && res.points.length >= 2
      ? [A, ...trimOutOfCellEnds(res.points, inCell), B] : [A, B];
    // R1 review (finding 5): HAMLET-only path (linkBuilderPrimaries runs solely in the
    // hamlet branch), which has no planarity repair — so link roads stay on the clean
    // A* lattice (a de-quantized curve would self-cross with no junction). See growHamlet.
    const pts = raw;
    emitPolyline(builder, getNode, pts, sampler, frame, sc, stats, measure);
    union(bestA, bestB);
  }
}

/**
 * Emit a routed polyline as EDGE_ARTERIAL / EDGE_BRIDGE edges (a segment with a
 * wet centreline probe becomes a bridge) with exact-position node
 * dedup, and fold its trunk-share (fraction of length whose segment midpoints lie
 * within REUSE_RADIUS_M of the already-emitted primaries) into `stats`.
 */
function emitPolyline(
  builder: CityGraphBuilder, getNode: (u: number, v: number) => number,
  pts: { u: number; v: number }[], sampler: PlanetSampler, frame: RegionFrame,
  sc: GrowthScratch, stats: PrimaryStats, measure: RoadNetwork,
  edgesOut?: [number, number][],
): void {
  let prev = getNode(pts[0].u, pts[0].v);
  for (let i = 1; i < pts.length; i++) {
    const cur = getNode(pts[i].u, pts[i].v);
    if (cur !== prev) {
      const wet = segmentHasWater((u, v) => isWaterAt(sampler, frame, u, v, sc),
        pts[i - 1].u, pts[i - 1].v, pts[i].u, pts[i].v);
      builder.addEdge(prev, cur, wet ? EDGE_BRIDGE : EDGE_ARTERIAL, 0);
      if (edgesOut !== undefined) edgesOut.push([prev, cur]);
      const len = pmath.hypot(pts[i].u - pts[i - 1].u, pts[i].v - pts[i - 1].v);
      const mu = (pts[i - 1].u + pts[i].u) / 2, mv = (pts[i - 1].v + pts[i].v) / 2;
      stats.totalLen += len;
      if (measure.nearPolyline(mu, mv, REUSE_RADIUS_M)) stats.reuseLen += len;
    }
    prev = cur;
  }
  measure.register(pts);
}

// ── hamlet through-road + buildings (part 1c, spec §"Tiers 1–2") ──────────────

/**
 * Grow a HAMLET's THROUGH-ROAD (spec §"Tiers 1–2"): ONE A* through-road — the two
 * seed-nearest ports routed THROUGH the seed (fallback: a single port→seed) — emitted
 * as EDGE_ARTERIAL/BRIDGE on the clean A* lattice.
 *
 * R1 Task 1.5 SPLIT: cottage placement is now a SEPARATE pass ({@link placeHamletCottages}),
 * run AFTER the whole cell's road graph is linked ({@link linkBuilderPrimaries}) and
 * DE-QUANTIZED ({@link curveHamletPrimaries}). Deferring cottages lets the through-roads
 * gain planarity discipline + gentle curvature first, so cottages anchor on the CURVED
 * road (the R1 road-ribbon exclusion in placeHamletCottages already anticipates a
 * de-quantized road that curves/loops back). The old hamlet SC growth is gone — a
 * hamlet is a lane and its cottages.
 */
function growHamlet(
  sampler: PlanetSampler, cell: CellId, frame: RegionFrame, builder: CityGraphBuilder,
  ports: { u: number; v: number }[], portNodes: number[],
  seed: { u: number; v: number; k: number; tier: number },
  sharedNet?: RoadNetwork, sharedMeasure?: RoadNetwork,
): void {
  const terrain = memoTerrain(makeSamplerTerrain(sampler, frame));
  // R1 D7-F5: all hamlet seeds in a cell share ONE RoadNetwork (hoisted to the
  // caller loop in runRegionPipeline), so a second hamlet's through-road earns the
  // mutual reuse discount off the first — same/nearby-port through-roads converge
  // onto a shared trunk instead of running as separate near-parallels the linker
  // then sews into closed eyes. Standalone callers (tests) get a fresh empty net.
  const net = sharedNet ?? new RoadNetwork();
  const measure = sharedMeasure ?? new RoadNetwork();
  const sc = newGrowthScratch();
  // R1 D2: the hamlet through-road is a PRIMARY (EDGE_ARTERIAL/BRIDGE), so it takes the
  // SAME chart bound + exact cell-confinement (makeInCell) as VILLAGE+ primaries, so it
  // meets neighbours only at shared ports. R1 Task 1.5: the whole cell's road graph is
  // later de-quantized as one planar net (curveHamletPrimaries), so it is emitted on the
  // clean A* lattice HERE and curved after linking.
  const cellHalfM = cellArcM(sampler.def.radius, cell.level) / 2;
  const boundM = cellHalfM + PRIMARY_BOUND_COLLAR_M;
  const inCell = makeInCell(sampler, cell, frame);

  const nodeOf = new Map<string, number>();
  for (let i = 0; i < ports.length; i++) nodeOf.set(posKey(ports[i].u, ports[i].v), portNodes[i]);
  const special = new Map<string, number>();
  special.set(posKey(seed.u, seed.v), NODE_SEED);
  const getNode = (u: number, v: number): number => {
    const key = posKey(u, v);
    let idx = nodeOf.get(key);
    if (idx === undefined) { idx = builder.addNode(u, v, special.get(key) ?? NODE_JUNCTION); nodeOf.set(key, idx); }
    return idx;
  };

  const order: number[] = [];
  for (let i = 0; i < ports.length; i++) order.push(i);
  order.sort((a, b) => {
    const da = (ports[a].u - seed.u) * (ports[a].u - seed.u) + (ports[a].v - seed.v) * (ports[a].v - seed.v);
    const db = (ports[b].u - seed.u) * (ports[b].u - seed.u) + (ports[b].v - seed.v) * (ports[b].v - seed.v);
    return (da - db) || (a - b);
  });

  // Through-road as an ORDERED node-index polyline (port0 → seed → port1). Each
  // leg is a routePrimary; the seed sits at the shared join (deduped).
  const roadNodes: number[] = [];
  const addLeg = (from: { u: number; v: number }, to: { u: number; v: number }): void => {
    const res = routePrimary(terrain, net, from, to, { boundM, inCell });
    if (res === null || res.points.length < 2) return;
    const pts = [{ u: from.u, v: from.v }, ...trimOutOfCellEnds(res.points, inCell), { u: to.u, v: to.v }];
    // R1 Task 1.5: emit the through-road on the CLEAN A* lattice here; the whole cell's
    // road graph is later given planarity discipline + de-quantized as one net
    // ({@link curveHamletPrimaries} in runRegionPipeline), so hamlet through-roads gain
    // the same gentle curvature VILLAGE+ primaries have WITHOUT the self-crossings that
    // per-leg curving on a net-less builder produced (T1 finding 5). Oracle 4 (0
    // hamlet self-crossings) guards the planarity that de-quant then relies on.
    let prev = getNode(pts[0].u, pts[0].v);
    if (roadNodes.length === 0) roadNodes.push(prev);
    for (let i = 1; i < pts.length; i++) {
      const cur = getNode(pts[i].u, pts[i].v);
      if (cur !== prev) {
        const wet = segmentHasWater((u, v) => isWaterAt(sampler, frame, u, v, sc),
          pts[i - 1].u, pts[i - 1].v, pts[i].u, pts[i].v);
        builder.addEdge(prev, cur, wet ? EDGE_BRIDGE : EDGE_ARTERIAL, 0);
        if (roadNodes[roadNodes.length - 1] !== cur) roadNodes.push(cur);
      }
      prev = cur;
    }
    measure.register(pts);
  };
  if (order.length >= 2) {
    addLeg(ports[order[0]], { u: seed.u, v: seed.v });
    addLeg({ u: seed.u, v: seed.v }, ports[order[1]]);
  } else if (order.length === 1) {
    addLeg(ports[order[0]], { u: seed.u, v: seed.v });
  }
}

/**
 * R1 Task 1.5 — trace a hamlet seed's DE-QUANTIZED through-road as an ordered node-index
 * lane (port → … → seed → … → port) over the rebuilt (curved) graph, so cottages anchor
 * on the CURVED road. Starts at the seed's NODE_SEED node (found by exact position — the
 * de-quant flush preserves node kinds + anchor positions) and walks each incident PRIMARY
 * direction to an endpoint/junction, collecting the ordered node indices. Returns [] if no
 * seed node or no primary lane is present (a degenerate/pruned hamlet — no cottages).
 * Deterministic: incident primaries are walked in ascending edge order; a walk stops at
 * any non-degree-2 primary node (port/junction) or a visited edge (no infinite loop).
 */
function hamletSeedLane(
  builder: CityGraphBuilder, seed: { u: number; v: number },
): number[] {
  // Locate the seed node by kind + exact position (the de-quant flush kept both).
  let seedNode = -1;
  for (let i = 0; i < builder.nodeCount; i++) {
    if (builder.nodeKindAt(i) !== NODE_SEED) continue;
    if (posKey(builder.nodeUAt(i), builder.nodeVAt(i)) === posKey(seed.u, seed.v)) { seedNode = i; break; }
  }
  if (seedNode < 0) return [];
  const g = builder.build();
  const isPrim = (k: number): boolean => k === EDGE_ARTERIAL || k === EDGE_BRIDGE;
  const padj: number[][] = Array.from({ length: g.nodeU.length }, () => []);
  for (let e = 0; e < g.edgeA.length; e++) if (isPrim(g.edgeKind[e])) { padj[g.edgeA[e]].push(g.edgeB[e]); padj[g.edgeB[e]].push(g.edgeA[e]); }
  if (padj[seedNode].length === 0) return [];
  // Walk one direction from the seed to an endpoint/junction, returning the ordered
  // node list from that endpoint IN toward the seed (excluding the seed).
  const walk = (first: number): number[] => {
    const out: number[] = [];
    let prev = seedNode, cur = first;
    const seen = new Set<number>([seedNode]);
    while (true) {
      out.push(cur);
      if (seen.has(cur)) break;
      seen.add(cur);
      if (padj[cur].length !== 2) break; // endpoint / junction
      const nx = padj[cur][0] === prev ? padj[cur][1] : padj[cur][0];
      prev = cur; cur = nx;
    }
    return out;
  };
  // Two lane arms off the seed (ports at both ends): [armA reversed] + seed + [armB].
  const arms = padj[seedNode];
  const armA = walk(arms[0]);
  const lane: number[] = [];
  for (let i = armA.length - 1; i >= 0; i--) lane.push(armA[i]);
  lane.push(seedNode);
  if (arms.length >= 2) { const armB = walk(arms[1]); for (const n of armB) lane.push(n); }
  return lane;
}

/**
 * R1 Task 1.5 — place a hamlet seed's 8–15 cottages on its (now de-quantized) through-
 * road: interior road nodes within the cluster radius, offset 10–25 m perpendicular with
 * ±25° heading noise, each a driveway stub (STREET, logged) + a square parcel. Split out
 * of {@link growHamlet} so it runs AFTER {@link curveHamletPrimaries} — cottages front the
 * curved lane. Hash draws (SALT_HAMLET, per-seed) are UNCHANGED from the old inline path;
 * only the anchor lane is now the curved road's node list ({@link hamletSeedLane}).
 */
function placeHamletCottages(
  sampler: PlanetSampler, cell: CellId, frame: RegionFrame, builder: CityGraphBuilder,
  seed: { u: number; v: number; k: number; tier: number },
): void {
  const planetHash = hashSeed(sampler.def.seed);
  const rk = packCell(cell);
  const sc = newGrowthScratch();
  const roadNodes = hamletSeedLane(builder, seed);
  if (roadNodes.length < 3) return; // need at least one INTERIOR node to build on

  // Buildings: 8–15, anchored on interior road nodes (exclude the two port ends),
  // offset perpendicular by 10–25 m (near face), side + noise hashed. Each gets a
  // driveway stub (STREET, logged) + a square parcel fronting it (epoch = stub).
  let logCount = builder.logCount;
  const [blo, bhi] = HAMLET_BUILDINGS;
  const want = blo + Math.floor(h01(hashInts(planetHash, SALT_HAMLET, rk, seed.k, 0)) * (bhi - blo + 1));
  // Cottages cluster around the seed along the lane: only interior road nodes
  // within the hamlet tier radius are candidates (keeps every parcel inside the
  // 1.5× seed radius the F11 lot-clustering discipline requires, and reads as a
  // settlement rather than houses strung to the cell edge).
  const clusterR = TIER_PARAMS[TIER_HAMLET].radiusM;
  const clusterR2 = clusterR * clusterR;
  const interior: number[] = [];
  for (let i = 1; i + 1 < roadNodes.length; i++) {
    const du = builder.nodeUAt(roadNodes[i]) - seed.u, dv = builder.nodeVAt(roadNodes[i]) - seed.v;
    if (du * du + dv * dv <= clusterR2) interior.push(i);
  }
  if (interior.length === 0) return;
  const dir = new Float64Array(3);
  const samp: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
  const hAt = (u: number, v: number): number => { localToDir(frame, u, v, dir); sampler.sample(dir[0], dir[1], dir[2], samp, false); return samp.h; };
  // ROAD-ribbon exclusion (R1): snapshot every non-tunnel edge present before cottage
  // placement (this hamlet's DE-QUANTIZED road can curve/loop back to within a lot's
  // exclusion band, and a co-located hamlet's road likewise), so a cottage is rejected
  // if it lands on ANY ribbon. The cottage is 10–25 m off its own lane's anchor segment
  // (≫ the ~5 m band), so its own straight approach never false-rejects — only a curved
  // loop-back within the band does. Its own driveway stub is added AFTER this snapshot.
  const g0 = builder.build();
  const fau: number[] = [], fav: number[] = [], fbu: number[] = [], fbv: number[] = [], fband: number[] = [];
  for (let e = 0; e < g0.edgeKind.length; e++) {
    if (g0.edgeKind[e] === EDGE_TUNNEL) continue;
    const a = g0.edgeA[e], b = g0.edgeB[e];
    fau.push(g0.nodeU[a]); fav.push(g0.nodeV[a]); fbu.push(g0.nodeU[b]); fbv.push(g0.nodeV[b]);
    fband.push(ROAD_HALF_BY_KIND[g0.edgeKind[e]] + ROAD_EXCLUDE_M);
  }
  const onForeignRoad = (pu: number, pv: number): boolean => {
    for (let f = 0; f < fau.length; f++) if (pointSegDist(pu, pv, fau[f], fav[f], fbu[f], fbv[f]) <= fband[f]) return true;
    return false;
  };
  const acceptedU: number[] = [], acceptedV: number[] = [];
  const stride = Math.max(1, Math.floor(interior.length / want));
  for (let b = 0; b < want; b++) {
    const slot = interior[(b * stride) % interior.length];
    const ni = roadNodes[slot];
    const nu = builder.nodeUAt(ni), nv = builder.nodeVAt(ni);
    if (isWaterAt(sampler, frame, nu, nv, sc)) continue; // no stub may originate at a wet road node
    // local road tangent from the neighbouring road nodes.
    const pa = roadNodes[slot - 1], pb = roadNodes[slot + 1];
    let tu = builder.nodeUAt(pb) - builder.nodeUAt(pa);
    let tv = builder.nodeVAt(pb) - builder.nodeVAt(pa);
    const tl = pmath.hypot(tu, tv);
    if (tl < 1e-6) continue;
    tu /= tl; tv /= tl;
    const side = h01(hashInts(planetHash, SALT_HAMLET, rk, seed.k, b * 4 + 1)) < 0.5 ? 1 : -1;
    let pu = -tv * side, pv = tu * side;                       // perpendicular, chosen side
    const ang = (h01(hashInts(planetHash, SALT_HAMLET, rk, seed.k, b * 4 + 2)) * 2 - 1) * HAMLET_NOISE_RAD;
    const ca = pmath.cos(ang), sa = pmath.sin(ang);
    const ru = pu * ca - pv * sa, rv = pu * sa + pv * ca;      // rotate by the noise angle
    pu = ru; pv = rv;
    const off = HAMLET_OFFSET_MIN_M + h01(hashInts(planetHash, SALT_HAMLET, rk, seed.k, b * 4 + 3)) * (HAMLET_OFFSET_MAX_M - HAMLET_OFFSET_MIN_M);
    const faceU = nu + pu * off, faceV = nv + pv * off;                                   // stub end (near face)
    const cu = nu + pu * (off + HAMLET_LOT_HALF_M), cv = nv + pv * (off + HAMLET_LOT_HALF_M); // parcel centre
    if (isWaterAt(sampler, frame, faceU, faceV, sc)) continue; // stub may not terminate in water
    const parcel = squareParcel(sampler, cu, cv, HAMLET_LOT_HALF_M, hAt);
    if (parcel === null) continue;                             // wet slot dropped
    // Reject a cottage sitting on a FOREIGN road ribbon (centre + 4 corners).
    if (onForeignRoad(cu, cv)
      || onForeignRoad(parcel.poly[0], parcel.poly[1]) || onForeignRoad(parcel.poly[2], parcel.poly[3])
      || onForeignRoad(parcel.poly[4], parcel.poly[5]) || onForeignRoad(parcel.poly[6], parcel.poly[7])) continue;
    let clash = false;
    for (let a = 0; a < acceptedU.length; a++) {
      if (pmath.hypot(acceptedU[a] - cu, acceptedV[a] - cv) < 2 * HAMLET_LOT_HALF_M + 8) { clash = true; break; }
    }
    if (clash) continue;
    const faceNode = builder.addNode(faceU, faceV, NODE_JUNCTION);
    // Driveway stubs are ALLEY class at creation (v11) — they are added AFTER the
    // hamlet classify pass, so they carry their class/width here.
    const ei = builder.addEdge(ni, faceNode, EDGE_STREET, logCount,
      CLASS_ALLEY, classWidth(CLASS_ALLEY, TIER_HAMLET, planetHash, rk));
    builder.log(LOG_EXTEND, ei, 0);
    parcel.epoch = logCount;
    logCount++;
    const pIdx = builder.addParcel(parcel);
    builder.log(LOG_PARCEL, pIdx, 0);
    acceptedU.push(cu); acceptedV.push(cv);
  }
}

// ── per-tier footprint + calibration targets (part 2) ────────────────────────
//
// The space-colonization skeleton grower + densify that once lived here are RETIRED
// (W2d-2 Task 3) — the era'd tensor-field secondary grower under the planarity
// invariant (streetgrow.ts) replaces them for VILLAGE+ cells. What SURVIVES here is
// the per-tier FOOTPRINT radius + the surviving-building calibration target, which
// emitParcels / assignUses / the anchor + secondary growers all still read.

/**
 * A tier's binding FOOTPRINT radius + the surviving-building calibration target.
 * The SC-era skeleton fields (attractors/stepCap/stepM/influenceM/killM) are gone —
 * the secondary grower's knobs live in streetgrow.ts's SECONDARY_PARAMS (spec
 * §"Core growth parameters"). `radiusM` is the binding tier footprint; parcels/
 * anchors/eras all scale by it.
 */
export interface TierParams {
  /** Binding tier footprint radius (m). */
  radiusM: number;
  /** Inclusive [min,max] surviving-building count target for this tier (spec). */
  targetBuildings: [number, number];
}

/**
 * Per-TIER footprint radius + building targets — index by SettlementSeed.tier
 * (0..5). WAYSTATION (0) is the road-free huddle (emitWaystationCluster); HAMLET
 * (1) is the one-lane growHamlet path; VILLAGE+ (2..5) grow era'd tensor secondaries
 * (streetgrow.ts). Building counts are calibrated by SECONDARY_PARAMS budgets + the
 * parcel knobs; the `targetBuildings` spec ranges are verbatim (tierparams oracle).
 */
// 2026-07-30 "bigger buildings" user direction: settlements trade COUNT for
// SIZE — lower bounds reduced (village 18→12, town 45→34, city 120→90,
// metro 300→220) so the bigger-lot calibration has honest headroom. Upper
// bounds unchanged (capParcels' trim ceiling). This supersedes the original
// spec row values; the tierparams oracle asserts THESE.
export const TIER_PARAMS: readonly TierParams[] = [
  { radiusM: 30,   targetBuildings: [3, 6] },     // 0 waystation — huddle
  { radiusM: 230,  targetBuildings: [8, 15] },    // 1 hamlet — one lane
  { radiusM: 245,  targetBuildings: [12, 35] },   // 2 village
  { radiusM: 560,  targetBuildings: [34, 90] },   // 3 town
  { radiusM: 820,  targetBuildings: [90, 250] },  // 4 city
  // 5 metropolis — a single ~2 km cell cannot hold the spec's 8–12 km sprawl (that
  // is multi-cell W2c-2); the building count is REPORTED, not hard-asserted.
  { radiusM: 1050, targetBuildings: [220, 600] },
];

const NODE_GRID_M = 20;   // spatial-hash cell size (spec: 20 m grid) — NodeHash/emitParcels

// ── node spatial hash (20 m grid) ────────────────────────────────────────────

/**
 * Uniform-grid spatial index over builder node indices, so {@link emitParcels}'s
 * "does this candidate lot contain any network node?" query scans O(local bucket)
 * instead of O(all nodes). The query is order-independent: buckets are scanned over
 * a fixed (gx,gy) rectangle and the result set (see {@link NodeHash.within}) is by
 * distance only and explicitly sorted, so it matches a linear scan regardless of
 * Map insertion order.
 */
class NodeHash {
  private buckets = new Map<string, number[]>();
  constructor(private readonly builder: CityGraphBuilder) {}
  private key(u: number, v: number): string {
    return Math.floor(u / NODE_GRID_M) + "," + Math.floor(v / NODE_GRID_M);
  }
  add(i: number): void {
    const k = this.key(this.builder.nodeUAt(i), this.builder.nodeVAt(i));
    let b = this.buckets.get(k);
    if (b === undefined) { b = []; this.buckets.set(k, b); }
    b.push(i);
  }
  /**
   * ALL node indices within `r` meters of (u,v), sorted ascending. Bucket
   * enumeration order cannot leak: the result set is order-independent
   * (membership by distance only) and explicitly sorted before returning.
   */
  within(u: number, v: number, r: number): number[] {
    const gx0 = Math.floor((u - r) / NODE_GRID_M);
    const gx1 = Math.floor((u + r) / NODE_GRID_M);
    const gy0 = Math.floor((v - r) / NODE_GRID_M);
    const gy1 = Math.floor((v + r) / NODE_GRID_M);
    const out: number[] = [];
    for (let gx = gx0; gx <= gx1; gx++) {
      for (let gy = gy0; gy <= gy1; gy++) {
        const b = this.buckets.get(gx + "," + gy);
        if (b === undefined) continue;
        for (let j = 0; j < b.length; j++) {
          const idx = b[j];
          if (pmath.hypot(this.builder.nodeUAt(idx) - u, this.builder.nodeVAt(idx) - v) <= r) out.push(idx);
        }
      }
    }
    out.sort((x, y) => x - y);
    return out;
  }
}


// ── edge width classes (W2d-3, spec §"Street widths") ────────────────────────
//
// Every edge carries a WIDTH CLASS + a RESOLVED width (CityGraph v11). The class
// is assigned by {@link classifyEdges} right after the growth stages (edges made
// LATER — alley cuts, driveway stubs, waystation paths — pass their class at
// addEdge time and classify leaves them alone); the width is the spec table value
// for (class × settlement tier), with the spec era multipliers on city/metro
// secondaries. Downstream (carve, parcel road-exclusion, renderer ribbons) reads
// ONE resolved number per edge — ROAD_W becomes the kind-keyed FALLBACK for
// graphs built outside the pipeline (unit tests driving emitParcels directly).

/**
 * Spec width table (metres): [min,max] per CLASS row × tier 0..5 column. The
 * spec's columns 3 Village / 4 Town / 5 City / 6 Metropolis are OUR tiers 2..5;
 * waystation(0)/hamlet(1) have no spec column — documented decisions: a hamlet
 * THROUGH-ROAD carves at 5 m (between village primary 6 and secondary 4),
 * waystation/hamlet surface links are ALLEY class at the smallest alley width.
 * Ranged entries (city/metro primaries) resolve by ONE hash-roll per
 * (cell, class, tier) — SALT_CLASS_W.
 */
export const CLASS_WIDTH_TABLE: readonly (readonly (readonly [number, number])[])[] = [
  [[5, 5], [5, 5], [6, 6], [8, 8], [10, 12], [14, 18]],                   // CLASS_PRIMARY
  [[4, 4], [4, 4], [4, 4], [5, 5], [6, 6], [7, 7]],                       // CLASS_SECONDARY
  [[2.5, 2.5], [2.5, 2.5], [2.5, 2.5], [3, 3], [3.5, 3.5], [4, 4]],       // CLASS_TERTIARY
  [[1.5, 1.5], [1.5, 1.5], [1.5, 1.5], [2, 2], [2, 2], [2.5, 2.5]],       // CLASS_ALLEY
];
/** Ranged class-width roll (hash.ts registry). */
const SALT_CLASS_W = 0x77d1;
/** Era width multipliers (spec: old-core ×0.75 / outer ×1.15, city+metro secondaries). */
export const ERA_W_OLD_CORE = 0.75;
export const ERA_W_OUTER = 1.15;

/** Resolve a class × tier width; ranged entries roll once per (cell, class, tier). */
export function classWidth(cls: number, tier: number, planetHash: number, cellKey: number): number {
  const [lo, hi] = CLASS_WIDTH_TABLE[cls][tier];
  if (hi <= lo) return lo;
  return lo + h01(hashInts(planetHash, SALT_CLASS_W, cellKey, cls, tier)) * (hi - lo);
}

/**
 * Assign width classes + resolved widths to every still-UNSET edge (v11).
 * Runs AFTER the growth stages and BEFORE parcels (block-lot setbacks read the
 * widths), mutating the builder's class/width columns in place. Rules:
 *   - ARTERIAL/BRIDGE → PRIMARY at the CELL's max settlement tier (the trunk
 *     network is the biggest settlement's infrastructure; a hamlet cell's
 *     through-road resolves to the 5 m hamlet column).
 *   - STAIR/TERRACE/TUNNEL → TERTIARY (spec: steep connectors are the
 *     tertiary/alley classes on grades) at the nearest seed's tier.
 *   - STREET → nearest-seed tier; a seed of tier ≤ HAMLET owns ALLEY-class
 *     surface links (driveway stubs / waystation paths grown into a shared
 *     cell); otherwise a degree-1-endpoint LEAF edge is a TERTIARY lane and
 *     everything else is SECONDARY. City/metro SECONDARIES take the spec era
 *     multiplier by edge-epoch percentile among the cell's STREET edges
 *     (< ⅓ → ×0.75 old core, > ⅔ → ×1.15 outer; "eras = epochs").
 * Edges classified at creation (CLASS != UNSET) are left untouched.
 * Deterministic: per-edge rules + one registered ranged-width salt.
 */
export function classifyEdges(
  builder: CityGraphBuilder,
  seeds: readonly { u: number; v: number; tier: number }[],
  planetHash: number,
  cellKey: number,
): void {
  const ec = builder.edgeCount;
  if (ec === 0) return;
  let maxTier = TIER_VILLAGE;
  for (const s of seeds) if (s.tier > maxTier) maxTier = s.tier;

  // Node degrees (leaf test) over the current builder edges.
  const deg = new Int32Array(builder.nodeCount);
  for (let e = 0; e < ec; e++) { deg[builder.edgeAAt(e)]++; deg[builder.edgeBAt(e)]++; }

  // Street-epoch percentile rank (era multiplier). Sorted copy + rank by epoch.
  const streetEpochs: number[] = [];
  for (let e = 0; e < ec; e++) if (builder.edgeKindAt(e) === EDGE_STREET) streetEpochs.push(builder.edgeEpochAt(e));
  streetEpochs.sort((a, b) => a - b);
  const epochPct = (ep: number): number => {
    if (streetEpochs.length < 2) return 0.5;
    let lo = 0, hi = streetEpochs.length;
    while (lo < hi) { const m = (lo + hi) >> 1; if (streetEpochs[m] < ep) lo = m + 1; else hi = m; }
    return lo / (streetEpochs.length - 1);
  };

  const seedTierAt = (mu: number, mv: number): number => {
    let best = TIER_VILLAGE, bestD = Infinity;
    for (const s of seeds) {
      const d = (s.u - mu) * (s.u - mu) + (s.v - mv) * (s.v - mv);
      if (d < bestD) { bestD = d; best = s.tier; }
    }
    return best;
  };

  for (let e = 0; e < ec; e++) {
    if (builder.edgeClassAt(e) !== CLASS_UNSET) continue; // creation-time class wins
    const kind = builder.edgeKindAt(e);
    const a = builder.edgeAAt(e), b = builder.edgeBAt(e);
    const mu = (builder.nodeUAt(a) + builder.nodeUAt(b)) / 2;
    const mv = (builder.nodeVAt(a) + builder.nodeVAt(b)) / 2;
    let cls: number;
    let tier: number;
    if (kind === EDGE_ARTERIAL || kind === EDGE_BRIDGE) {
      cls = CLASS_PRIMARY;
      tier = maxTier;
    } else if (kind === EDGE_STAIR || kind === EDGE_TERRACE || kind === EDGE_TUNNEL) {
      cls = CLASS_TERTIARY;
      tier = seedTierAt(mu, mv);
    } else {
      tier = seedTierAt(mu, mv);
      if (tier <= TIER_HAMLET) cls = CLASS_ALLEY;
      else cls = deg[a] === 1 || deg[b] === 1 ? CLASS_TERTIARY : CLASS_SECONDARY;
    }
    let w = classWidth(cls, tier, planetHash, cellKey);
    if (cls === CLASS_SECONDARY && tier >= TIER_CITY) {
      const p = epochPct(builder.edgeEpochAt(e));
      if (p < 1 / 3) w *= ERA_W_OLD_CORE;
      else if (p > 2 / 3) w *= ERA_W_OUTER;
    }
    builder.setEdgeClass(e, cls, w);
  }
}

/**
 * Demote degree-1 SECONDARY street stubs to TERTIARY lanes (v11). Runs AFTER
 * pruneUtility: at classify time the graph is dead-end-pruned, so leaf lanes
 * only EXIST once utility pruning has cut the far streets back to their served
 * stubs. NARROW-ONLY by construction (a lane's width never grows), so every
 * parcel laid against the wider secondary band stays clear of the road ribbon.
 */
function demoteLeafLanes(
  builder: CityGraphBuilder,
  seeds: readonly { u: number; v: number; tier: number }[],
  planetHash: number,
  cellKey: number,
): void {
  const ec = builder.edgeCount;
  const deg = new Int32Array(builder.nodeCount);
  for (let e = 0; e < ec; e++) { deg[builder.edgeAAt(e)]++; deg[builder.edgeBAt(e)]++; }
  for (let e = 0; e < ec; e++) {
    if (builder.edgeKindAt(e) !== EDGE_STREET || builder.edgeClassAt(e) !== CLASS_SECONDARY) continue;
    const a = builder.edgeAAt(e), b = builder.edgeBAt(e);
    if (deg[a] !== 1 && deg[b] !== 1) continue;
    // W2d-4: a promoted MAIN STREET (width above the secondary table row) keeps
    // its width even at a temporary dead end — never demote the high street.
    if (builder.edgeWidthAt(e) > CLASS_WIDTH_TABLE[CLASS_SECONDARY][5][1] + 0.1) continue;
    const mu = (builder.nodeUAt(a) + builder.nodeUAt(b)) / 2;
    const mv = (builder.nodeVAt(a) + builder.nodeVAt(b)) / 2;
    let tier = TIER_VILLAGE, bestD = Infinity;
    for (const s of seeds) {
      const d = (s.u - mu) * (s.u - mu) + (s.v - mv) * (s.v - mv);
      if (d < bestD) { bestD = d; tier = s.tier; }
    }
    const w = Math.min(classWidth(CLASS_TERTIARY, tier, planetHash, cellKey), builder.edgeWidthAt(e));
    builder.setEdgeClass(e, CLASS_TERTIARY, w);
  }
}

// ── W2d-4: desire-line / promotion pass (spec §"Desire-line / promotion pass") ──
//
// The street network learns where its traffic goes. Edge BETWEENNESS + sampled
// high-traffic shortest paths (traffic.ts) rank the pre-parcel graph; then:
//   - FOOTPATH promotion: an ALLEY/TERTIARY edge crossed by ≥ the tier threshold
//     of sampled desire paths promotes ONE class (alley→tertiary,
//     tertiary→secondary) and takes its new class width. Never demoted, never
//     past secondary.
//   - MAIN STREETS: the top-K SECONDARY runs by betweenness (grown greedily
//     through aligned continuations) take width = tier PRIMARY width × the spec
//     multiplier — the commercial high street. Class stays SECONDARY (the spec's
//     "main street" row is a WIDTH row); demoteLeafLanes skips them by width.
// Runs AFTER classifyEdges and BEFORE the lot stage, so lot setbacks see the
// widened mains. Plaza spawn (same spec table) is the separate plaza stage —
// it consumes the betweenness this pass returns.

/** One tier's promotion knobs (spec table verbatim, our tiers 2..5; waystation/
 *  hamlet rows are defensive — cells below VILLAGE never run the pass). */
export interface PromoteParams {
  /** High-traffic pairs sampled. */ pairs: number;
  /** Desire-path crossings at which a footpath promotes. */ footpathThreshold: number;
  /** Main-street count [min,max] (metro rolls in-range). */ mainStreets: [number, number];
  /** Plaza spawn probability at the top-betweenness node (plaza stage). */ plazaProb: number;
  /** Main-street width as a multiple of the tier PRIMARY width. */ mainWidthMult: number;
}
export const PROMOTE_PARAMS: readonly PromoteParams[] = [
  { pairs: 3,  footpathThreshold: 2, mainStreets: [1, 1], plazaProb: 0,   mainWidthMult: 1.0 },  // 0 → village row
  { pairs: 3,  footpathThreshold: 2, mainStreets: [1, 1], plazaProb: 0,   mainWidthMult: 1.0 },  // 1 → village row
  { pairs: 3,  footpathThreshold: 2, mainStreets: [1, 1], plazaProb: 0,   mainWidthMult: 1.0 },  // 2 village
  { pairs: 8,  footpathThreshold: 3, mainStreets: [1, 1], plazaProb: 0.5, mainWidthMult: 1.25 }, // 3 town
  { pairs: 20, footpathThreshold: 3, mainStreets: [2, 2], plazaProb: 1,   mainWidthMult: 1.5 },  // 4 city
  { pairs: 40, footpathThreshold: 4, mainStreets: [3, 4], plazaProb: 1,   mainWidthMult: 1.5 },  // 5 metropolis
];
/** Metro main-street count roll (hash.ts registry). */
const SALT_MAINK = 0x3a9d;
/** A main-street run stops extending past a turn sharper than this (rad ≈ 55°). */
const MAIN_RUN_MAX_TURN = 0.96;
/** Main-street run length cap (edges) — a high street, not a ring road. */
const MAIN_RUN_MAX_EDGES = 40;

/** What the promotion pass hands the plaza stage. */
export interface PromotionResult {
  /** Per-edge betweenness of the promoted graph (plaza ranking reuses it). */
  eb: Float64Array;
  /** Edges widened to main-street width. */
  mainEdges: number[];
  /** Footpath edges promoted a class. */
  promotedFootpaths: number[];
}

/**
 * Rank the builder's street graph and apply the spec promotions (mutating the
 * v11 class/width columns in place). VILLAGE+ cells only; ranks with the
 * PRIMARY settlement's tier (documented multi-seed simplification).
 */
export function promoteStreets(
  builder: CityGraphBuilder,
  seeds: readonly { u: number; v: number; k: number; tier: number }[],
  planetHash: number,
  cellKey: number,
): PromotionResult {
  const ec = builder.edgeCount;
  const nc = builder.nodeCount;
  const empty: PromotionResult = { eb: new Float64Array(ec), mainEdges: [], promotedFootpaths: [] };
  let prim = seeds.length ? seeds[0] : null;
  for (const s of seeds) if (prim !== null && (s.tier > prim.tier || (s.tier === prim.tier && s.k < prim.k))) prim = s;
  if (prim === null || prim.tier < TIER_VILLAGE || ec === 0) return empty;
  const P = PROMOTE_PARAMS[prim.tier];

  // Graph view over the builder (traffic.ts takes ArrayLikes).
  const nodeU = new Float64Array(nc), nodeV = new Float64Array(nc), nodeKind = new Uint8Array(nc);
  for (let i = 0; i < nc; i++) { nodeU[i] = builder.nodeUAt(i); nodeV[i] = builder.nodeVAt(i); nodeKind[i] = builder.nodeKindAt(i); }
  const edgeA = new Uint32Array(ec), edgeB = new Uint32Array(ec);
  for (let e = 0; e < ec; e++) { edgeA[e] = builder.edgeAAt(e); edgeB[e] = builder.edgeBAt(e); }
  const view = { nodeU, nodeV, edgeA, edgeB };
  const eligible = (e: number): boolean => builder.edgeKindAt(e) !== EDGE_TUNNEL;

  const eb = edgeBetweenness(view, eligible);
  const ends = trafficEndpoints(view, nodeKind, NODE_SEED, NODE_PORT, 8, eligible);
  const pairs = samplePairs(planetHash, cellKey, prim.k, ends, P.pairs);
  const crossings = pathCrossings(view, pairs, eligible);

  // ── footpath promotion ──────────────────────────────────────────────────────
  const promotedFootpaths: number[] = [];
  const seedTierAt = (mu: number, mv: number): number => {
    let best = TIER_VILLAGE, bestD = Infinity;
    for (const s of seeds) {
      const d = (s.u - mu) * (s.u - mu) + (s.v - mv) * (s.v - mv);
      if (d < bestD) { bestD = d; best = s.tier; }
    }
    return best;
  };
  for (let e = 0; e < ec; e++) {
    const cls = builder.edgeClassAt(e);
    if (cls !== CLASS_ALLEY && cls !== CLASS_TERTIARY) continue;
    if (crossings[e] < P.footpathThreshold) continue;
    const a = builder.edgeAAt(e), b = builder.edgeBAt(e);
    const tier = seedTierAt((builder.nodeUAt(a) + builder.nodeUAt(b)) / 2, (builder.nodeVAt(a) + builder.nodeVAt(b)) / 2);
    const nCls = cls === CLASS_ALLEY ? CLASS_TERTIARY : CLASS_SECONDARY;
    builder.setEdgeClass(e, nCls, classWidth(nCls, tier, planetHash, cellKey));
    promotedFootpaths.push(e);
  }

  // ── main streets: top-K secondary runs by betweenness ──────────────────────
  const kRange = P.mainStreets;
  const K = kRange[1] > kRange[0]
    ? kRange[0] + Math.floor(h01(hashInts(planetHash, SALT_MAINK, cellKey, prim.k)) * (kRange[1] - kRange[0] + 1))
    : kRange[0];
  const mainW = classWidth(CLASS_PRIMARY, prim.tier, planetHash, cellKey) * P.mainWidthMult;
  // Adjacency over SECONDARY edges only (post-footpath-promotion classes).
  const inc: number[][] = Array.from({ length: nc }, () => []);
  for (let e = 0; e < ec; e++) {
    if (builder.edgeClassAt(e) !== CLASS_SECONDARY) continue;
    inc[builder.edgeAAt(e)].push(e);
    inc[builder.edgeBAt(e)].push(e);
  }
  const heading = (e: number, from: number): [number, number] => {
    const a = builder.edgeAAt(e), b = builder.edgeBAt(e);
    const to = a === from ? b : a;
    const du = builder.nodeUAt(to) - builder.nodeUAt(from);
    const dv = builder.nodeVAt(to) - builder.nodeVAt(from);
    const l = pmath.hypot(du, dv) || 1;
    return [du / l, dv / l];
  };
  const rank: number[] = [];
  for (let e = 0; e < ec; e++) if (builder.edgeClassAt(e) === CLASS_SECONDARY) rank.push(e);
  rank.sort((a, b) => (eb[b] - eb[a]) || (a - b));
  const claimed = new Uint8Array(ec);
  const mainEdges: number[] = [];
  const cosMax = pmath.cos(MAIN_RUN_MAX_TURN);
  for (let k = 0, ri = 0; k < K && ri < rank.length; k++) {
    while (ri < rank.length && claimed[rank[ri]]) ri++;
    if (ri >= rank.length) break;
    const seed = rank[ri];
    const run = [seed];
    claimed[seed] = 1;
    // Grow both ends greedily along the best-betweenness ALIGNED continuation.
    for (const dir of [0, 1]) {
      let node = dir === 0 ? builder.edgeAAt(seed) : builder.edgeBAt(seed);
      let cur = seed;
      while (run.length < MAIN_RUN_MAX_EDGES) {
        const far = builder.edgeAAt(cur) === node ? builder.edgeBAt(cur) : builder.edgeAAt(cur);
        const inH = heading(cur, far); // heading INTO node along cur
        let best = -1, bestEb = -1;
        for (const e of inc[node]) {
          if (claimed[e] || e === cur) continue;
          const outH = heading(e, node);
          if (inH[0] * outH[0] + inH[1] * outH[1] < cosMax) continue;
          if (eb[e] > bestEb + 1e-12 || (Math.abs(eb[e] - bestEb) <= 1e-12 && (best < 0 || e < best))) { bestEb = eb[e]; best = e; }
        }
        if (best < 0) break;
        claimed[best] = 1;
        run.push(best);
        node = builder.edgeAAt(best) === node ? builder.edgeBAt(best) : builder.edgeAAt(best);
        cur = best;
      }
    }
    for (const e of run) {
      builder.setEdgeClass(e, CLASS_SECONDARY, mainW);
      mainEdges.push(e);
    }
  }
  return { eb, mainEdges, promotedFootpaths };
}

// ── W2d-4: PLAZAS at top-betweenness junctions (spec promotion table row 4) ────

/** Plaza octagon radius by tier (m). Village never spawns one (prob 0). */
const PLAZA_RADIUS: readonly number[] = [0, 0, 0, 9, 12, 14];
/** Plaza spawn roll (hash.ts registry). */
const SALT_PLAZA = 0x62a7;
/** Metro core/outer band split (fraction of the tier radius) for per-district plazas. */
const PLAZA_BAND_FRAC = 0.4;

/** What the plaza stage returns: parcel polys (lot/strip obstacles) + centres
 *  (the plaza-first placement anchors). */
export interface PlazaResult {
  polys: Float64Array[];
  centres: { u: number; v: number }[];
}

/**
 * Emit open PLAZA parcels at the settlement's top-betweenness junction(s):
 * town 50% (hash roll), city always, metro per district (approximated as the
 * old-core and outer bands — documented). The plaza is an octagon parcel with
 * USE_MARKET_SQ (open — structures stamps no box; the terrain paint paves it),
 * carved flat like any parcel platform. Junction nodes/edges deliberately sit
 * INSIDE it. Wet or steep sites skip (the junction keeps its ordinary look).
 */
export function emitPlazas(
  sampler: PlanetSampler,
  frame: RegionFrame,
  builder: CityGraphBuilder,
  seeds: readonly { u: number; v: number; k: number; tier: number }[],
  promotion: PromotionResult,
  planetHash: number,
  cellKey: number,
  landmark: { u: number; v: number } | null = null,
): PlazaResult {
  const out: PlazaResult = { polys: [], centres: [] };
  let prim = seeds.length ? seeds[0] : null;
  for (const s of seeds) if (prim !== null && (s.tier > prim.tier || (s.tier === prim.tier && s.k < prim.k))) prim = s;
  if (prim === null || prim.tier < TIER_TOWN) return out;
  const P = PROMOTE_PARAMS[prim.tier];
  if (P.plazaProb <= 0) return out;
  if (P.plazaProb < 1 && h01(hashInts(planetHash, SALT_PLAZA, cellKey, prim.k)) >= P.plazaProb) return out;

  // Node scores = Σ incident edge betweenness (road edges), junctions only.
  const nc = builder.nodeCount;
  const score = new Float64Array(nc);
  const deg = new Int32Array(nc);
  for (let e = 0; e < builder.edgeCount; e++) {
    if (builder.edgeKindAt(e) === EDGE_TUNNEL) continue;
    const a = builder.edgeAAt(e), b = builder.edgeBAt(e);
    score[a] += promotion.eb[e];
    score[b] += promotion.eb[e];
    deg[a]++; deg[b]++;
  }
  const radius = PLAZA_RADIUS[prim.tier];
  const bandR = TIER_PARAMS[prim.tier].radiusM * PLAZA_BAND_FRAC;
  // Metro: one plaza per band (core, outer); town/city: one overall.
  const bands: ((d: number) => boolean)[] = prim.tier >= 5
    ? [(d) => d < bandR, (d) => d >= bandR]
    : [() => true];

  const dir = new Float64Array(3);
  const samp: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
  const hAt = (u: number, v: number): number => {
    localToDir(frame, u, v, dir);
    sampler.sample(dir[0], dir[1], dir[2], samp, false);
    return samp.h;
  };
  const amp = sampler.amplitude;

  for (const inBand of bands) {
    // Top-scored junction in this band (deg ≥ 3, not a port, near the seed reach).
    let best = -1, bestScore = 0;
    for (let i = 0; i < nc; i++) {
      if (deg[i] < 3 || builder.nodeKindAt(i) === NODE_PORT) continue;
      const d = pmath.hypot(builder.nodeUAt(i) - prim.u, builder.nodeVAt(i) - prim.v);
      if (!inBand(d) || d > TIER_PARAMS[prim.tier].radiusM * PARCEL_SEED_RADIUS_MULT) continue;
      if (score[i] > bestScore + 1e-9 || (Math.abs(score[i] - bestScore) <= 1e-9 && best >= 0 && i < best)) {
        bestScore = score[i];
        best = i;
      }
    }
    // R3 landmark-first: among near-top junctions (score ≥ 85% of best), prefer
    // the one NEAREST the landmark — cathedral-on-the-square.
    if (best >= 0 && landmark !== null) {
      let bestD = pmath.hypot(builder.nodeUAt(best) - landmark.u, builder.nodeVAt(best) - landmark.v);
      for (let i = 0; i < nc; i++) {
        if (i === best || deg[i] < 3 || builder.nodeKindAt(i) === NODE_PORT) continue;
        if (score[i] < bestScore * 0.85) continue;
        const dSeed = pmath.hypot(builder.nodeUAt(i) - prim.u, builder.nodeVAt(i) - prim.v);
        if (!inBand(dSeed) || dSeed > TIER_PARAMS[prim.tier].radiusM * PARCEL_SEED_RADIUS_MULT) continue;
        const dl = pmath.hypot(builder.nodeUAt(i) - landmark.u, builder.nodeVAt(i) - landmark.v);
        if (dl < bestD - 1e-9) { bestD = dl; best = i; }
      }
    }
    if (best < 0) continue;
    const cu = builder.nodeUAt(best), cv = builder.nodeVAt(best);
    // Octagon, CCW; water/grade discipline like every parcel.
    const poly = new Float64Array(16);
    const hs: number[] = [];
    let wet = false;
    for (let s = 0; s < 8; s++) {
      const a = (pmath.TWO_PI * s) / 8;
      poly[2 * s] = cu + pmath.cos(a) * radius;
      poly[2 * s + 1] = cv + pmath.sin(a) * radius;
      const h = hAt(poly[2 * s], poly[2 * s + 1]);
      hs.push(h);
      if (sampler.seaRel !== 0 && h <= sampler.seaRel) wet = true;
    }
    if (wet) continue;
    let maxG = 0;
    for (let s = 0; s < 8; s++) {
      const s2 = (s + 1) & 7;
      const run = pmath.hypot(poly[2 * s2] - poly[2 * s], poly[2 * s2 + 1] - poly[2 * s + 1]);
      const gr = run < 1e-9 ? 0 : (Math.abs(hs[s2] - hs[s]) * amp) / run;
      if (gr > maxG) maxG = gr;
    }
    if (maxG > PARCEL_MAX_GRADE) continue;
    let level = 0;
    for (const h of hs) level += h;
    level /= 8;
    const pIdx = builder.addParcel({ poly, use: USE_MARKET_SQ, epoch: 0, level, retaining: maxG > RETAIN_GRADE });
    builder.log(LOG_PARCEL, pIdx, Math.fround(maxG));
    out.polys.push(poly);
    out.centres.push({ u: cu, v: cv });
  }
  return out;
}

// ── block subdivision: LOTS + ALLEYS (W2d-3 Task 2, spec §"Block subdivision") ──
//
// City BLOCKS are the interior planar faces of the street graph (blocks.ts);
// each qualifying face is (optionally) cut by an ALLEY chord between two of its
// boundary nodes, then recursively subdivided into building LOTS (lots.ts) that
// REPLACE the strip parcels along every street side that fronts a lotted face.
// The strip emitter survives ONLY as the fallback for street sides facing the
// outer face or a rejected face (dead-end lanes keep their houses).
//
// DETERMINISM. Faces are visited in trace order (first-encountered half-edge,
// ascending); every roll is a keyed hashInts draw: the alley/oversize chord roll
// on (cellKey, faceId, subIdx) [SALT_BLOCK] and the cut-point jitter on
// (cellKey, faceId, subIdx, path) [SALT_LOT], where `path` encodes the position
// in the recursion tree — the lot set is a pure function of the street graph.

/** A tier's block-subdivision knobs (spec §"Block subdivision", verbatim rows
 *  for tiers 2..5; waystation/hamlet reuse the village row defensively — they
 *  never reach the block path). `maxLotArea` is OURS (calibration knob: the
 *  split-termination area), not spec. Metro's dual min lot area is era-banded:
 *  `minLotArea` in the old core (< 0.4·radius), `minLotAreaOuter` beyond. */
export interface BlockParams {
  blockAreaMin: number; blockAreaMax: number;
  minLotArea: number; minLotAreaOuter: number;
  maxLotArea: number;
  aspectMax: number;
  alleyProb: number;
  skewFrac: number;
}
// 2026-07-28 interiors scale-up: lot areas ×~1.1 (village/town/metro; city
// keeps its braid-fix calibration untouched). The 4 m module alone already
// buys interior-worthy rooms (headroom is a MODULE property, not a lot one);
// lot growth is a mild bonus, hard-bounded by the tierparams building-count
// oracle — a ×1.5 first cut collapsed dense tiers (city 167 → 38 buildings:
// fewer lots → utility streets pruned → fewer strips — a cascade). Block
// gates (blockAreaMin/Max) are untouched — blocks come from the street graph,
// which this release deliberately does not re-tune.
// 2026-07-30 "bigger buildings" user pass: lot areas up another ~15% (the
// count trade is deliberate — TIER_PARAMS lower bounds were reduced with it).
// 2026-08-02 "bases too small" pass: maxLotArea +~20% across tiers (a CAUTIOUS
// step — the recorded ×1.5 attempt collapsed dense tiers; pair with the
// FOOTPRINT_FRAC 0.97 / cap 38 raise in structures.ts, which is the
// zero-cascade half of the change).
export const BLOCK_PARAMS: readonly BlockParams[] = [
  { blockAreaMin: 1500, blockAreaMax: 3000, minLotArea: 140, minLotAreaOuter: 140, maxLotArea: 540, aspectMax: 3,   alleyProb: 0.2,  skewFrac: 0.25 }, // 0 → village row
  { blockAreaMin: 1500, blockAreaMax: 3000, minLotArea: 140, minLotAreaOuter: 140, maxLotArea: 540, aspectMax: 3,   alleyProb: 0.2,  skewFrac: 0.25 }, // 1 → village row
  { blockAreaMin: 1500, blockAreaMax: 3000, minLotArea: 140, minLotAreaOuter: 140, maxLotArea: 540, aspectMax: 3,   alleyProb: 0.2,  skewFrac: 0.25 }, // 2 village
  { blockAreaMin: 1200, blockAreaMax: 2500, minLotArea: 115, minLotAreaOuter: 115, maxLotArea: 515, aspectMax: 3,   alleyProb: 0.35, skewFrac: 0.20 }, // 3 town
  // city maxLotArea 260 → 345 (R3 braid-fix recalibration: merged trunks densified
  // the street net; the braid budget became real blocks and the city overshot to
  // 306 — bigger lots bring it back inside the band).
  { blockAreaMin: 900,  blockAreaMax: 2000, minLotArea: 92,  minLotAreaOuter: 92,  maxLotArea: 720, aspectMax: 3.5, alleyProb: 0.5,  skewFrac: 0.15 }, // 4 city
  // metro stays at 265: the +20% pass dropped its cluster count below the
  // [300,600] spec band (288) — the dense tier has no slack.
  { blockAreaMin: 700,  blockAreaMax: 1800, minLotArea: 70,  minLotAreaOuter: 175, maxLotArea: 265, aspectMax: 4,   alleyProb: 0.6,  skewFrac: 0.12 }, // 5 metropolis
];
/** Alley-chord roll (hash.ts registry). */
const SALT_BLOCK = 0x6b0c;
/** Lot cut-point jitter (hash.ts registry). */
const SALT_LOT = 0x107a;
/** Lot frontage setback off an ALLEY chord (streets use PARCEL_SETBACK_M). */
const ALLEY_SETBACK_M = 1.5;
/** A lot must share at least this much boundary with a street/alley frontage. */
const FRONTAGE_MIN_M = 3;
/** Minimum useful alley chord length (m). */
const ALLEY_MIN_LEN_M = 8;
/** Oversized faces force chord splits down to this many recursion levels. */
const CHORD_DEPTH_CAP = 2;

/** What the block stage hands the strip fallback. */
export interface BlockLotResult {
  /** Half-edge keys (2e + side) of street sides fronting a LOTTED face. */
  lottedSides: Set<number>;
  /** Accepted lot polygons (seed the strip emitter's overlap rejection). */
  lotPolys: Float64Array[];
}

/**
 * Detect blocks, cut alleys, subdivide lots, and append the accepted lots as
 * parcels (with the strips' water/grade/node/road disciplines). Mutates the
 * builder (alley edges + parcels + logs); returns the lotted-side set + lot
 * polys the strip fallback consumes. VILLAGE+ cells only.
 */
export function emitBlockLots(
  sampler: PlanetSampler,
  frame: RegionFrame,
  builder: CityGraphBuilder,
  seeds: { u: number; v: number; k: number; tier: number }[],
  obstacles: Float64Array[] = [], // pre-existing open parcels (plazas) lots must clear
): BlockLotResult {
  const planetHash = hashSeed(sampler.def.seed);
  const cell = builder.regionId;
  const cellKey = packCell(cell);
  const amp = sampler.amplitude;
  const out: BlockLotResult = { lottedSides: new Set(), lotPolys: [] };

  const g = builder.build(); // classified snapshot (edgeClass/edgeWidth present)
  if (g.edgeA.length === 0) return out;
  const trace = traceFaces(g);

  // Height/water probes (climate off) — the emitParcels discipline.
  const dir = new Float64Array(3);
  const samp: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
  const heightAt = (u: number, v: number): number => {
    localToDir(frame, u, v, dir);
    sampler.sample(dir[0], dir[1], dir[2], samp, false);
    return samp.h;
  };
  const wetAt = (u: number, v: number): boolean => sampler.seaRel !== 0 && heightAt(u, v) <= sampler.seaRel;

  // Node hash for the contains-a-node rejection (same as strips).
  const nodeHash = new NodeHash(builder);
  for (let i = 0; i < g.nodeU.length; i++) nodeHash.add(i);

  // FOREIGN-road rejection (W2d-4 fix): a lot clips only against its OWN face
  // boundary, so at a junction a corner lot can poke into the OTHER arm's ribbon
  // — invisible at ribbon-era widths, exposed when main streets widened. Reject
  // any lot whose corner enters ANY road band; own-face edges cannot trip this
  // (the setback clip keeps corners ≥ half + PARCEL_SETBACK_M off them).
  const fAu: number[] = [], fAv: number[] = [], fBu: number[] = [], fBv: number[] = [], fBand: number[] = [];
  for (let e = 0; e < g.edgeKind.length; e++) {
    if (g.edgeKind[e] === EDGE_TUNNEL) continue;
    const a = g.edgeA[e], b = g.edgeB[e];
    fAu.push(g.nodeU[a]); fAv.push(g.nodeV[a]); fBu.push(g.nodeU[b]); fBv.push(g.nodeV[b]);
    const half = g.edgeWidth[e] > 0 ? g.edgeWidth[e] * 0.5 : ROAD_HALF_BY_KIND[g.edgeKind[e]];
    fBand.push(half + ROAD_EXCLUDE_M);
  }
  const cornerOnRoad = (lot: number[]): boolean => {
    for (let e = 0; e < fAu.length; e++) {
      for (let m = 0; m < lot.length; m += 2) {
        if (pointSegDist(lot[m], lot[m + 1], fAu[e], fAv[e], fBu[e], fBv[e]) <= fBand[e]) return true;
      }
    }
    return false;
  };

  // VILLAGE+ seeds own blocks (F11 near-seed discipline).
  const owners = seeds.filter((s) => s.tier >= TIER_VILLAGE);
  if (owners.length === 0) return out;
  const ownerOf = (cu: number, cv: number): { u: number; v: number; k: number; tier: number } | null => {
    let best: { u: number; v: number; k: number; tier: number } | null = null;
    let bestD = Infinity;
    for (const s of owners) {
      const d = (s.u - cu) * (s.u - cu) + (s.v - cv) * (s.v - cv);
      if (d < bestD) { bestD = d; best = s; }
    }
    if (best === null) return null;
    const reach = PARCEL_SEED_RADIUS_MULT * TIER_PARAMS[best.tier].radiusM;
    return bestD <= reach * reach ? best : null;
  };

  // One sub-block of a face: CCW verts + per-vertex graph node id (−1 = cut
  // vertex, never a chord endpoint) + per-SEGMENT frontage metadata (segment k
  // runs vert k → k+1): the setback offset and the epoch it fronts.
  interface SubBlock {
    verts: number[];        // [u,v, …] CCW
    nodes: number[];        // node id per vert
    segOff: number[];       // setback offset per segment
    segEpoch: number[];     // epoch per segment
  }

  const c: [number, number] = [0, 0];
  for (let fi = 0; fi < trace.faces.length; fi++) {
    const face = trace.faces[fi];
    if (face.nodes.length < 3) continue;
    const area = face.area2 * 0.5;
    if (!(area > 1e-6)) continue;

    faceCentroid(g, face, c);
    const owner = ownerOf(c[0], c[1]);
    if (owner === null) continue;
    const P = BLOCK_PARAMS[owner.tier];
    const culture = cultureFor(planetHash, cellKey, owner.k);
    const blockMax = P.blockAreaMax * culture.blockAreaScale;
    if (area < P.minLotArea || area > blockMax * 4) continue; // sliver / rural void
    if (wetAt(c[0], c[1])) continue;
    const skew = P.skewFrac + (culture.lotSkew - P.skewFrac) * 0.5; // 50% culture blend

    // Root sub-block from the face cycle (dedupe the possible repeated cut
    // vertices of an out-and-back twig walk by keeping ALL — geometry handles it).
    const root: SubBlock = { verts: [], nodes: [], segOff: [], segEpoch: [] };
    for (let i = 0; i < face.nodes.length; i++) {
      const ni = face.nodes[i];
      root.verts.push(g.nodeU[ni], g.nodeV[ni]);
      root.nodes.push(ni);
      const e = face.halfEdges[i] >> 1;
      root.segOff.push(g.edgeWidth[e] * 0.5 + PARCEL_SETBACK_M);
      root.segEpoch.push(g.edgeEpoch[e]);
    }

    const alleyW = classWidth(CLASS_ALLEY, owner.tier, planetHash, cellKey);
    let anyLot = false;
    let subIdx = 0;

    // Chord pass (alley roll at depth 0, forced while oversized), then lots.
    const handle = (sb: SubBlock, depth: number): void => {
      const myIdx = subIdx++;
      const sbArea = Math.abs(polygonArea(sb.verts));
      const wantChord =
        depth < CHORD_DEPTH_CAP &&
        (sbArea > blockMax ||
          (depth === 0 && sbArea >= P.blockAreaMin * culture.blockAreaScale &&
            h01(hashInts(planetHash, SALT_BLOCK, cellKey, fi, myIdx)) < P.alleyProb));
      if (wantChord) {
        const cut = bestChord(sb);
        if (cut !== null) {
          // Insert the alley edge (planar: the chord is interior to the face).
          const ep = builder.logCount;
          const ei = builder.addEdge(sb.nodes[cut.i], sb.nodes[cut.j], EDGE_STREET, ep, CLASS_ALLEY, alleyW);
          builder.log(LOG_EXTEND, ei, 0);
          const off = alleyW * 0.5 + ALLEY_SETBACK_M;
          const [ha, hb] = splitSubBlock(sb, cut.i, cut.j, off, ep);
          handle(ha, depth + 1);
          handle(hb, depth + 1);
          return;
        }
      }
      emitLotsFor(sb, myIdx);
    };

    // Best alley chord: non-adjacent NODE pair, strictly interior, longest-
    // balanced split (maximize min half-area), ties by (i, j) ascending.
    const bestChord = (sb: SubBlock): { i: number; j: number } | null => {
      const n = sb.nodes.length;
      let bi = -1, bj = -1, bScore = 0;
      for (let i = 0; i < n; i++) {
        if (sb.nodes[i] < 0) continue;
        for (let j = i + 2; j < n; j++) {
          if (sb.nodes[j] < 0) continue;
          if (i === 0 && j === n - 1) continue; // cyclically adjacent
          if (sb.nodes[i] === sb.nodes[j]) continue; // twig repeat — no self-chord
          const ax = sb.verts[2 * i], ay = sb.verts[2 * i + 1];
          const bx = sb.verts[2 * j], by = sb.verts[2 * j + 1];
          if (pmath.hypot(bx - ax, by - ay) < ALLEY_MIN_LEN_M) continue;
          if (!pointInPolygon(sb.verts, (ax + bx) / 2, (ay + by) / 2)) continue;
          if (chordCrossesBoundary(sb.verts, ax, ay, bx, by, i, j)) continue;
          // Half areas by cycle split.
          let a1 = 0;
          for (let k = i; k !== j; k = (k + 1) % n) {
            const k2 = (k + 1) % n;
            a1 += sb.verts[2 * k] * sb.verts[2 * k2 + 1] - sb.verts[2 * k2] * sb.verts[2 * k + 1];
          }
          a1 = Math.abs((a1 + (bx * ay - ax * by)) * 0.5);
          const a2 = Math.abs(polygonArea(sb.verts)) - a1;
          const score = Math.min(a1, a2);
          if (score > bScore + 1e-9 && score >= P.minLotArea * 2) { bScore = score; bi = i; bj = j; }
        }
      }
      return bi >= 0 ? { i: bi, j: bj } : null;
    };

    // Split a sub-block cycle at vertex indices i<j into the two CCW halves;
    // the chord segment carries the alley setback + epoch.
    const splitSubBlock = (sb: SubBlock, i: number, j: number, chordOff: number, chordEp: number): [SubBlock, SubBlock] => {
      const n = sb.nodes.length;
      const mk = (from: number, to: number): SubBlock => {
        const o: SubBlock = { verts: [], nodes: [], segOff: [], segEpoch: [] };
        for (let k = from; ; k = (k + 1) % n) {
          o.verts.push(sb.verts[2 * k], sb.verts[2 * k + 1]);
          o.nodes.push(sb.nodes[k]);
          if (k === to) break;
          o.segOff.push(sb.segOff[k]);
          o.segEpoch.push(sb.segEpoch[k]);
        }
        o.segOff.push(chordOff); // closing segment to → from = the chord
        o.segEpoch.push(chordEp);
        return o;
      };
      return [mk(i, j), mk(j, i)];
    };

    // Subdivide one sub-block into lots and append the survivors as parcels.
    const emitLotsFor = (sb: SubBlock, myIdx: number): void => {
      const nSeg = sb.nodes.length;
      // Frontage segments, flat [ax,ay,bx,by,…] + per-seg offset/epoch.
      const segs = new Float64Array(4 * nSeg);
      for (let k = 0; k < nSeg; k++) {
        const k2 = (k + 1) % nSeg;
        segs[4 * k] = sb.verts[2 * k]; segs[4 * k + 1] = sb.verts[2 * k + 1];
        segs[4 * k + 2] = sb.verts[2 * k2]; segs[4 * k + 3] = sb.verts[2 * k2 + 1];
      }
      // Metro dual min lot area: era-banded by the sub-block centroid.
      polygonCentroid(sb.verts, c);
      const dSeed = pmath.hypot(c[0] - owner.u, c[1] - owner.v);
      const minLot = dSeed < 0.4 * TIER_PARAMS[owner.tier].radiusM ? P.minLotArea : P.minLotAreaOuter;
      const lots = subdividePolygon(sb.verts.slice(), {
        maxLotArea: P.maxLotArea,
        minLotArea: minLot,
        aspectMax: P.aspectMax,
        skew,
        draw: (path: number) => h01(hashInts(planetHash, SALT_LOT, cellKey, fi, myIdx, path)),
      });
      for (const raw of lots) {
        // Frontage FIRST (pre-setback: pieces tile the face, so a fronting
        // piece touches the boundary exactly).
        if (sharedBoundaryLength(raw, segs, 1.0) < FRONTAGE_MIN_M) continue;
        // Setback clip against every NEARBY frontage segment (interior is the
        // LEFT of each CCW boundary segment).
        let lot = raw;
        for (let k = 0; k < nSeg && lot.length >= 6; k++) {
          const ax = segs[4 * k], ay = segs[4 * k + 1];
          const bx = segs[4 * k + 2], by = segs[4 * k + 3];
          const dx = bx - ax, dy = by - ay;
          const l = pmath.hypot(dx, dy);
          if (l < 1e-9) continue;
          const off = sb.segOff[k];
          // Quick reach test: any lot vertex within off+2 of the segment line band?
          let near = false;
          for (let m = 0; m < lot.length && !near; m += 2) {
            const t = ((lot[m] - ax) * dx + (lot[m + 1] - ay) * dy) / (l * l);
            const tt = t < 0 ? 0 : t > 1 ? 1 : t;
            near = pmath.hypot(lot[m] - (ax + dx * tt), lot[m + 1] - (ay + dy * tt)) < off + 2;
          }
          if (!near) continue;
          lot = clipHalfPlane(lot, ax - (dy / l) * off, ay + (dx / l) * off, -dy / l, dx / l);
        }
        if (lot.length < 8) continue; // triangles/slivers: no downstream consumer
        if (Math.abs(polygonArea(lot)) < minLot * 0.55) continue; // setback ate it
        // Contains-a-node rejection (REQFILL/waystation squares come later).
        polygonCentroid(lot, c);
        let circR = 0;
        for (let m = 0; m < lot.length; m += 2) {
          const d = pmath.hypot(lot[m] - c[0], lot[m + 1] - c[1]);
          if (d > circR) circR = d;
        }
        // Carve single-bucket invariant: a lot's influence (circumradius + blend)
        // must stay ≤ CARVE_MAX_M or SettlementIndex fail-louds. Degenerate
        // depth-capped slivers can exceed it — reject them here instead.
        if (circR + PARCEL_BLEND_M > CARVE_MAX_M - 0.5) continue;
        // Junction-corner guard (W2d-4): no corner in ANY road band.
        if (cornerOnRoad(lot)) continue;
        // Plaza guard (W2d-4): lots never overlap an open plaza (convex SAT).
        {
          let hit = false;
          const lotArr = Float64Array.from(lot);
          for (const ob of obstacles) {
            if (quadsOverlap(lotArr, ob)) { hit = true; break; }
          }
          if (hit) continue;
        }
        const near = nodeHash.within(c[0], c[1], circR + 1e-6);
        let rejected = false;
        for (let ni = 0; ni < near.length && !rejected; ni++) {
          if (pointInPolygon(lot, g.nodeU[near[ni]], g.nodeV[near[ni]])) rejected = true;
        }
        if (rejected) continue;
        // Water + grade discipline over the lot corners (emitParcels rules).
        const nc = lot.length / 2;
        const hs: number[] = [];
        let wet = false;
        for (let m = 0; m < nc; m++) {
          const h = heightAt(lot[2 * m], lot[2 * m + 1]);
          hs.push(h);
          if (sampler.seaRel !== 0 && h <= sampler.seaRel) { wet = true; break; }
        }
        if (wet) continue;
        let maxG = 0;
        for (let m = 0; m < nc; m++) {
          const m2 = (m + 1) % nc;
          const run = pmath.hypot(lot[2 * m2] - lot[2 * m], lot[2 * m2 + 1] - lot[2 * m + 1]);
          const gr = run < 1e-9 ? 0 : (Math.abs(hs[m2] - hs[m]) * amp) / run;
          if (gr > maxG) maxG = gr;
        }
        if (maxG > PARCEL_MAX_GRADE) continue;
        // Epoch = the nearest frontage segment's epoch.
        let ep = sb.segEpoch[0], bestD = Infinity;
        for (let k = 0; k < nSeg; k++) {
          const dx = segs[4 * k + 2] - segs[4 * k], dy = segs[4 * k + 3] - segs[4 * k + 1];
          const l2 = dx * dx + dy * dy;
          let t = l2 > 1e-12 ? ((c[0] - segs[4 * k]) * dx + (c[1] - segs[4 * k + 1]) * dy) / l2 : 0;
          t = t < 0 ? 0 : t > 1 ? 1 : t;
          const d = pmath.hypot(c[0] - (segs[4 * k] + dx * t), c[1] - (segs[4 * k + 1] + dy * t));
          if (d < bestD) { bestD = d; ep = sb.segEpoch[k]; }
        }
        let level = 0;
        for (const h of hs) level += h;
        level /= nc;
        const frozen = Float64Array.from(lot);
        const pIdx = builder.addParcel({ poly: frozen, use: 0, epoch: ep, level, retaining: maxG > RETAIN_GRADE });
        builder.log(LOG_PARCEL, pIdx, Math.fround(maxG));
        out.lotPolys.push(frozen);
        anyLot = true;
      }
    };

    handle(root, 0);
    if (anyLot) for (const he of face.halfEdges) out.lottedSides.add(he);
  }
  return out;
}

// ── parcel subdivision (part 3) ───────────────────────────────────────────────
//
// densify() is RETIRED (W2d-2 Task 3) — the era grower's planarity invariant makes
// street loops implicitly (a segment that meets an edge junctions there), so there
// is no separate hop-gated loop-closure pass (the hop gate self-propagated ladders,
// diagnosis §H2). emitParcels is UNCHANGED: it fronts the (now era-grown) STREET
// edges with strip lots — the interim strip parcels stand until the W2d-3 block/lot
// subdivision replaces them.
//
// DETERMINISM. emitParcels walks edges ascending, sides front (+) then back (−),
// strips in span order, drawing widths from ONE per-region mulberry32 stream (salt
// 0x9a2c) in that fixed order. pmath only; no Set/Map iteration-order dependence.

// ── constants (binding) ──────────────────────────────────────────────────────

/** Lot depth away from the street (meters). Deliberately UNCHANGED by the
 *  2026-07-28 interiors scale-up: a 16 m strip already yields a 3-cell-deep
 *  footprint at the 4 m module, and a 20 m trial made facing strips overlap
 *  back-to-back across close city streets (SAT rejection collapsed city
 *  building counts 167 → ~80 via the parcel→street-prune cascade). */
export const PARCEL_DEPTH_M = 16;
/** Lot width bounds (meters) for the 0x9a2c jitter stream. W2d-3 narrowed the
 *  max 22 → 15: strip frontage packs denser (the "towns feel sparse" lever on
 *  the fallback sides), matching the block lots' typical frontage. Unchanged
 *  by the interiors scale-up (see PARCEL_DEPTH_M note). */
export const PARCEL_W_MIN = 9;
export const PARCEL_W_MAX = 15;
/** Reject a lot when any corner-pair grade exceeds this. */
export const PARCEL_MAX_GRADE = 0.6;
/** Above this max corner-pair grade the pad needs a retaining wall. */
export const RETAIN_GRADE = 0.25;
/**
 * W2b-2.6 (F11): dwellings cluster at settlements. A lot is only emitted along an
 * edge whose distance to SOME seed is within this multiple of that seed's tier
 * radius (`TIER_PARAMS[tier].radiusM`). i.e. PARCEL_SEED_RADIUS_M =
 * PARCEL_SEED_RADIUS_MULT × radiusM. The roadside-hamlet emergence (parcels on
 * far-flung arterial junctions) is thereby killed — the user rejected it.
 */
export const PARCEL_SEED_RADIUS_MULT = 1.5;
/**
 * W2b-2.7 (hamlet hotfix, F-hotfix-1): a STREET/STAIR edge is UTILITY — it exists
 * to serve dwellings. After parcels are laid, {@link pruneUtility} deletes every
 * STREET/STAIR edge whose distance to the NEAREST parcel centroid exceeds this
 * (chart point-to-segment metres); ARTERIAL/BRIDGE/TUNNEL/TERRACE are exempt.
 * A parcel centroid sits PARCEL_SETBACK_M + PARCEL_DEPTH_M/2 = 11 m off its
 * fronting edge, well inside this radius, so every fronting edge survives by
 * construction (asserted by the utility oracle). Result: streets only where
 * dwellings are — the user's "2 houses drowning in roads" defect.
 */
export const PARCEL_SERVICE_M = 40;
/**
 * Foreign-road exclusion margin (metres) beyond a road ribbon's half-width. A
 * candidate lot is rejected when any corner OR its centroid lies within
 * `ROAD_W[kind]/2 + ROAD_EXCLUDE_M` of ANY non-TUNNEL edge OTHER than the lot's
 * own fronting edge — the "houses on roads" fix (W2b-2.5). The fronting edge is
 * excepted because the lot is deliberately set back from it by PARCEL_SETBACK_M.
 */
export const ROAD_EXCLUDE_M = 1.5;

/**
 * Road ribbon HALF-width (metres) indexed by EDGE_* kind — the exclusion band's
 * base (mirrors carveindex's HALF_W / the renderer's ROAD_WIDTH_BY_KIND). TUNNEL
 * (index 3) is never an exclusion source (it runs beneath the surface).
 */
const ROAD_HALF_BY_KIND = [
  ROAD_W.arterial * 0.5,     // EDGE_ARTERIAL (0)
  ROAD_W.street * 0.5,       // EDGE_STREET   (1)
  ROAD_W.stair * 0.5,        // EDGE_STAIR    (2)
  ROAD_W.tunnel * 0.5,       // EDGE_TUNNEL   (3) — excluded source
  ROAD_W.terracePath * 0.5,  // EDGE_TERRACE  (4)
  ROAD_W.bridge * 0.5,       // EDGE_BRIDGE   (5)
];

/** Perpendicular distance from (px,py) to segment a-b (region-local metres). */
function pointSegDist(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax, dy = by - ay;
  const l2 = dx * dx + dy * dy;
  let t = l2 > 0 ? ((px - ax) * dx + (py - ay) * dy) / l2 : 0;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  return pmath.hypot(px - (ax + dx * t), py - (ay + dy * t));
}

/** Parcel width jitter stream (hash.ts registry). */
const SALT_PARCEL_W = 0x9a2c;
/** Street-to-parcel setback: lots span ⟂ offsets [3, 3 + PARCEL_DEPTH_M]. */
const PARCEL_SETBACK_M = 3;
/** AABB-grid cell for edge/parcel any-hit rejection queries (meters). */
const SEG_GRID_M = 64;

// ── planar geometry predicates ───────────────────────────────────────────────

/** Twice the signed area of triangle (a,b,p) — the orientation predicate. */
function orient2(ax: number, ay: number, bx: number, by: number, px: number, py: number): number {
  return (bx - ax) * (py - ay) - (by - ay) * (px - ax);
}

/** A separating axis exists among P's edge normals (exact touching separates).
 *  Generalized to n-gons for the W2d-3 block lots (exact for convex; a mildly
 *  concave lot can only FALSE-NEGATIVE, i.e. miss an overlap — acceptable for
 *  the strip fallback, whose sides never face a lotted block). */
function sepAxis(P: Float64Array, Q: Float64Array): boolean {
  const nP = P.length / 2;
  const nQ = Q.length / 2;
  for (let k = 0; k < nP; k++) {
    const k2 = (k + 1) % nP;
    const ax = -(P[2 * k2 + 1] - P[2 * k + 1]);
    const ay = P[2 * k2] - P[2 * k];
    let minP = Infinity, maxP = -Infinity, minQ = Infinity, maxQ = -Infinity;
    for (let m = 0; m < nP; m++) {
      const dp = ax * P[2 * m] + ay * P[2 * m + 1];
      if (dp < minP) minP = dp;
      if (dp > maxP) maxP = dp;
    }
    for (let m = 0; m < nQ; m++) {
      const dq = ax * Q[2 * m] + ay * Q[2 * m + 1];
      if (dq < minQ) minQ = dq;
      if (dq > maxQ) maxQ = dq;
    }
    if (maxP <= minQ || maxQ <= minP) return true;
  }
  return false;
}

/**
 * Exact convex-polygon overlap via SAT over both polygons' edge normals.
 * Boundary touching does NOT count as overlap (`<=` separation), so lots
 * abutting along a shared strip boundary — bitwise-identical corner floats by
 * construction — coexist, while any interior overlap is rejected.
 */
function quadsOverlap(P: Float64Array, Q: Float64Array): boolean {
  return !sepAxis(P, Q) && !sepAxis(Q, P);
}

/** Point inside-or-on-boundary of a CCW quad (boundary counts: conservative reject). */
function pointInQuad(q: Float64Array, px: number, py: number): boolean {
  for (let k = 0; k < 4; k++) {
    const k2 = (k + 1) & 3;
    if (orient2(q[2 * k], q[2 * k + 1], q[2 * k2], q[2 * k2 + 1], px, py) < 0) return false;
  }
  return true;
}


// ── parcels ──────────────────────────────────────────────────────────────────

/**
 * Subdivide building lots along STREET/STAIR edges (no lots on arterials,
 * bridges, tunnels, or terraces in W2a). Both sides of each edge are offset by
 * PARCEL_SETBACK_M and filled with PARCEL_DEPTH_M-deep strips whose widths come
 * from the per-region 0x9a2c stream; a candidate lot is rejected when any
 * corner-pair grade exceeds PARCEL_MAX_GRADE, it overlaps an accepted lot
 * (exact SAT), or it contains any network node. Accepted lots carry
 * `epoch` = the fronting edge's epoch, `level` = mean corner height (amplitude
 * units), `retaining` = max corner-pair grade > RETAIN_GRADE, `use` = 0
 * (dwelling), and log LOG_PARCEL (ref = parcel index, slope = max grade).
 */
export function emitParcels(
  sampler: PlanetSampler,
  frame: RegionFrame,
  builder: CityGraphBuilder,
  seeds: { u: number; v: number; tier: number }[],
  blockOut?: BlockLotResult,
): void {
  const planetHash = hashSeed(sampler.def.seed);
  const amp = sampler.amplitude;
  const cell = builder.regionId;
  // W2b-2.6 (F11): an edge only bears lots when it lies within some seed's
  // PARCEL_SEED_RADIUS_M (1.5 × that seed's tier radius) — chart point-to-segment
  // distance. No seeds ⇒ no lots at all (a seedless cell never reaches here in the
  // orchestrator, but the guard keeps the function total).
  const edgeNearSeed = (au: number, av: number, bu: number, bv: number): boolean => {
    for (let s = 0; s < seeds.length; s++) {
      const r = PARCEL_SEED_RADIUS_MULT * TIER_PARAMS[seeds[s].tier].radiusM;
      if (pointSegDist(seeds[s].u, seeds[s].v, au, av, bu, bv) <= r) return true;
    }
    return false;
  };
  const g = builder.build(); // read-only snapshot: nodes + edges (frozen API)
  const rng = rngFromCell(planetHash, LEVEL_REGION, cell.face, cell.x, cell.y, SALT_PARCEL_W);

  const dir = new Float64Array(3);
  const samp: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
  const heightAt = (u: number, v: number): number => {
    localToDir(frame, u, v, dir);
    sampler.sample(dir[0], dir[1], dir[2], samp, false);
    return samp.h;
  };

  const hash = new NodeHash(builder);
  for (let i = 0; i < g.nodeU.length; i++) hash.add(i);

  // Road-exclusion grid (W2b-2.5): every non-TUNNEL edge, its own band folded in,
  // so a candidate lot corner/centroid falling on a FOREIGN road ribbon is
  // rejected via a single-bucket lookup. Each edge is inserted into every bucket
  // its (AABB expanded by its band) touches, so a point within `band` of the edge
  // always shares that point's bucket — the any-hit query cannot miss it.
  const edgeBand: number[] = new Array(g.edgeKind.length);
  const exGrid = new Map<string, number[]>();
  for (let e = 0; e < g.edgeKind.length; e++) {
    const kind = g.edgeKind[e];
    if (kind === EDGE_TUNNEL) { edgeBand[e] = 0; continue; }
    // v11: the exclusion band follows the edge's RESOLVED width; the kind-keyed
    // half-width is the fallback for graphs built outside the classify pipeline.
    const half = g.edgeWidth[e] > 0 ? g.edgeWidth[e] * 0.5 : ROAD_HALF_BY_KIND[kind];
    const band = half + ROAD_EXCLUDE_M;
    edgeBand[e] = band;
    const a = g.edgeA[e], b = g.edgeB[e];
    const au = g.nodeU[a], av = g.nodeV[a], bu = g.nodeU[b], bv = g.nodeV[b];
    const gx0 = Math.floor((Math.min(au, bu) - band) / SEG_GRID_M);
    const gx1 = Math.floor((Math.max(au, bu) + band) / SEG_GRID_M);
    const gy0 = Math.floor((Math.min(av, bv) - band) / SEG_GRID_M);
    const gy1 = Math.floor((Math.max(av, bv) + band) / SEG_GRID_M);
    for (let gx = gx0; gx <= gx1; gx++) {
      for (let gy = gy0; gy <= gy1; gy++) {
        const key = gx + "," + gy;
        let bkt = exGrid.get(key);
        if (bkt === undefined) { bkt = []; exGrid.set(key, bkt); }
        bkt.push(e);
      }
    }
  }
  // True iff (pu,pv) lies within a foreign edge's exclusion band. `frontEdge` (the
  // lot's own fronting edge) is skipped — the setback keeps the lot just off it by
  // design. Any-hit, so bucket enumeration order cannot leak into the result.
  const onForeignRoad = (pu: number, pv: number, frontEdge: number): boolean => {
    const bkt = exGrid.get(Math.floor(pu / SEG_GRID_M) + "," + Math.floor(pv / SEG_GRID_M));
    if (bkt === undefined) return false;
    for (let bi = 0; bi < bkt.length; bi++) {
      const fe = bkt[bi];
      if (fe === frontEdge) continue;
      const a = g.edgeA[fe], b = g.edgeB[fe];
      if (pointSegDist(pu, pv, g.nodeU[a], g.nodeV[a], g.nodeU[b], g.nodeV[b]) <= edgeBand[fe]) return true;
    }
    return false;
  };

  // Accepted lot polys + an AABB grid over parcel indices (any-hit rejection).
  // W2d-3: block lots are SEEDED as obstacles so a fallback strip can never
  // overlap a lot (corner wrap-arounds — lotted faces themselves are skipped).
  const quads: Float64Array[] = [];
  const pgrid = new Map<string, number[]>();
  const seedObstacle = (poly: Float64Array): void => {
    let minU = poly[0], maxU = poly[0], minV = poly[1], maxV = poly[1];
    for (let k = 1; k < poly.length / 2; k++) {
      minU = Math.min(minU, poly[2 * k]); maxU = Math.max(maxU, poly[2 * k]);
      minV = Math.min(minV, poly[2 * k + 1]); maxV = Math.max(maxV, poly[2 * k + 1]);
    }
    const qi = quads.length;
    quads.push(poly);
    for (let gx = Math.floor(minU / SEG_GRID_M); gx <= Math.floor(maxU / SEG_GRID_M); gx++) {
      for (let gy = Math.floor(minV / SEG_GRID_M); gy <= Math.floor(maxV / SEG_GRID_M); gy++) {
        const key = gx + "," + gy;
        let bkt = pgrid.get(key);
        if (bkt === undefined) { bkt = []; pgrid.set(key, bkt); }
        bkt.push(qi);
      }
    }
  };
  if (blockOut !== undefined) for (const poly of blockOut.lotPolys) seedObstacle(poly);

  const poly = new Float64Array(8); // scratch quad; copied on accept
  const hC = new Float64Array(4);   // corner heights (amplitude units)

  for (let e = 0; e < g.edgeKind.length; e++) {
    const kind = g.edgeKind[e];
    if (kind !== EDGE_STREET && kind !== EDGE_STAIR) continue;
    // W2d-3: alleys are lot infrastructure — block lots front them; strips never do.
    if (g.edgeClass[e] === CLASS_ALLEY) continue;
    // v11 WIDTH-AWARE setback: the historical fixed 3 m offset is measured from
    // the edge CENTERLINE, so any street wider than 6 m (city secondary 6,
    // era-widened metro secondary 8.05) would put strip corners INSIDE its own
    // ribbon — which the foreign-road rejection deliberately exempts, and the
    // collider would fold solid (a building blocking its own street). Keep the
    // historical 0.5 m clearance off the ribbon edge instead.
    const ownHalf = g.edgeWidth[e] > 0 ? g.edgeWidth[e] * 0.5 : ROAD_HALF_BY_KIND[kind];
    const offNear = Math.max(PARCEL_SETBACK_M, ownHalf + 0.5);
    const offFar = offNear + PARCEL_DEPTH_M;
    const a = g.edgeA[e], b = g.edgeB[e];
    const au = g.nodeU[a], av = g.nodeV[a];
    const bu = g.nodeU[b], bv = g.nodeV[b];
    const L = pmath.hypot(bu - au, bv - av);
    if (L < 1e-9) continue;
    if (!edgeNearSeed(au, av, bu, bv)) continue; // F11: lots only near a settlement
    const du = (bu - au) / L, dv = (bv - av) / L;
    const pu = -dv, pv = du; // edge dir rotated +90° (CCW in the chart)

    for (let side = 0; side < 2; side++) {
      // W2d-3: a side fronting a LOTTED block face is served by its lots.
      if (blockOut !== undefined && blockOut.lottedSides.has(2 * e + side)) continue;
      const s = side === 0 ? 1 : -1;
      let t = 0;
      for (;;) {
        const w = PARCEL_W_MIN + rng() * (PARCEL_W_MAX - PARCEL_W_MIN);
        if (t + w > L) break; // leftover span too narrow for this strip
        const t0 = t, t1 = t + w;
        t = t1;

        // Corner order flips with the side so the winding stays CCW. Shared
        // strip corners are bitwise-identical floats (same t, same expression),
        // which the SAT touching rule relies on.
        if (s > 0) {
          poly[0] = au + du * t0 + pu * offNear; poly[1] = av + dv * t0 + pv * offNear;
          poly[2] = au + du * t1 + pu * offNear; poly[3] = av + dv * t1 + pv * offNear;
          poly[4] = au + du * t1 + pu * offFar;  poly[5] = av + dv * t1 + pv * offFar;
          poly[6] = au + du * t0 + pu * offFar;  poly[7] = av + dv * t0 + pv * offFar;
        } else {
          poly[0] = au + du * t1 - pu * offNear; poly[1] = av + dv * t1 - pv * offNear;
          poly[2] = au + du * t0 - pu * offNear; poly[3] = av + dv * t0 - pv * offNear;
          poly[4] = au + du * t0 - pu * offFar;  poly[5] = av + dv * t0 - pv * offFar;
          poly[6] = au + du * t1 - pu * offFar;  poly[7] = av + dv * t1 - pv * offFar;
        }

        // Road-exclusion (W2b-2.5): reject a lot whose ANY corner or centroid sits
        // on a FOREIGN road ribbon (its own fronting edge `e` excepted). Cheapest
        // reject, so it runs before the height sampling below.
        {
          const ecu = (poly[0] + poly[2] + poly[4] + poly[6]) / 4;
          const ecv = (poly[1] + poly[3] + poly[5] + poly[7]) / 4;
          if (onForeignRoad(ecu, ecv, e)
            || onForeignRoad(poly[0], poly[1], e) || onForeignRoad(poly[2], poly[3], e)
            || onForeignRoad(poly[4], poly[5], e) || onForeignRoad(poly[6], poly[7], e)) continue;
        }

        // Corner heights (chart → dir → sample, climate off) and the 4
        // corner-pair grades along the quad sides.
        for (let k = 0; k < 4; k++) hC[k] = heightAt(poly[2 * k], poly[2 * k + 1]);
        // Reject a lot touching water: any corner at/below sea level (climate
        // off), reusing the heights just sampled. seaRel === 0 is a no-sea
        // planet (no water), so it never rejects — matching isWaterAt's guard.
        if (sampler.seaRel !== 0) {
          let wet = false;
          for (let k = 0; k < 4; k++) if (hC[k] <= sampler.seaRel) { wet = true; break; }
          if (wet) continue;
        }
        let maxG = 0;
        for (let k = 0; k < 4; k++) {
          const k2 = (k + 1) & 3;
          const run = pmath.hypot(poly[2 * k2] - poly[2 * k], poly[2 * k2 + 1] - poly[2 * k + 1]);
          const gr = run < 1e-9 ? 0 : Math.abs(hC[k2] - hC[k]) * amp / run;
          if (gr > maxG) maxG = gr;
        }
        if (maxG > PARCEL_MAX_GRADE) continue;

        // Contains-a-node rejection: nodes within the circumradius, exact
        // point-in-quad (boundary counts — conservative).
        const cu = (poly[0] + poly[2] + poly[4] + poly[6]) / 4;
        const cv = (poly[1] + poly[3] + poly[5] + poly[7]) / 4;
        const near = hash.within(cu, cv, pmath.hypot(w, PARCEL_DEPTH_M) / 2 + 1e-6);
        let rejected = false;
        for (let ni = 0; ni < near.length; ni++) {
          if (pointInQuad(poly, g.nodeU[near[ni]], g.nodeV[near[ni]])) { rejected = true; break; }
        }
        if (rejected) continue;

        // Overlap rejection vs accepted lots: AABB grid → exact SAT (any-hit).
        let minU = poly[0], maxU = poly[0], minV = poly[1], maxV = poly[1];
        for (let k = 1; k < 4; k++) {
          minU = Math.min(minU, poly[2 * k]); maxU = Math.max(maxU, poly[2 * k]);
          minV = Math.min(minV, poly[2 * k + 1]); maxV = Math.max(maxV, poly[2 * k + 1]);
        }
        const gx0 = Math.floor(minU / SEG_GRID_M), gx1 = Math.floor(maxU / SEG_GRID_M);
        const gy0 = Math.floor(minV / SEG_GRID_M), gy1 = Math.floor(maxV / SEG_GRID_M);
        const seen = new Set<number>();
        for (let gx = gx0; gx <= gx1 && !rejected; gx++) {
          for (let gy = gy0; gy <= gy1 && !rejected; gy++) {
            const bkt = pgrid.get(gx + "," + gy);
            if (bkt === undefined) continue;
            for (let q = 0; q < bkt.length; q++) {
              const pi = bkt[q];
              if (seen.has(pi)) continue;
              seen.add(pi);
              if (quadsOverlap(poly, quads[pi])) { rejected = true; break; }
            }
          }
        }
        if (rejected) continue;

        // Accept: freeze the quad, log LOG_PARCEL, index it for later overlaps.
        const frozen = poly.slice();
        const level = (hC[0] + hC[1] + hC[2] + hC[3]) / 4;
        const pIdx = builder.addParcel({
          poly: frozen, use: 0, epoch: g.edgeEpoch[e], level,
          retaining: maxG > RETAIN_GRADE,
        });
        builder.log(LOG_PARCEL, pIdx, Math.fround(maxG));
        const qi = quads.length; // local index (grid refs `quads`, not the builder)
        quads.push(frozen);
        for (let gx = gx0; gx <= gx1; gx++) {
          for (let gy = gy0; gy <= gy1; gy++) {
            const key = gx + "," + gy;
            let bkt = pgrid.get(key);
            if (bkt === undefined) { bkt = []; pgrid.set(key, bkt); }
            bkt.push(qi);
          }
        }
      }
    }
  }
}

// ── required-building placement (part 3b, W2c-1 Task 2) ───────────────────────
//
// The settlement-tier spec makes each tier's REQUIRED building list its gameplay
// affordance list (inn = rest, garrison = bounties, …) and its promotion contract
// (a village "becomes" a town by satisfying the town list). We realize that as a
// DECLARATIVE table (TIER_REQUIREMENTS) of USE_* codes with exact multiplicities,
// plus a per-tier OPTIONAL_POOLS table rolled sparsely. After parcels exist,
// assignUses stamps the required codes into the parcels NEAREST the seed
// (ascending distance, deterministic), marks MARKET_SQ parcels OPEN (structures
// stamps no building there), rolls optionals over the remaining near parcels, and
// leaves the rest USE_DWELLING. Population is hash-rolled in the tier's spec range;
// tier/population/deviation of the region's PRIMARY settlement land in the header.
//
// PROMOTION-READY: because the requirements are data, a later tier's list is a
// superset check away — nothing here hard-codes tier behaviour beyond the tables.

/**
 * Required building USE_* codes per tier (exact multiplicities), transcribed from
 * the spec's "Required:" lines. USE_PRODUCTION is the abstract biome-production
 * slot (concrete subtype via {@link productionType}); USE_MARKET_SQ is an OPEN
 * square (no building). METROPOLIS = CITY's list + its own extras (spec: "every-
 * thing from City tier, plus …"), so its promotion contract literally contains a
 * city's. Landmarks, district-unique buildings, and the Loom-gate-as-object are
 * W2c-2/3 (recorded, not placed here).
 */
// 2026-08-02 (user call): USE_LOOM_GATE retired from every tier list — the code
// stays in the enum (append-only) but no parcel is assigned it any more; the
// freed slots simply fall back to dwellings.
/** One reserved civic pair per real settlement; district pseudo-seeds reuse
 * their metropolis pair and skip these two uses during assignment. */
const CIVIC_REQUIRED: readonly number[] = [USE_ADMIN, USE_MONUMENT];
const CITY_REQUIRED: readonly number[] = [
  ...CIVIC_REQUIRED, USE_MARKET_HALL, USE_TEMPLE, USE_GARRISON,
  USE_GUILDHALL, USE_GUILDHALL, USE_GUILDHALL, USE_ACADEMY, USE_HARBOR, USE_COURTHOUSE,
];
export const TIER_REQUIREMENTS: readonly (readonly number[])[] = [
  [...CIVIC_REQUIRED, USE_LANDING_PAD, USE_DEPOT, USE_BUNKHOUSE],
  [...CIVIC_REQUIRED, USE_DWELLING, USE_DWELLING, USE_DWELLING, USE_DWELLING, USE_WELL, USE_HALL, USE_PRODUCTION],
  [...CIVIC_REQUIRED, USE_INN, USE_MARKET_SQ, USE_CHAPEL, USE_WORKSHOP, USE_WORKSHOP, USE_WAREHOUSE],
  [...CIVIC_REQUIRED, USE_MARKET_HALL, USE_INN, USE_INN, USE_TEMPLE, USE_GUILDHALL, USE_SMITHY,
   USE_WORKSHOP, USE_WORKSHOP, USE_WAREHOUSE, USE_GARRISON],
  CITY_REQUIRED,
  [...CITY_REQUIRED, USE_LOOM_NEXUS, USE_BASILICA,
   USE_MARKET_SQ, USE_MARKET_SQ, USE_MARKET_SQ, USE_MARKET_SQ, USE_MARKET_SQ,
   USE_EMBASSY, USE_STARPORT, USE_PRECURSOR],
];

/** Optional-pool USE_* codes per tier (rolled sparsely, spec "Optional pool"). */
export const OPTIONAL_POOLS: readonly (readonly number[])[] = [
  [USE_FUEL_SHED, USE_SHRINE, USE_WATCHTOWER],                       // waystation
  [USE_SMITHY, USE_SHRINE, USE_MILL, USE_STABLE],                    // hamlet
  [USE_BATHHOUSE, USE_APOTHECARY, USE_GARRISON, USE_DOCKYARD],       // village
  [USE_THEATER, USE_BATHHOUSE, USE_ARENA, USE_DOCKYARD, USE_ACADEMY, USE_PRISON], // town
  [USE_ARENA, USE_ACADEMY, USE_THEATER, USE_PRISON],                 // city
  [USE_ARENA, USE_ACADEMY],                                          // metropolis (wonders → W3)
];

/** Population spec range [min,max] per tier (metadata; drives W4 roster sizing). */
export const TIER_POP_RANGES: readonly [number, number][] = [
  [5, 15], [25, 60], [80, 200], [300, 800], [1500, 5000], [20000, 45000],
];

/** Chance a remaining near-seed parcel takes an optional-pool use (else dwelling). */
export const OPTIONAL_PROB = 0.18;

// Placement salts (registered in hash.ts).
const SALT_POP = 0x90b1;         // population roll within the tier range
const SALT_OPT = 0x0b7a;         // optional-pool membership + pick
const SALT_WAYCLUSTER = 0x3c15;  // waystation cluster building count

// Biome → production subtype (W2c-1 Task 2). Concrete farm/fishery/mine/lumber
// codes for the abstract USE_PRODUCTION slot; a PURE function of the seed's temp
// (~[-1,1]) and moist ([0,1]) bands so any later dress pass derives the same type
// without extra storage. Bands (documented): moist ≥ 0.75 → FISHERY (well-watered
// lowland/coast); else moist ≥ 0.45 → FARM (arable); else temp < -0.2 → MINE (cold
// dry highland); else → LUMBER (temperate dry). Exported for the mapping oracle.
export const PROD_FARM = 0, PROD_FISHERY = 1, PROD_MINE = 2, PROD_LUMBER = 3;
export function productionType(temp: number, moist: number): number {
  if (moist >= 0.75) return PROD_FISHERY;
  if (moist >= 0.45) return PROD_FARM;
  if (temp < -0.2) return PROD_MINE;
  return PROD_LUMBER;
}

/** Population hash-rolled within tier range (metadata). */
function rollPopulation(tier: number, planetHash: number, rk: number, k: number): number {
  const [lo, hi] = TIER_POP_RANGES[tier];
  return lo + Math.floor(h01(hashInts(planetHash, SALT_POP, rk, k)) * (hi - lo + 1));
}

/** Deterministic present-day census for one dealt settlement seed. */
export function settlementPopulationFor(planetSeed: string, regionId: CellId, tier: number, k: number): number {
  return rollPopulation(tier, hashSeed(planetSeed), packCell(regionId), k);
}

/**
 * A CCW square parcel of side `2*half` centred at chart (cu,cv), rejected (→ null)
 * when any corner or the centre is at/below sea level (climate off; seaRel guard),
 * matching emitParcels' water discipline. `level` = mean corner height (amp units).
 */
function squareParcel(
  sampler: PlanetSampler, cu: number, cv: number, half: number,
  hAt: (u: number, v: number) => number,
): Parcel | null {
  const poly = new Float64Array([cu - half, cv - half, cu + half, cv - half, cu + half, cv + half, cu - half, cv + half]);
  const wet = (u: number, v: number): boolean => sampler.seaRel !== 0 && hAt(u, v) <= sampler.seaRel;
  let sum = 0;
  for (let c = 0; c < 4; c++) {
    const u = poly[2 * c], v = poly[2 * c + 1];
    if (wet(u, v)) return null;
    sum += hAt(u, v);
  }
  if (wet(cu, cv)) return null;
  return { poly, use: USE_DWELLING, epoch: 0, level: sum / 4, retaining: false };
}

/**
 * WAYSTATION placement (spec §"Tiers 1–2"): a single cluster of 3–6 buildings
 * huddled around the seed pad, plus DIRECT building-to-building paths — NO road
 * network (no primary, no SC mesh). Adds the seed NODE_SEED (pad anchor), rolls a
 * 3–6 slot count, rings small square parcels about the seed at
 * TIER_PARAMS[0].radiusM (wet slots dropped — hostile-biome waystations can sit
 * near water/ice), places a node at each PLACED building's centroid, and links
 * consecutive placed buildings with EDGE_STREET paths (the spec's ALLEY class
 * arrives W2d-3). Buildings arise from the parcels in structures.ts; assignUses
 * stamps the landing-pad/depot/bunkhouse (+ optional) codes. Emits ZERO
 * EDGE_ARTERIAL edges (no trunk network).
 */
function emitWaystationCluster(
  sampler: PlanetSampler, cell: CellId, frame: RegionFrame,
  seed: { u: number; v: number; k: number }, builder: CityGraphBuilder,
): void {
  const planetHash = hashSeed(sampler.def.seed);
  const dir = new Float64Array(3);
  const samp: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
  const hAt = (u: number, v: number): number => { localToDir(frame, u, v, dir); sampler.sample(dir[0], dir[1], dir[2], samp, false); return samp.h; };
  builder.addNode(seed.u, seed.v, NODE_SEED);
  const n = 3 + Math.floor(h01(hashInts(planetHash, SALT_WAYCLUSTER, packCell(cell), seed.k)) * 4); // 3..6
  const ring = TIER_PARAMS[TIER_WAYSTATION].radiusM * 0.5; // ~15 m huddle radius
  const half = 5.5; // ~11 m footprint — small so ground spread stays under structures' cap
  const buildNodes: number[] = []; // one node per PLACED building (for the paths)
  for (let i = 0; i < n; i++) {
    const th = pmath.TWO_PI * (i / n);
    const cu = seed.u + ring * pmath.cos(th);
    const cv = seed.v + ring * pmath.sin(th);
    const p = squareParcel(sampler, cu, cv, half, hAt);
    if (p !== null) { builder.addParcel(p); buildNodes.push(builder.addNode(cu, cv, NODE_JUNCTION)); }
  }
  // Direct building-to-building paths (ALLEY class — the spec's footpath tier,
  // carried per-edge since v11; epoch 0, unlogged — no growth/parcel bookkeeping,
  // matching the cluster's log-free style). Consecutive buildings link into an
  // open path.
  const pathW = classWidth(CLASS_ALLEY, TIER_WAYSTATION, planetHash, packCell(cell));
  for (let i = 0; i + 1 < buildNodes.length; i++) {
    builder.addEdge(buildNodes[i], buildNodes[i + 1], EDGE_STREET, 0, CLASS_ALLEY, pathW);
  }
}

/** Fill salt (hash.ts registry). Synthesizes required-building parcels when the
 *  road-derived parcels near a seed fall short of its required-use list. */
const SALT_REQFILL = 0x9e7f;
/** Half-side (m) of a synthesized required-fill parcel — a ~11 m footprint, matching
 *  the waystation huddle so structures builds an ordinary box on it. Exported:
 *  the block-lot oracle exempts exact fill-sized squares from the road-service
 *  invariant — a fill ring at a roadless seed is a DESIGNED huddle, not an
 *  orphaned lot (2026-07-28: bigger lots → fewer spare parcels → fills now fire
 *  at remote seeds instead of claiming parcels in a town 500 m away). */
export const REQFILL_HALF_M = 5.5;
/** Slots per ring before the fill radius steps outward (deterministic spiral). */
const REQFILL_RING_SLOTS = 8;

/**
 * Assign USE_* codes to the graph's parcels from the declarative TIER_REQUIREMENTS
 * / OPTIONAL_POOLS tables, and stamp the region's primary-settlement metadata into
 * the header. Deterministic; mutates g in place.
 *
 * REQUIRED-FIRST, GLOBAL (R1 warehouse-regression fix). Required buildings are the
 * quest API and must NEVER be silently dropped, so placement runs in two GLOBAL
 * passes over the seeds (each sorted tier desc, k asc — a higher tier claims before
 * a co-located lower one):
 *   PASS 1 (required): each seed claims its TIER_REQUIREMENTS uses from the nearest
 *     UNCLAIMED quad parcels searching ALL parcels — NOT reach-bounded, so a
 *     required use never yields to a parcel that merely landed outside the seed's
 *     optional reach. If the network produced FEWER parcels than the required list
 *     needs (e.g. a village whose secondary net grew few streets), the shortfall is
 *     SYNTHESIZED: deterministic square parcels are ringed around the seed (salt
 *     {@link SALT_REQFILL}, water-/overlap-rejected) and appended to g.parcels, so
 *     EVERY required use is realized. Required placement is thus robust to parcel-
 *     count fluctuations by construction.
 *   PASS 2 (optional): each seed rolls its OPTIONAL_POOLS over the remaining
 *     reach-bounded near parcels. Running globally AFTER all required placement
 *     guarantees an optional/dwelling never steals a parcel a required building of
 *     ANY seed needs.
 * MARKET_SQ stays USE_MARKET_SQ (open square — structures stamps no box); everything
 * unclaimed stays USE_DWELLING (the emit default).
 */
function assignUses(
  g: CityGraph, sampler: PlanetSampler,
  seeds: { u: number; v: number; k: number; tier: number; deviation: number }[],
  plazas: { u: number; v: number }[] = [],
  landmark: { u: number; v: number } | null = null,
): void {
  assignCivicSites(g,sampler,seeds);
  const planetHash = hashSeed(sampler.def.seed);
  const rk = packCell(g.regionId);
  // Parcel centroids (chart metres). Grows as required-fill appends synthetic parcels.
  const pcu: number[] = [], pcv: number[] = [];
  const centroid = (poly: Float64Array): [number, number] => {
    const m = poly.length / 2;
    let su = 0, sv = 0;
    for (let c = 0; c < m; c++) { su += poly[2 * c]; sv += poly[2 * c + 1]; }
    return [su / m, sv / m];
  };
  for (let p = 0; p < g.parcels.length; p++) {
    const [cu, cv] = centroid(g.parcels[p].poly);
    pcu.push(cu); pcv.push(cv);
  }
  const claimed: number[] = new Array(g.parcels.length).fill(0);
  // Plaza parcels arrive with USE_MARKET_SQ already set (emitPlazas) — pre-claim
  // them so no required/optional use overwrites the open square.
  for (let p = 0; p < g.parcels.length; p++) if ([USE_MARKET_SQ,USE_ADMIN,USE_MONUMENT].includes(g.parcels[p].use)) claimed[p] = 1;

  // Primary settlement (tier desc, k asc) → header metadata.
  let primary = seeds[0];
  for (const s of seeds) if (s.tier > primary.tier || (s.tier === primary.tier && s.k < primary.k)) primary = s;
  g.tier = primary.tier;
  g.population = settlementPopulationFor(sampler.def.seed, g.regionId, primary.tier, primary.k);
  g.deviation = primary.deviation;

  const order = [...seeds].sort((a, b) => (b.tier - a.tier) || (a.k - b.k));

  // Height sampler over the region chart (climate off), for synthetic-parcel water
  // rejection — matches emitParcels / squareParcel discipline.
  const dir = new Float64Array(3);
  const samp: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
  const hAt = (u: number, v: number): number => { localToDir(g.frame, u, v, dir); sampler.sample(dir[0], dir[1], dir[2], samp, false); return samp.h; };

  // Nearest UNCLAIMED quad parcels to a seed over ALL parcels (dist², then index).
  const nearestUnclaimed = (su: number, sv: number): number[] => {
    const near: number[] = [];
    for (let p = 0; p < g.parcels.length; p++) {
      if (claimed[p] || g.parcels[p].poly.length < 8) continue;
      near.push(p);
    }
    near.sort((a, b) => {
      const da = (pcu[a] - su) * (pcu[a] - su) + (pcv[a] - sv) * (pcv[a] - sv);
      const db = (pcu[b] - su) * (pcu[b] - su) + (pcv[b] - sv) * (pcv[b] - sv);
      return (da - db) || (a - b);
    });
    return near;
  };

  // ROAD-ribbon exclusion for required-fill (R1 T1.6 review). A synthesized parcel
  // must not stand inside a road ribbon or the collider folds it solid → a physical
  // roadblock on a primary road. Snapshot every non-tunnel edge's band once (mirrors
  // the emitParcels / onForeignRoad discipline: ROAD_HALF_BY_KIND[kind]+ROAD_EXCLUDE_M),
  // then reject any fill slot whose FOOTPRINT (centre + 4 corners) enters a band.
  const rau: number[] = [], rav: number[] = [], rbu: number[] = [], rbv: number[] = [], rband: number[] = [];
  for (let e = 0; e < g.edgeKind.length; e++) {
    if (g.edgeKind[e] === EDGE_TUNNEL) continue;
    const a = g.edgeA[e], b = g.edgeB[e];
    rau.push(g.nodeU[a]); rav.push(g.nodeV[a]); rbu.push(g.nodeU[b]); rbv.push(g.nodeV[b]);
    const half = g.edgeWidth[e] > 0 ? g.edgeWidth[e] * 0.5 : ROAD_HALF_BY_KIND[g.edgeKind[e]];
    rband.push(half + ROAD_EXCLUDE_M);
  }
  const onRoad = (px: number, py: number): boolean => {
    for (let e = 0; e < rau.length; e++) if (pointSegDist(px, py, rau[e], rav[e], rbu[e], rbv[e]) <= rband[e]) return true;
    return false;
  };
  const parcelOnRoad = (par: Parcel): boolean => {
    const p = par.poly;
    const cu = (p[0] + p[2] + p[4] + p[6]) / 4, cv = (p[1] + p[3] + p[5] + p[7]) / 4;
    return onRoad(cu, cv) || onRoad(p[0], p[1]) || onRoad(p[2], p[3]) || onRoad(p[4], p[5]) || onRoad(p[6], p[7]);
  };

  // Append a synthetic required-fill parcel near (su,sv) for `use`, spiralling out
  // over deterministic ring slots until a dry, non-overlapping site is found. Returns
  // the new parcel index (its use is set by the caller). Always succeeds within the
  // slot budget on any land cell (the seed itself sits on land by selection).
  const overlapsExisting = (cu: number, cv: number): boolean => {
    for (let p = 0; p < g.parcels.length; p++) {
      if (g.parcels[p].poly.length < 8) continue;
      // 2·half + a small gap: reject centroids within one footprint of each other.
      if (Math.abs(pcu[p] - cu) < 2 * REQFILL_HALF_M + 1 && Math.abs(pcv[p] - cv) < 2 * REQFILL_HALF_M + 1) return true;
    }
    return false;
  };
  const appendFill = (s: { u: number; v: number; k: number; tier: number }, slot: number, use: number): void => {
    const step = 2 * REQFILL_HALF_M + 2;               // ring-to-ring radial step (m)
    for (let slotTry = slot; slotTry < slot + REQFILL_RING_SLOTS * 6; slotTry++) {
      const ring = 1 + Math.floor(slotTry / REQFILL_RING_SLOTS);
      const r = ring * step;
      const a = pmath.TWO_PI * (h01(hashInts(planetHash, SALT_REQFILL, rk, s.k, slotTry)) + (slotTry % REQFILL_RING_SLOTS) / REQFILL_RING_SLOTS);
      const cu = s.u + r * pmath.cos(a);
      const cv = s.v + r * pmath.sin(a);
      const par = squareParcel(sampler, cu, cv, REQFILL_HALF_M, hAt);
      if (par === null || overlapsExisting(cu, cv) || parcelOnRoad(par)) continue;
      par.use = use;
      g.parcels.push(par);
      pcu.push(cu); pcv.push(cv);
      claimed.push(1);
      return;
    }
    // Slot budget exhausted (a near-fully-wet seed neighbourhood): stamp the use onto
    // the seed footprint itself so the required building is NEVER silently dropped.
    const par = squareParcel(sampler, s.u, s.v, REQFILL_HALF_M, hAt)
      ?? { poly: new Float64Array([s.u - REQFILL_HALF_M, s.v - REQFILL_HALF_M, s.u + REQFILL_HALF_M, s.v - REQFILL_HALF_M, s.u + REQFILL_HALF_M, s.v + REQFILL_HALF_M, s.u - REQFILL_HALF_M, s.v + REQFILL_HALF_M]), use, epoch: 0, level: hAt(s.u, s.v), retaining: false };
    par.use = use;
    g.parcels.push(par);
    pcu.push(s.u); pcv.push(s.v);
    claimed.push(1);
  };

  // ── PASS 1: required uses (all seeds), never dropped. W2d-4: required
  // buildings claim lots NEAREST THE PLAZA when the settlement has one (the
  // spec's "nearest-to-plaza first"); the anchor falls back to the seed. ──────
  const anchorFor = (s: { u: number; v: number; tier: number }): { u: number; v: number } => {
    let best: { u: number; v: number } | null = null;
    let bestD = PARCEL_SEED_RADIUS_MULT * TIER_PARAMS[s.tier].radiusM;
    for (const pl of plazas) {
      const d = Math.sqrt((pl.u - s.u) * (pl.u - s.u) + (pl.v - s.v) * (pl.v - s.v));
      if (d < bestD) { bestD = d; best = pl; }
    }
    return best ?? s;
  };
  // R3 LANDMARK-FIRST: the primary settlement's landmark use claims the parcel
  // nearest the landmark SITE before anything else (it is one of the required
  // uses — sited, not added; the primary's required loop then skips one
  // instance of it so multiplicities hold).
  let landmarkPlaced = false;
  if (landmark !== null && LANDMARK_USE[primary.tier] !== 0) {
    const near = nearestUnclaimed(landmark.u, landmark.v);
    if (near.length > 0) {
      claimed[near[0]] = 1;
      g.parcels[near[0]].use = LANDMARK_USE[primary.tier];
      landmarkPlaced = true;
    }
  }
  for (const s of order) {
    // Civic sites are already reserved once per real settlement; sprawl seeds
    // are districts of their parent metropolis and receive no extra pair.
    let req: readonly number[] = TIER_REQUIREMENTS[s.tier].filter(use=>use!==USE_ADMIN&&use!==USE_MONUMENT);
    if (landmarkPlaced && s === primary) {
      const idx = req.indexOf(LANDMARK_USE[primary.tier]);
      if (idx >= 0) req = [...req.slice(0, idx), ...req.slice(idx + 1)];
    }
    const anchor = anchorFor(s);
    const near = nearestUnclaimed(anchor.u, anchor.v);
    let oi = 0, fillSlot = 0;
    for (let r = 0; r < req.length; r++) {
      if (oi < near.length) {
        const p = near[oi++];
        claimed[p] = 1;
        g.parcels[p].use = req[r];
      } else {
        appendFill(s, fillSlot, req[r]);
        fillSlot++;
      }
    }
  }

  // ── PASS 2: optional pools (all seeds), over remaining reach-bounded parcels. ─
  for (const s of order) {
    const pool = OPTIONAL_POOLS[s.tier];
    if (pool.length === 0) continue;
    const reach = PARCEL_SEED_RADIUS_MULT * TIER_PARAMS[s.tier].radiusM;
    const reach2 = reach * reach;
    const near = nearestUnclaimed(s.u, s.v).filter((p) => {
      const du = pcu[p] - s.u, dv = pcv[p] - s.v;
      return du * du + dv * dv <= reach2;
    });
    for (let oi = 0; oi < near.length; oi++) {
      const p = near[oi];
      if (h01(hashInts(planetHash, SALT_OPT, rk, p)) >= OPTIONAL_PROB) continue;
      claimed[p] = 1;
      g.parcels[p].use = pool[Math.floor(h01(hashInts(planetHash, SALT_OPT, rk, p, 1)) * pool.length)];
    }
  }
}

// ── orchestrator (part 4) ─────────────────────────────────────────────────────
//
// materializeRegion turns one region cell into a complete, frozen CityGraph —
// the single entry point W2b's region worker calls. It is a pure function of the
// planet sampler and the cell id: every stage below is already deterministic, so
// the composition is byte-identical across regenerations and across engines (the
// twice-in-process / fresh-process oracles in worldgen-growth.test.mts).
//
// BINDING ORCHESTRATION ORDER (spec §5.1, W2d-1/W2d-2):
//   regionFrame → portLocals → settlementSeeds (ascending k) → emitPorts →
//   [tier branch] → build(). VILLAGE+ routes A* PRIMARIES (routePrimaries), then
//   grows ERA'D TENSOR SECONDARIES into a shared planar net (growSecondaryNetwork,
//   ascending k) → pruneDeadEnds → emitParcels → pruneUtility; a HAMLET emits one
//   A* through-road + cottages (growHamlet); a WAYSTATION emits its building huddle
//   + paths (emitWaystationCluster). The contour-streamline arterial tracer and the
//   SC skeleton + densify are retired — primaries carry the EDGE_ARTERIAL enum value.
// The builder's ctor computes the region frame, so regionFrame is folded into
// `new CityGraphBuilder`; `builder.frame` is that exact frame (reused, never
// recomputed, so no chance of a divergent second frame).
//
// SEEDS. settlementSeeds returns SettlementSeeds with unit dirs (.dx/.dy/.dz);
// growth works in the chart, so each is projected to local (u,v) via dirToLocal,
// keeping .k/.tier/.suit/.deviation. Seeds already arrive in ascending k (the scan
// increments k), but we sort explicitly so the "ascending k" contract does not
// depend on that internal detail. The same {u,v,k,…} array feeds the tier
// pipeline: routePrimaries/growHamlet and growSecondaryNetwork all read u,v,k,tier
// (suit/deviation are consumed earlier, by seed selection).
//
// EMPTY REGIONS. A cell with NEITHER seeds NOR ports short-circuits to an empty
// graph (0 nodes) with no terrain sampling beyond what settlementSeeds already
// did — cheap and byte-stable. In practice edgePorts emits ≥1 port per side, so
// every real region cell has ports and this branch is a defensive floor; it is
// nonetheless byte-trivial (an empty build()), preserving the contract.
//
// MULTI-SETTLEMENT EPOCHS. growSecondaryNetwork drives ONE era-banded epoch
// counter across every seed it grows into the shared planar net (starting above
// the primaries' epoch 0), and its flush emits edges in (epoch asc, creation asc)
// order, so edge epochs stay globally monotone with no collision across settlements.

/**
 * Prune dead-end road chains to a fixpoint (W2b-2.6, F9), returning a FRESH
 * builder that holds only the survivors (the "pre-parcels array-rebuild" variant
 * the plan sanctions — pruned edges never enter the final graph, so every
 * downstream consumer sees a clean network with NO new provenance and NO
 * LOG_PRUNE bookkeeping; the log/epoch invariants stay trivially clean).
 *
 * A node is removed when it is NOT a seed or a port AND its live degree ≤ 1 (a
 * free end reaching neither anchor, or a fully-isolated junction); removing it
 * drops its single incident edge and may expose a new degree-1 neighbour, so the
 * process iterates to a fixpoint via a worklist (each edge is retired at most
 * once — O(V+E)). Isolated NODE_PORTs (seedless cells, or the ports a seed cell
 * chose not to trace) are anchors, so they SURVIVE with degree 0 — cross-region
 * boundary continuity is preserved.
 *
 * Runs BEFORE emitParcels, so "reaches a parcel-fronting edge" needs no special
 * case: parcels are laid only on the surviving network afterward. Survivors keep
 * their original edge EPOCH (ascending creation order is preserved, so epochs
 * stay non-decreasing); edge-referencing log entries (EXTEND/TYPESWITCH/LOOP —
 * the only kinds present pre-parcels) are copied with their ref remapped to the
 * new edge index, dropping entries whose edge was pruned.
 */
function pruneDeadEnds(builder: CityGraphBuilder): CityGraphBuilder {
  const g = builder.build();
  const nc = g.nodeU.length;
  const ec = g.edgeA.length;
  const anchored = (i: number): boolean => g.nodeKind[i] === NODE_SEED || g.nodeKind[i] === NODE_PORT;

  const adj: number[][] = new Array(nc);
  for (let i = 0; i < nc; i++) adj[i] = [];
  const degree = new Int32Array(nc);
  for (let e = 0; e < ec; e++) {
    adj[g.edgeA[e]].push(e); adj[g.edgeB[e]].push(e);
    degree[g.edgeA[e]]++; degree[g.edgeB[e]]++;
  }
  const nodeAlive = new Uint8Array(nc).fill(1);
  const edgeAlive = new Uint8Array(ec).fill(1);

  // Worklist seeded with every prunable node (ascending); the fixpoint is
  // order-independent (membership by anchor/degree only).
  const queue: number[] = [];
  for (let i = 0; i < nc; i++) if (!anchored(i) && degree[i] <= 1) queue.push(i);
  for (let qh = 0; qh < queue.length; qh++) {
    const i = queue[qh];
    if (!nodeAlive[i] || anchored(i) || degree[i] > 1) continue;
    nodeAlive[i] = 0;
    for (let a = 0; a < adj[i].length; a++) {
      const e = adj[i][a];
      if (!edgeAlive[e]) continue;
      edgeAlive[e] = 0;
      const other = g.edgeA[e] === i ? g.edgeB[e] : g.edgeA[e];
      degree[other]--; degree[i]--;
      if (!anchored(other) && nodeAlive[other] && degree[other] <= 1) queue.push(other);
    }
  }

  // Rebuild survivors into a fresh builder, remapping node + edge indices.
  const nb = new CityGraphBuilder(g.frame.radius, g.regionId);
  const nodeRemap = new Int32Array(nc).fill(-1);
  for (let i = 0; i < nc; i++) if (nodeAlive[i]) nodeRemap[i] = nb.addNode(g.nodeU[i], g.nodeV[i], g.nodeKind[i]);
  const edgeRemap = new Int32Array(ec).fill(-1);
  for (let e = 0; e < ec; e++) {
    if (!edgeAlive[e]) continue;
    edgeRemap[e] = nb.addEdge(nodeRemap[g.edgeA[e]], nodeRemap[g.edgeB[e]], g.edgeKind[e], g.edgeEpoch[e], g.edgeClass[e], g.edgeWidth[e]);
  }
  // Copy edge-referencing logs (all logs pre-parcels), remapping ref → new edge.
  for (let l = 0; l < g.logKind.length; l++) {
    const ne = edgeRemap[g.logRef[l]];
    if (ne >= 0) nb.log(g.logKind[l], ne, g.logSlope[l]);
  }
  return nb;
}

/**
 * W2b-2.7 UTILITY PRUNING (F-hotfix-1). Runs AFTER emitParcels: streets exist to
 * serve dwellings, so a STREET/STAIR edge farther than {@link PARCEL_SERVICE_M}
 * from EVERY parcel centroid (chart point-to-segment distance) is deleted; the
 * remaining stubs are then dead-end-pruned to a fixpoint. ARTERIAL/BRIDGE (trunk
 * infrastructure) and TUNNEL/TERRACE (steep connectors) are exempt from the
 * distance filter but still participate in the dead-end fixpoint.
 *
 * Same rebuild-by-VALUE surgery as {@link pruneDeadEnds} (survivors keep their
 * epoch, so epochs stay non-decreasing), but PARCELS are carried through — the
 * parcel→fronting-edge epoch reference must survive, and it does: a parcel
 * centroid is 11 m off its fronting edge (< PARCEL_SERVICE_M), so every fronting
 * edge clears the distance filter by construction. Fronting-edge endpoints are
 * ALSO anchored in the dead-end fixpoint, so a served spur (a house on a short
 * terminal street) is never mistaken for a bare stub — no parcel is orphaned.
 *
 * LOG surgery: EXTEND/TYPESWITCH/LOOP entries (edge refs) are remapped and
 * dropped with their edge; LOG_PARCEL entries (parcel refs) are copied verbatim
 * (the parcel array is preserved in order).
 */
function pruneUtility(builder: CityGraphBuilder): CityGraphBuilder {
  const g = builder.build();
  const nc = g.nodeU.length;
  const ec = g.edgeA.length;

  // Parcel centroids (chart metres).
  const pcu: number[] = [];
  const pcv: number[] = [];
  for (const p of g.parcels) {
    let su = 0, sv = 0;
    const m = p.poly.length / 2;
    for (let c = 0; c < m; c++) { su += p.poly[2 * c]; sv += p.poly[2 * c + 1]; }
    pcu.push(su / m); pcv.push(sv / m);
  }

  // Distance filter: a STREET/STAIR edge survives iff within PARCEL_SERVICE_M of
  // some parcel centroid. Everything else survives this stage (dead-end pruned
  // below). A cell with zero parcels drops ALL its STREET/STAIR edges here.
  const edgeAlive = new Uint8Array(ec).fill(1);
  const fronts = new Uint8Array(ec); // edge fronts ≥1 parcel (anchors its ends)
  for (let e = 0; e < ec; e++) {
    const kind = g.edgeKind[e];
    if (kind !== EDGE_STREET && kind !== EDGE_STAIR) continue;
    const a = g.edgeA[e], b = g.edgeB[e];
    const au = g.nodeU[a], av = g.nodeV[a], bu = g.nodeU[b], bv = g.nodeV[b];
    let near = false;
    for (let pi = 0; pi < pcu.length; pi++) {
      if (pointSegDist(pcu[pi], pcv[pi], au, av, bu, bv) <= PARCEL_SERVICE_M) { near = true; break; }
    }
    if (!near) edgeAlive[e] = 0;
  }
  // R1 D1 — BRIDGE-EDGE EXEMPTION: never delete a STREET/STAIR edge whose removal
  // would disconnect its component (the mid-chain-orphan mechanism, diagnosis H1a).
  // One union-find pass: union the distance-filter SURVIVORS, then walk the deleted
  // edges in ascending order and re-instate any that still join two distinct
  // components (a bridge / bridge-chain). This keeps the graph exactly as connected
  // as it entered pruneUtility while still dropping only genuinely redundant streets.
  {
    const parent = new Int32Array(nc);
    for (let i = 0; i < nc; i++) parent[i] = i;
    const find = (x: number): number => { while (parent[x] !== x) { parent[x] = parent[parent[x]]; x = parent[x]; } return x; };
    const union = (a: number, b: number): void => { const ra = find(a), rb = find(b); if (ra !== rb) parent[ra < rb ? rb : ra] = ra < rb ? ra : rb; };
    for (let e = 0; e < ec; e++) if (edgeAlive[e]) union(g.edgeA[e], g.edgeB[e]);
    for (let e = 0; e < ec; e++) {
      if (edgeAlive[e]) continue;
      if (find(g.edgeA[e]) !== find(g.edgeB[e])) { edgeAlive[e] = 1; union(g.edgeA[e], g.edgeB[e]); }
    }
  }
  // Fronting edges: the logged STREET/STAIR edge whose epoch matches a parcel's.
  // These carry a dwelling, so their endpoints anchor the dead-end fixpoint.
  for (let pi = 0; pi < g.parcels.length; pi++) {
    const ep = g.parcels[pi].epoch;
    for (let e = 0; e < ec; e++) {
      const k = g.edgeKind[e];
      if ((k === EDGE_STREET || k === EDGE_STAIR) && g.edgeEpoch[e] === ep) { fronts[e] = 1; break; }
    }
  }

  // Dead-end fixpoint over the surviving edge set (same worklist as pruneDeadEnds)
  // with an extended anchor set: NODE_SEED, NODE_PORT, or any endpoint of a
  // fronting edge. Degree is counted over LIVE edges only.
  const anchorNode = new Uint8Array(nc);
  for (let i = 0; i < nc; i++) if (g.nodeKind[i] === NODE_SEED || g.nodeKind[i] === NODE_PORT) anchorNode[i] = 1;
  for (let e = 0; e < ec; e++) if (edgeAlive[e] && fronts[e]) { anchorNode[g.edgeA[e]] = 1; anchorNode[g.edgeB[e]] = 1; }

  const adj: number[][] = new Array(nc);
  for (let i = 0; i < nc; i++) adj[i] = [];
  const degree = new Int32Array(nc);
  for (let e = 0; e < ec; e++) {
    if (!edgeAlive[e]) continue;
    adj[g.edgeA[e]].push(e); adj[g.edgeB[e]].push(e);
    degree[g.edgeA[e]]++; degree[g.edgeB[e]]++;
  }
  const nodeAlive = new Uint8Array(nc).fill(1);
  const queue: number[] = [];
  for (let i = 0; i < nc; i++) if (!anchorNode[i] && degree[i] <= 1) queue.push(i);
  for (let qh = 0; qh < queue.length; qh++) {
    const i = queue[qh];
    if (!nodeAlive[i] || anchorNode[i] || degree[i] > 1) continue;
    nodeAlive[i] = 0;
    for (let a = 0; a < adj[i].length; a++) {
      const e = adj[i][a];
      if (!edgeAlive[e]) continue;
      edgeAlive[e] = 0;
      const other = g.edgeA[e] === i ? g.edgeB[e] : g.edgeA[e];
      degree[other]--; degree[i]--;
      if (!anchorNode[other] && nodeAlive[other] && degree[other] <= 1) queue.push(other);
    }
  }

  // Rebuild survivors into a fresh builder, carrying parcels + remapping logs.
  const nb = new CityGraphBuilder(g.frame.radius, g.regionId);
  const nodeRemap = new Int32Array(nc).fill(-1);
  for (let i = 0; i < nc; i++) if (nodeAlive[i]) nodeRemap[i] = nb.addNode(g.nodeU[i], g.nodeV[i], g.nodeKind[i]);
  const edgeRemap = new Int32Array(ec).fill(-1);
  for (let e = 0; e < ec; e++) {
    if (!edgeAlive[e]) continue;
    edgeRemap[e] = nb.addEdge(nodeRemap[g.edgeA[e]], nodeRemap[g.edgeB[e]], g.edgeKind[e], g.edgeEpoch[e], g.edgeClass[e], g.edgeWidth[e]);
  }
  for (const p of g.parcels) nb.addParcel(p);
  // Logs: edge-ref kinds remap (dropped with a pruned edge); LOG_PARCEL keeps its
  // parcel-index ref (parcels are preserved in order).
  for (let l = 0; l < g.logKind.length; l++) {
    if (g.logKind[l] === LOG_PARCEL) { nb.log(LOG_PARCEL, g.logRef[l], g.logSlope[l]); continue; }
    const ne = edgeRemap[g.logRef[l]];
    if (ne >= 0) nb.log(g.logKind[l], ne, g.logSlope[l]);
  }
  return nb;
}

/**
 * Materialize a region cell into a complete, frozen {@link CityGraph}: A* primaries
 * → era'd tensor secondary growth (ascending k) → prune → lots → utility-prune.
 * Pure and deterministic in (sampler, cell). See the section header for the binding
 * stage order and the empty-region floor. W2b-2.6: seedless cells stop after the
 * port nodes (no primaries/streets/parcels); seed-bearing cells prune dead ends
 * before laying lots. `repairOut` is an inert TEST-ONLY seam (see
 * {@link growSecondaryNetwork}); the returned graph is byte-identical either way.
 */
// ── R3 Task 3: DISTRICTS — derived "sections" of a settlement ──────────────────
//
// A district id is a PURE function of position — nothing is serialized. Bands
// (core / mid / outer by radial distance from the nearest seed, the era read)
// cross hashed angular SECTORS (2–4 per settlement, hashed rotation) to give
// every large settlement 3–12 legible sections. Consumers: shared building
// height scaling (collision + render stay in sync) and the renderer's wall
// palette tint. TOWN+ only — a village is one neighbourhood.

/** District sector salt (hash.ts registry). */
const SALT_DISTRICT = 0x415d;
/** Per-band building height scale (core taller, outskirts lower). */
export const DISTRICT_H_SCALE: readonly number[] = [1.15, 1.0, 0.86];

export interface DistrictId { band: number; sector: number; id: number }

/** The district at chart (u,v): band 0..2 × sector 0..3 → id = band*4 + sector. */
export function districtAt(
  planetHash: number, cellKey: number,
  seeds: readonly { u: number; v: number }[], tier: number,
  u: number, v: number,
): DistrictId {
  if (tier < TIER_TOWN || seeds.length === 0) return { band: 1, sector: 0, id: 4 };
  let su = seeds[0].u, sv = seeds[0].v, bestD = Infinity;
  for (const s of seeds) {
    const d = (s.u - u) * (s.u - u) + (s.v - v) * (s.v - v);
    if (d < bestD) { bestD = d; su = s.u; sv = s.v; }
  }
  const R = TIER_PARAMS[tier].radiusM;
  const d = Math.sqrt(bestD);
  const band = d < R * 0.35 ? 0 : d < R * 0.7 ? 1 : 2;
  const S = 2 + (hashInts(planetHash, SALT_DISTRICT, cellKey, 0) % 3); // 2..4 sectors
  const rot = pmath.TWO_PI * h01(hashInts(planetHash, SALT_DISTRICT, cellKey, 1));
  let a = pmath.atan2(v - sv, u - su) + rot;
  a -= pmath.TWO_PI * Math.floor(a / pmath.TWO_PI);
  const sector = Math.min(S - 1, Math.floor((a / pmath.TWO_PI) * S));
  return { band, sector, id: band * 4 + sector };
}

// ── R3 Task 2: METRO MULTI-CELL SPRAWL ─────────────────────────────────────────
//
// A metropolis' spec footprint (8–12 km, 300–600 buildings) cannot fit its
// single ~2 km region cell — the standing density escalation. Sprawl closes it
// REGION-INDEPENDENTLY: when a cell's SAME-FACE edge-neighbour holds a dealt
// METRO seed (the planet-global TierRegistry answers this from hashes alone —
// no cross-cell materialization), the cell grows a SPRAWL PSEUDO-SEED: a
// TOWN-budget growth centre in the band facing the metro, connected through the
// shared border ports like any settlement. The metro cluster (metro cell + its
// sprawling neighbours) then carries the spec building count; the tierparams
// cluster oracle hard-asserts it. Cross-FACE neighbours are skipped (cube-edge
// adjacency needs the boundary machinery — documented asymmetry at face seams).

/** Sprawl pseudo-seed jitter roll (hash.ts registry). */
const SALT_SPRAWL = 0x5b4a;
/** Pseudo-seed distance from the cell centre toward the metro (× cell half). */
const SPRAWL_BAND_FRAC = 0.44;
/** Sprawl growth tier (a town-sized outskirt per neighbour cell). */
const SPRAWL_TIER = TIER_TOWN;

/**
 * The cell's sprawl pseudo-seeds (chart coords): one per same-face
 * edge-neighbour holding a METRO seed, positioned toward that neighbour with a
 * hashed tangential jitter, wet-rerolled twice (ocean flanks stay empty).
 * Skipped entirely when the cell has its own VILLAGE+ seed (a dealt settlement
 * next to a metro is already part of the conurbation — no double density).
 * k = 1000 + direction index (never collides with dealt k values).
 */
export function sprawlSeeds(
  sampler: PlanetSampler, cell: CellId, frame: RegionFrame,
  dealt: readonly { tier: number }[],
): { u: number; v: number; k: number; tier: number; suit: number; deviation: number }[] {
  if (dealt.some((s) => s.tier >= TIER_VILLAGE)) return [];
  const reg = tierRegistryFor(sampler);
  const n = cellsPerFace(sampler.def.radius, cell.level);
  const planetHash = hashSeed(sampler.def.seed);
  const half = cellArcM(sampler.def.radius, cell.level) / 2;
  const dir = new Float64Array(3);
  const samp: TerrainSample = { h: 0, temp: 0, moist: 0, river: 0, mountain: 0, flowx: 0, flowy: 0, flowz: 0, town: 0, road: 8 };
  const wet = (u: number, v: number): boolean => {
    if (sampler.seaRel === 0) return false;
    localToDir(frame, u, v, dir);
    sampler.sample(dir[0], dir[1], dir[2], samp, false);
    return samp.h <= sampler.seaRel;
  };
  const out: { u: number; v: number; k: number; tier: number; suit: number; deviation: number }[] = [];
  const DIRS4: readonly [number, number][] = [[1, 0], [-1, 0], [0, 1], [0, -1]];
  for (let d = 0; d < DIRS4.length; d++) {
    const nx = cell.x + DIRS4[d][0];
    const ny = cell.y + DIRS4[d][1];
    if (nx < 0 || ny < 0 || nx >= n || ny >= n) continue; // cross-face seam — skipped
    const arr = reg.byCell.get(packCell({ face: cell.face, x: nx, y: ny, level: cell.level }));
    if (arr === undefined || !arr.some((a) => a.tier === TIER_METROPOLIS)) continue;
    // Toward-the-metro band position ± hashed tangential jitter; wet re-rolls.
    for (let attempt = 0; attempt < 6; attempt++) {
      const along = half * (SPRAWL_BAND_FRAC + 0.18 * (h01(hashInts(planetHash, SALT_SPRAWL, packCell(cell), d, attempt * 2)) - 0.5));
      const across = half * 0.5 * (h01(hashInts(planetHash, SALT_SPRAWL, packCell(cell), d, attempt * 2 + 1)) * 2 - 1);
      const u = DIRS4[d][0] !== 0 ? DIRS4[d][0] * along : across;
      const v = DIRS4[d][1] !== 0 ? DIRS4[d][1] * along : across;
      if (wet(u, v)) continue;
      out.push({ u, v, k: 1000 + d, tier: SPRAWL_TIER, suit: 0, deviation: 0 });
      break;
    }
  }
  return out;
}

export function materializeRegion(sampler: PlanetSampler, cell: CellId, repairOut?: number[]): CityGraph {
  const radius = sampler.def.radius;
  const planetHash = hashSeed(sampler.def.seed);

  // regionFrame (folded into the builder ctor) → the chart every stage shares.
  const builder = new CityGraphBuilder(radius, cell);
  const frame = builder.frame;

  // portLocals → settlementSeeds. Both are pure, hashed boundary conditions.
  const ports = portLocals(planetHash, radius, cell, frame);
  const rawSeeds = settlementSeeds(sampler, cell);

  // R3: metro sprawl pseudo-seeds (registry-driven, region-independent).
  const sprawl = sprawlSeeds(sampler, cell, frame, rawSeeds);

  // Empty region: no seeds AND no ports AND no sprawl ⇒ valid empty graph.
  if (ports.length === 0 && rawSeeds.length === 0 && sprawl.length === 0) return builder.build();

  // Project seeds into the chart (dir → local u,v), keep k/tier/suit/deviation,
  // and order by ascending k (the growth contract). W2c-1 Task 2: seeds now drive
  // growth by their 0..5 TIER directly (TIER_PARAMS), no legacy 0/1/2 bridge.
  const loc: [number, number] = [0, 0];
  const seeds = rawSeeds.map((s) => {
    dirToLocal(frame, s.dx, s.dy, s.dz, loc);
    return { u: loc[0], v: loc[1], k: s.k, tier: s.tier, suit: s.suit, deviation: s.deviation };
  });
  seeds.push(...sprawl);
  seeds.sort((a, b) => a.k - b.k);
  return runRegionPipeline(sampler, cell, frame, builder, ports, seeds, undefined, repairOut);
}

/**
 * Materialize ONE SYNTHETIC settlement of `tier` at the CENTRE of `cell` — the
 * TIER_PARAMS calibration/oracle entry point (W2c-1 Task 2). It runs the EXACT
 * orchestration {@link materializeRegion} uses (via the shared pipeline), but with
 * a single hand-placed seed instead of the registry's dealt seeds, so a test can
 * measure the surviving building count a tier produces GIVEN adequate buildable
 * land (the flattest cell). Naturally-dealt sites are terrain/area-limited and
 * under-deliver vs spec — see the w2c1-task-2 report's calibration escalation.
 *
 * `budgetOverride` (force the grower's per-tier iteration budget — the truncation
 * oracle) and `repairOut` (collect planarity-repair junction coords — the angle
 * oracle) are inert TEST-ONLY seams: omitting them reproduces the production graph
 * byte-for-byte. See {@link growSecondaryNetwork}.
 */
export function materializeSyntheticSettlement(
  sampler: PlanetSampler, cell: CellId, tier: number, k = 0, deviation = 0,
  budgetOverride?: number, repairOut?: number[],
): CityGraph {
  const radius = sampler.def.radius;
  const planetHash = hashSeed(sampler.def.seed);
  const builder = new CityGraphBuilder(radius, cell);
  const frame = builder.frame;
  const ports = portLocals(planetHash, radius, cell, frame);
  const suit = suitabilityAt(sampler, frame.cx, frame.cy, frame.cz);
  const seeds = [{ u: 0, v: 0, k, tier, suit, deviation }];
  return runRegionPipeline(sampler, cell, frame, builder, ports, seeds, budgetOverride, repairOut);
}

/**
 * The shared region-materialization pipeline (ports → primaries/hamlet-road/
 * waystation-huddle by tier → VILLAGE+ era'd tensor secondary growth → prune → lots
 * → utility-prune → required-building placement), consuming already-projected chart
 * seeds. Both {@link materializeRegion} (registry seeds) and
 * {@link materializeSyntheticSettlement} (one synthetic seed) route through it so
 * the two paths never drift.
 *
 * W2d-1/W2d-2: the old contour-streamline arterials AND the SC skeleton + densify
 * are gone. A VILLAGE+ cell routes A* PRIMARIES between hashed anchors
 * ({@link routePrimaries}), then grows era'd tensor SECONDARIES off them under the
 * global planarity invariant ({@link growSecondaryNetwork}). A HAMLET gets one A*
 * through-road + cottages ({@link growHamlet}); a WAYSTATION gets its huddle + paths.
 * Every cell emits its NODE_PORTs first (boundary continuity), so a seedless cell
 * carries just those. `budgetOverride`/`repairOut` are inert TEST-ONLY seams (see
 * {@link growSecondaryNetwork}) — omitting them reproduces the production graph exactly.
 */
export function runRegionPipeline(
  sampler: PlanetSampler, cell: CellId, frame: RegionFrame, builder: CityGraphBuilder,
  ports: { u: number; v: number }[],
  seeds: { u: number; v: number; k: number; tier: number; suit: number; deviation: number }[],
  budgetOverride?: number, repairOut?: number[],
): CityGraph {
  let maxTier = 0;
  for (let i = 0; i < seeds.length; i++) if (seeds[i].tier > maxTier) maxTier = seeds[i].tier;

  // Boundary ports first (cross-region continuity), in every branch below.
  const portNodes = emitPorts(builder, ports);

  // Seedless cell: no roads, no lots — just the port nodes.
  if (seeds.length === 0) return builder.build();

  // WAYSTATION cell (spec §"Tiers 1–2"): a 3–6 building huddle + building-to-
  // building paths, NO road network. No SC growth / densify / emitParcels — just
  // the cluster, then required-building placement.
  if (maxTier === TIER_WAYSTATION) {
    for (let i = 0; i < seeds.length; i++) emitWaystationCluster(sampler, cell, frame, seeds[i], builder);
    const g = builder.build();
    assignUses(g, sampler, seeds);
    return g;
  }

  // HAMLET cell (spec §"Tiers 1–2"): ONE A* through-road + 8–15 cottages with
  // driveway stubs, per hamlet seed. The old hamlet SC growth is gone. Utility-
  // prune to clean any degenerate stub, then required-building placement.
  if (maxTier === TIER_HAMLET) {
    // Tier guard (W2d-2 rider 3): only tier ≥ HAMLET seeds get a through-road;
    // a co-located WAYSTATION seed gets its road-free huddle, never growHamlet.
    // R1 Task 1.5: build ONLY the through-ROADS here (no cottages/huddles yet), link
    // them, then give the whole road graph planarity discipline + de-quant curvature.
    // R1 D7-F5: ONE shared RoadNetwork across every hamlet seed in the cell, so a
    // second through-road reuses the first (mutual reuse discount) instead of routing
    // near-parallel — killing the offset-parallel eyes the component linker then closed.
    const hamletNet = new RoadNetwork();
    const hamletMeasure = new RoadNetwork();
    for (let i = 0; i < seeds.length; i++) {
      if (seeds[i].tier >= TIER_HAMLET) growHamlet(sampler, cell, frame, builder, ports, portNodes, seeds[i], hamletNet, hamletMeasure);
    }
    // R1 D1: link multiple hamlet through-roads into ONE cell network.
    linkBuilderPrimaries(sampler, cell, frame, builder);
    // R1 Task 1.5: give the cell's through-roads/legs/links PLANARITY DISCIPLINE + the
    // same de-quant ladder VILLAGE+ primaries use, so hamlet roads are gently curved
    // (not raw 45°-lattice) while staying self-planar. Runs BEFORE cottages/huddles so
    // buildings front the CURVED road; the fresh builder it returns (no parcels/logs
    // yet, so the flush loses nothing) replaces the raw-road one.
    const curved = curveHamletPrimaries(sampler, cell, frame, builder);
    // v11: classify the through-roads/legs/links (PRIMARY at the hamlet width) BEFORE
    // buildings — cottage driveway stubs carry their ALLEY class at creation.
    classifyEdges(curved, seeds, hashSeed(sampler.def.seed), packCell(cell));
    // Now place buildings on the curved graph: hamlet cottages front the through-road;
    // a co-located WAYSTATION seed gets its road-free huddle.
    for (let i = 0; i < seeds.length; i++) {
      if (seeds[i].tier >= TIER_HAMLET) placeHamletCottages(sampler, cell, frame, curved, seeds[i]);
      else emitWaystationCluster(sampler, cell, frame, seeds[i], curved);
    }
    const served = pruneUtility(curved);
    const g = served.build();
    assignUses(g, sampler, seeds);
    return g;
  }

  // VILLAGE+ cell (W2d-2 Task 3): A* PRIMARY roads between hashed anchors (seed↔
  // anchor + anchor↔anchor, ONE shared RoadNetwork → trunk-and-branch), then ERA'D
  // TENSOR-FIELD secondary growth under the GLOBAL PLANARITY INVARIANT replaces the
  // retired SC skeleton + densify. Router and tensor field read ONE shared memoized
  // RouteTerrain (carry #5); secondaries + any hamlet lane snap/split against every
  // edge — primaries included — in a single planar net (carry #6/#8).
  const terrain = memoTerrain(makeSamplerTerrain(sampler, frame));
  routePrimaries(sampler, cell, frame, builder, ports, portNodes, seeds, terrain);
  const grown = growSecondaryNetwork(sampler, cell, frame, builder, seeds, terrain, budgetOverride, repairOut);

  // Pre-prune dead-end twigs (primaries + ports anchor the backbone), classify
  // widths (v11 — the parcel stage reads per-edge road bands), lay lots, then
  // prune UTILITY streets serving no dwelling.
  const preParcel = pruneDeadEnds(grown);
  classifyEdges(preParcel, seeds, hashSeed(sampler.def.seed), packCell(cell));
  // W2d-4: desire-line / promotion pass — footpaths promote, main streets widen
  // (lot setbacks below see the widened mains). Betweenness feeds the plaza stage.
  const promotion = promoteStreets(preParcel, seeds, hashSeed(sampler.def.seed), packCell(cell));
  // R3 landmark-first: the primary settlement's landmark site (same pure derivation
  // selectAnchors used for routing) anchors the plaza tie-break + placement below.
  let lmPrim = seeds.length ? seeds[0] : null;
  for (const s of seeds) if (lmPrim !== null && (s.tier > lmPrim.tier || (s.tier === lmPrim.tier && s.k < lmPrim.k))) lmPrim = s;
  const landmark = lmPrim !== null ? landmarkSite(sampler, frame, cell, lmPrim) : null;
  // W2d-4: plazas at the top-betweenness junction(s) — emitted BEFORE lots so the
  // open square is an obstacle every lot and strip must clear.
  const plazas = emitPlazas(sampler, preParcel.frame, preParcel, seeds, promotion, hashSeed(sampler.def.seed), packCell(cell), landmark);
  // W2d-3: blocks → alleys → lots first; strips remain ONLY on street sides that
  // face no lotted block (dead-end lanes, town edges).
  const lotted = emitBlockLots(sampler, preParcel.frame, preParcel, seeds, plazas.polys);
  // Strips avoid the plazas too (fold them into the obstacle set).
  for (const poly of plazas.polys) lotted.lotPolys.push(poly);
  emitParcels(sampler, preParcel.frame, preParcel, seeds, lotted);
  const served = pruneUtility(preParcel);
  // v11: utility pruning exposes the true leaf lanes — demote them to TERTIARY
  // (narrow-only, so the parcel setbacks laid above stay valid).
  demoteLeafLanes(served, seeds, hashSeed(sampler.def.seed), packCell(cell));
  const g = served.build();
  // v16: per-settlement PARCEL BUDGET — the spec's targetBuildings table becomes
  // a generation INPUT instead of a calibration hope (see capParcels).
  capParcels(g, hashSeed(sampler.def.seed), packCell(cell), seeds, plazas.centres, landmark);
  assignUses(g, sampler, seeds, plazas.centres, landmark);
  return g;
}

/** Parcel-budget roll (hash.ts registry). */
const SALT_PCAP = 0x51c2;
/** A parcel centroid this close to a plaza centre IS the plaza — exempt. */
const PCAP_PLAZA_EPS_M = 4;

/**
 * Per-settlement parcel budget (v16): every route-structure change so far
 * (W2d-3 blocks, v15 trunk merging, v16 d_sep) cascaded ±20–70% into the tier
 * building counts and burned a calibration knob to pull them back inside the
 * spec's verbatim targetBuildings bands. This ends the treadmill: the band is
 * enforced AT GENERATION — each settlement rolls a budget in
 * [ceil((lo+hi)/2), hi] (upper half: footprint failures eat a few percent, and
 * the roll keeps counts varied) and keeps only its budget-nearest parcels
 * (distance to the owner seed, ties by index), so trimming thins the FRINGE
 * and the civic core is untouched. Plaza parcels are exempt (open pavement,
 * not buildings), as is the parcel nearest the landmark site (the flagship's
 * claim must survive). The cap only ever REMOVES: under-target counts still
 * report the honest deficit (metropolis stays a reported floor).
 */
function capParcels(
  g: CityGraph, planetHash: number, cellKey: number,
  seeds: { u: number; v: number; k: number; tier: number }[],
  plazaCentres: { u: number; v: number }[],
  landmark: { u: number; v: number } | null,
): void {
  if (g.parcels.length === 0 || seeds.length === 0) return;
  const n = g.parcels.length;
  const byOwner: number[][] = seeds.map(() => []);
  const dist2 = new Float64Array(n);
  let lmNearest = -1, lmBest = Infinity;
  for (let i = 0; i < n; i++) {
    const poly = g.parcels[i].poly;
    let cu = 0, cv = 0;
    for (let j = 0; j < poly.length; j += 2) { cu += poly[j]; cv += poly[j + 1]; }
    cu /= poly.length / 2; cv /= poly.length / 2;
    let plaza = false;
    for (const c of plazaCentres) {
      if (pmath.hypot(cu - c.u, cv - c.v) <= PCAP_PLAZA_EPS_M) { plaza = true; break; }
    }
    if (plaza) continue; // exempt: never attributed, never trimmed
    if (landmark !== null) {
      const dl = (cu - landmark.u) * (cu - landmark.u) + (cv - landmark.v) * (cv - landmark.v);
      if (dl < lmBest) { lmBest = dl; lmNearest = i; }
    }
    let best = 0, bd = Infinity;
    for (let s = 0; s < seeds.length; s++) {
      const d2 = (cu - seeds[s].u) * (cu - seeds[s].u) + (cv - seeds[s].v) * (cv - seeds[s].v);
      if (d2 < bd) { bd = d2; best = s; }
    }
    byOwner[best].push(i);
    dist2[i] = bd;
  }
  const drop = new Set<number>();
  for (let s = 0; s < seeds.length; s++) {
    const [lo, hi] = TIER_PARAMS[seeds[s].tier].targetBuildings;
    const mid = Math.ceil((lo + hi) / 2);
    const cap = Math.min(hi, mid + Math.floor(h01(hashInts(planetHash, SALT_PCAP, cellKey, seeds[s].k)) * (hi - mid + 1)));
    const own = byOwner[s];
    if (own.length <= cap) continue;
    own.sort((a, b) => (dist2[a] - dist2[b]) || (a - b));
    for (let t = cap; t < own.length; t++) if (own[t] !== lmNearest) drop.add(own[t]);
  }
  if (drop.size > 0) g.parcels = g.parcels.filter((_, i) => !drop.has(i));
}

// ── growth knobs (spec §12 debug-panel surface) ──────────────────────────────
//
// The tunable growth constants gathered into one frozen object for W2b's debug
// panel to surface (and for any future calibration tooling to enumerate). This
// is a pure re-export of the binding constants defined above — reading it is
// exactly reading each constant; it introduces NO behavior and NO new tuning
// point. Names map 1:1 to the constants in this file (no aliasing was needed —
// every name in spec §12's list exists verbatim here).
export const GROWTH_KNOBS = {
  ANCHOR_COUNTS,
  PRIMARY_COUNTS,
  HAMLET_BUILDINGS,
  SECONDARY_PARAMS,
  TIER_PARAMS,
  PARCEL_DEPTH_M,
} as const;
